import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './App.css';
import { AvisosHost, DialogoHost, avisar, avisarError, confirmar } from './Dialogo';
import { estadoFranja, fechaHora, minutosRestantes, nombreMesPermiso, textoMinutos } from './permisos';
import Cobranza from './Cobranza';
import { BotonIcono } from './Tooltip';
import { DocumentosAnio, DocumentosMes } from './DocumentosCliente';
import FormularioCliente from './FormularioCliente';
import FiltroMenu from './FiltroMenu';
import CeldaCliente from './CeldaCliente';
import ObligacionesAdicionales from './ObligacionesAdicionales';
import DeclaracionAnual from './DeclaracionAnual';
import ResumenCliente from './ResumenCliente';
import { ejercicioActual } from './anual';
import { atrasosPorCliente } from './obligaciones';
import { seAbreEnNavegador, textoArchivos } from './documentos';
import { ROLES_ASIGNABLES, esAdministrador, marcaRevisado, nombreRol, puedeGestionarRol, seccionesDe, veClientes, veCobranza, veViento } from './roles';
import {
  ResultadoEnvio,
  ResumenEnvio,
  correosValidos,
  esperarEnvioMasivo,
  plural,
  textoProgreso,
  tieneTelefono,
} from './Envios';
import { ArrowLeft, ArrowRight, Ban, Building2, Calendar, Check, ChevronDown, FileClock, ChevronLeft, ChevronRight, ChevronUp, CircleCheck, CircleHelp, ClipboardList, Clock, Download, Eye, FileText, Hourglass, Info, KeyRound, LoaderCircle, Lock, LogOut, Mail, MessageSquare, NotebookPen, Pencil, Plus, Reply, Search, Send, Settings, Smartphone, Trash2, TriangleAlert, User, Users, X } from 'lucide-react';
import {
  NOMBRES_ESTADO,
  NOMBRES_SELLO,
  OPCIONES_OPINION,
  OPINION_NEGATIVA,
  PASO_TERMINADOS,
  calcularPasoActual,
  claveVisibleDeGuardado,
  construirFechasLimite,
  esDeclaracionPresentada,
  esOpinionCumplimiento,
  esPasoRevisable,
  estadoVisible,
  etiquetaRevision,
  fechaCorta,
  fechaLimiteDe,
  nombreVisibleProceso,
  tooltipCirculo,
  avancePasos,
  DIAS_POR_VENCER,
  INFO_PASOS,
  ETAPAS,
  nombreEstadoGuardado,
  normalizarNombre,
  pasosCascada,
  pasosPorRevisar,
  complementariaPendiente,
  MOTIVOS_CERO,
  resultadoOpinion,
  revisionPendienteAntes,
  normalizarRevision,
} from './estados';
import config from './config';
import PagoCompletado from './PagoCompletado';
import PagarCheckout from './components/PagarCheckout';
import PrivacidadPage from './pages/PrivacidadPage';
import TerminosPage from './pages/TerminosPage';

const API_URL = config.API_URL;

// Ícono dentro del círculo de estado (C-004). Bloqueado y Por hacer no llevan ícono.
function IconoEstado({ clave, size = 18 }) {
  if (clave === 'completado') return <Check size={size} strokeWidth={3} aria-hidden="true" />;
  if (clave === 'revisado') return <Check size={size} strokeWidth={3} aria-hidden="true" />;
  if (clave === 'en-proceso') return <Clock size={size} strokeWidth={2.5} aria-hidden="true" />;
  if (clave === 'presentado-cero') return <span className="estado-cero-texto">$0</span>;
  return null;
}

// Sello de la revisión de Pedro, abajo a la derecha del círculo (C-014): reloj ámbar = Por revisar,
// palomita oscura = Revisado. El badge de atraso va arriba a la derecha, así nunca se tapan.
function SelloRevision({ tipo }) {
  if (!tipo) return null;
  // C-034: "Complementaria pendiente" es un cuadrado ámbar (no un círculo como los de Pedro)
  if (tipo === 'complementaria') {
    return (
      <span className="sello-revision complementaria" aria-hidden="true">
        <FileClock size={9} strokeWidth={3} />
      </span>
    );
  }
  return (
    <span className={`sello-revision ${tipo}`} aria-hidden="true">
      {tipo === 'por-revisar'
        ? <Clock size={9} strokeWidth={3.4} />
        : <Check size={9} strokeWidth={3.4} />}
    </span>
  );
}

// Cliente nuevo (C-026): industria sin valor por defecto, para que nadie quede como "Vinícola" sin querer.
const CLIENTE_NUEVO = {
  nombre: '',
  tipo: 'moral',
  contador_id: '',
  email: '',
  telefono: '',
  regimen: '',
  industria: '',
  revision_pedro: 'no',
  tiene_imss: false,
  tiene_contabilidad_digital: false,
  tiene_180_mensual: false,
  tiene_180_trimestral: false,
  tiene_hospedaje: false,
  solo_cobranza: false,
  otros_ingresos: false,
};

// C-024: los nombres de paso se parten con guion si no caben (el navegador no siempre sabe
// partir palabras en español). Solo se usan si hace falta.
const GUIONES_SUAVES = {
  Contabilidad: 'Conta\u00ADbilidad', cumplimiento: 'cumpli\u00ADmiento', información: 'infor\u00ADmación',
  documentos: 'docu\u00ADmentos', declaración: 'decla\u00ADración', Declaración: 'Decla\u00ADración',
  presentada: 'presen\u00ADtada', impuestos: 'impues\u00ADtos',
};
const conGuionesSuaves = (texto) => texto.split(' ').map(p => GUIONES_SUAVES[p] || p).join(' ');

// C-023: id del cliente si la dirección es la de su ficha (/clientes/123).
const idFichaEnUrl = () => {
  const m = window.location.pathname.match(/^\/clientes\/(\d+)\/?$/);
  return m ? Number(m[1]) : null;
};

// Descarga con su nombre una URL firmada (C-015). Sin pestaña nueva: la URL ya pide la descarga,
// así que la página no se va, y no queda una pestaña en blanco.
function descargarDesdeUrl(url, nombre) {
  const link = document.createElement('a');
  link.href = url;
  link.download = nombre;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Opciones de los filtros de régimen e industria (C-012, ver diseno/filtros.md)
// Primer año con datos en el sistema; el selector de periodo no baja de aquí (C-013).
const ANIO_MINIMO = 2025;
// C-020: el primer ejercicio que se puede revisar en la Declaración anual (se presentó en 2025).
const EJERCICIO_MINIMO = 2024;

const REGIMENES = {
  resico: 'RESICO',
  general: 'General',
  actividad_empresarial: 'Actividad empresarial',
  arrendamiento: 'Arrendamiento',
  personas_morales: 'Personas morales',
};
const INDUSTRIAS = {
  vinicola: 'Vinícola',
  empresarial: 'Empresarial',
  comercio: 'Comercio',
  servicios: 'Servicios',
  manufactura: 'Manufactura',
};

// Los filtros de la pantalla principal viven en la URL (?paso=documentos&atrasados=1…)
// Los de varios valores van separados por coma: ?contador=2,4 (C-025).
const leerFiltroUrl = (clave) => {
  try {
    return new URLSearchParams(window.location.search).get(clave);
  } catch (e) {
    return null;
  }
};
const leerListaUrl = (clave) => (leerFiltroUrl(clave) || '').split(',').filter(v => v && v !== 'todos');

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [verificandoToken, setVerificandoToken] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Verificando sesión...');
  const [reconectando, setReconectando] = useState(false); // Mensaje de reconexión
  const [clientes, setClientes] = useState([]);
  const [procesos, setProcesos] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [mensajes, setMensajes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // Calcular mes anterior por defecto (para contabilidad)
  const calcularMesAnterior = () => {
    const fecha = new Date();
    let mes = fecha.getMonth(); // 0-11 (0=enero, 11=diciembre)
    let anio = fecha.getFullYear();

    if (mes === 0) { // Si es enero
      mes = 11;      // Mostrar diciembre (11)
      anio--;        // Del año anterior
    } else {
      mes--;         // Mes anterior
    }

    return { mes, anio };
  };

  const mesAnteriorData = calcularMesAnterior();
  const [selectedMes, setSelectedMes] = useState(mesAnteriorData.mes);  // Mes anterior por defecto
  const [selectedAnio, setSelectedAnio] = useState(mesAnteriorData.anio);  // Año correspondiente
  const [showMessages, setShowMessages] = useState(false);
  const [conversaciones, setConversaciones] = useState([]);
  const [conversacionActiva, setConversacionActiva] = useState(null);
  const [mensajesConversacion, setMensajesConversacion] = useState([]);
  const [cargandoMensajes, setCargandoMensajes] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showPickerEditar, setShowPickerEditar] = useState(false);
  const [busquedaPicker, setBusquedaPicker] = useState('');
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [confirmPassValue, setConfirmPassValue] = useState('');
  const [confirmPassError, setConfirmPassError] = useState('');
  const [confirmPassCallback, setConfirmPassCallback] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [docsMes, setDocsMes] = useState(null); // cliente cuyo modal "Documentos del mes" está abierto (C-015)
  const [showSettings, setShowSettings] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [settingsTab, setSettingsTab] = useState('perfil');
  // C-018: Ajustes se abre como "Mi cuenta" (perfil y contraseña) o como "Despacho" (usuarios y respaldo)
  const [settingsGrupo, setSettingsGrupo] = useState('personal');
  // C-017: mes de Cobranza y Viento (0-11), en el mes en curso al abrir
  const [periodoCobranza, setPeriodoCobranza] = useState(() => ({ mes: new Date().getMonth(), anio: new Date().getFullYear() }));
  const [passwordData, setPasswordData] = useState({ password_actual: '', password_nueva: '', password_confirmar: '' });
  const [perfilData, setPerfilData] = useState({ nombre: '', email: '' });
  const [newUserData, setNewUserData] = useState({ username: '', password: '', nombre: '', email: '', rol: 'contador' });
  const [editingUser, setEditingUser] = useState(null);
  const [showEditContact, setShowEditContact] = useState(false);
  const [selectedClientContact, setSelectedClientContact] = useState(null);
  const [contactData, setContactData] = useState({ email: '', telefono: '', regimen: ''});
  const [showProcesosDetalle, setShowProcesosDetalle] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [procesosDetalle, setProcesosDetalle] = useState({});
  const [pestanaFicha, setPestanaFicha] = useState('resumen'); // C-015: resumen · documentos · procesos

  // Estados para comentarios
  const [comentariosCliente, setComentariosCliente] = useState([]);
  const [nuevoComentario, setNuevoComentario] = useState('');
  const [guardandoComentario, setGuardandoComentario] = useState(false);
  const [puedeAgregarComentarios, setPuedeAgregarComentarios] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectorPeriodo, setSelectorPeriodo] = useState(null); // año que muestra la cuadrícula de meses abierta (C-013)
  const [showLeyenda, setShowLeyenda] = useState(false);
  const [currentRoute, setCurrentRoute] = useState(() =>
    window.location.pathname === '/cobranza' ? '/cobranza' : '/'
  );
  // C-023: la ficha tiene su dirección (/clientes/123); al cargar así, se abre en cuanto hay datos.
  const [fichaPendiente, setFichaPendiente] = useState(() => idFichaEnUrl());
  const [obligBuscar, setObligBuscar] = useState(''); // "Ver en la tabla →" de la ficha
  const [cobranzaData, setCobranzaData] = useState([]);
  const [cobranzaTodosClientes, setCobranzaTodosClientes] = useState([]);
  const [cobranzaSaldos, setCobranzaSaldos] = useState({});
  const [enviandoMasivo, setEnviandoMasivo] = useState(false);
  const [generandoExcel, setGenerandoExcel] = useState(false);
  // Cobros mensuales
  const [cobrosPendientesTodos, setCobrosPendientesTodos] = useState([]);
  const [resumenMensual, setResumenMensual] = useState([]);
  const [showAdjuntosModal, setShowAdjuntosModal] = useState(false);
  const [cobroAdjuntos, setCobroAdjuntos] = useState(null); // { cobro, clienteId }
  const [archivoPDF, setArchivoPDF] = useState(null);
  const [archivoXML, setArchivoXML] = useState(null);

 
  const [viewMode, setViewMode] = useState('mensual'); // 'mensual' | 'anual' | 'especiales'
  // C-020: el ejercicio de la Declaración anual (abre en el más reciente cuyo plazo ya empezó)
  const [ejercicio, setEjercicio] = useState(() => ejercicioActual());
  const [anualCliente, setAnualCliente] = useState(null); // "Ver en la tabla →" desde la ficha

 
  const [obligacionesMes, setObligacionesMes] = useState([]); // C-021: para los avisos "IMSS atrasado ›"
  const [obligAtrasadas, setObligAtrasadas] = useState(false); // abre Obligaciones adicionales en "Atrasadas"

  
  const [notasAdmin, setNotasAdmin] = useState('');
  const [guardandoNotas, setGuardandoNotas] = useState(false);
  const [puedeEditarNotas, setPuedeEditarNotas] = useState(false);

  
  const [showSolicitarPermiso, setShowSolicitarPermiso] = useState(false);
  const [mesParaPermiso, setMesParaPermiso] = useState(null);
  const [anioParaPermiso, setAnioParaPermiso] = useState(null);
  const [motivoPermiso, setMotivoPermiso] = useState('');
  const [solicitudesPendientes, setSolicitudesPendientes] = useState([]);
  const [showPermisosPanel, setShowPermisosPanel] = useState(false);
  // C-028: acceso del contador al mes elegido ({ editable, esFuturo, solicitud }) y panel de Administración
  const [accesoMes, setAccesoMes] = useState({ editable: true, esMesTrabajo: true, solicitud: null });
  const [solicitudesRespondidas, setSolicitudesRespondidas] = useState([]);
  const [rechazandoSolicitud, setRechazandoSolicitud] = useState(null); // { id, nota }
  const [errorMotivoPermiso, setErrorMotivoPermiso] = useState('');
  const [filtroHistorial, setFiltroHistorial] = useState(null); // { usuario_id, mes, anio, titulo }
  const [relojPermiso, setRelojPermiso] = useState(() => Date.now());

  const conversacionesRequestEnVueloRef = useRef(false);
  const ultimoFetchConversacionesRef = useRef(0);

  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [newMessage, setNewMessage] = useState({ para_usuario_id: '', mensaje: '', cliente_id: '', mensaje_padre_id: null });
  const [respondiendo, setRespondiendo] = useState(null);
  const [newClient, setNewClient] = useState(CLIENTE_NUEVO);
  const [editingClient, setEditingClient] = useState(null);

  // ── Viento ─────────────────────────────────────────────────────────────────
  const [vientoDepartamentos, setVientoDepartamentos] = useState([]);
  const [vientoSaldos, setVientoSaldos] = useState({});
  const [vientoHistoriales, setVientoHistoriales] = useState({});
  const [expandedViento, setExpandedViento] = useState(new Set());
  const [cargandoVientoHistorial, setCargandoVientoHistorial] = useState(new Set());
  const [busquedaViento, setBusquedaViento] = useState('');
  const [mostrarInactivosViento, setMostrarInactivosViento] = useState(false);
  const [showNuevoDepto, setShowNuevoDepto] = useState(false);
  const [showEditDepto, setShowEditDepto] = useState(false);
  const [newDepto, setNewDepto] = useState({ codigo: '', nombre: '', email: '', telefono: '', renta_mensual: '', notas: '', activo: true });
  const [editingDepto, setEditingDepto] = useState(null);
  const [showGenerarCobroViento, setShowGenerarCobroViento] = useState(false);
  const [deptoGenerarCobro, setDeptoGenerarCobro] = useState(null);
  const [formCobroViento, setFormCobroViento] = useState({ mes: new Date().getMonth() + 1, anio: new Date().getFullYear(), monto: '' });
  const [vientoResumenMensual, setVientoResumenMensual] = useState([]);
  const [selectedAnioViento, setSelectedAnioViento] = useState(new Date().getFullYear());
  const [editandoVientoCobro, setEditandoVientoCobro] = useState(null);
  const [editandoVientoFecha, setEditandoVientoFecha] = useState(null);
  // ───────────────────────────────────────────────────────────────────────────

  const [busqueda, setBusqueda] = useState(() => leerFiltroUrl('q') || "");
  // C-025: Contador, Régimen e Industria admiten varios valores (vacío = todos)
  const [filtroRegimen, setFiltroRegimen] = useState(() => leerListaUrl('regimen'));
  const [filtroIndustria, setFiltroIndustria] = useState(() => leerListaUrl('industria'));
  const [filtroContador, setFiltroContador] = useState(() => leerListaUrl('contador'));
  const [filtroPorVencer, setFiltroPorVencer] = useState(() => leerFiltroUrl('porvencer') === '1'); // C-024
  const [showGuiaMes, setShowGuiaMes] = useState(false); // C-024: "¿Cómo funciona el mes?"
  const [filtroPaso, setFiltroPaso] = useState(() => leerFiltroUrl('paso') || null);
  const [filtroAtrasados, setFiltroAtrasados] = useState(() => leerFiltroUrl('atrasados') === '1');
  const [filtroOpinionNegativa, setFiltroOpinionNegativa] = useState(() => leerFiltroUrl('opinion') === 'negativa');
  const [filtroPorRevisar, setFiltroPorRevisar] = useState(() => leerFiltroUrl('revision') === 'pendiente');
  const [filtroComplementarias, setFiltroComplementarias] = useState(() => leerFiltroUrl('complementarias') === '1'); // C-034
  // C-027: lista de clientes inactivos (Dueño y Supervisor)
  const [verInactivos, setVerInactivos] = useState(() => leerFiltroUrl('inactivos') === '1');
  const [clientesInactivos, setClientesInactivos] = useState([]);
  const [menuOpinion, setMenuOpinion] = useState(null); // `${cliente_id}-${proceso_id}` del menú abierto (C-006)
  // Validación de formularios: mensaje en rojo debajo del campo, no un aviso (C-011)
  const [erroresCampo, setErroresCampo] = useState({});
  const [avisoLogin, setAvisoLogin] = useState(null); // { tipo: 'servidor' | 'error', texto }

  const [showWhatsAppPanel, setShowWhatsAppPanel] = useState(false);
  const [notificacionesTab, setNotificacionesTab] = useState('enviar');
  const [envioManual, setEnvioManual] = useState({

  clientes_seleccionados: [],
  tipo: 'solicitud_inicial',
  mensaje_personalizado: ''
});
const [, setDocumentosFaltantes] = useState({}); // eslint-disable-line no-unused-vars
const [enviandoNotificaciones, setEnviandoNotificaciones] = useState(false);
const [reporteEmails, setReporteEmails] = useState({ dias: 30, resumen: { total: 0, enviados: 0, fallidos: 0 }, logs: [] });
const [cargandoReporteEmails, setCargandoReporteEmails] = useState(false);
const [descargandoRespaldo, setDescargandoRespaldo] = useState(false);

  useEffect(() => {
    const handlePopState = () => {
      // C-023: atrás/adelante entre la tabla y la ficha
      const id = idFichaEnUrl();
      if (id) { setFichaPendiente(id); return; }
      setShowProcesosDetalle(false);
      setCurrentRoute(window.location.pathname === '/cobranza' ? '/cobranza' : '/');
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const marcarError = (campo, texto) => {
    setErroresCampo(prev => ({ ...prev, [campo]: texto }));
    setTimeout(() => document.querySelector(`[data-campo="${campo}"]`)?.focus(), 0);
  };
  const limpiarErrores = (...campos) => setErroresCampo(prev => {
    if (!campos.some(c => prev[c])) return prev;
    const siguiente = { ...prev };
    campos.forEach(c => { delete siguiente[c]; });
    return siguiente;
  });
  const errorCampo = (campo) => erroresCampo[campo]
    ? <p className="error-campo" role="alert" id={`error-${campo}`}>{erroresCampo[campo]}</p>
    : null;
  const propsCampo = (campo) => ({
    'data-campo': campo,
    'aria-invalid': !!erroresCampo[campo],
    'aria-describedby': erroresCampo[campo] ? `error-${campo}` : undefined,
  });

  // Filtros de la pantalla principal → URL, para compartirlos y que sobrevivan a recargar (C-012)
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    if (currentRoute !== '/' || pathname === '/pago-completado' || pathname.startsWith('/pagar/')) return;
    const params = new URLSearchParams(search);
    const poner = (clave, valor) => (valor ? params.set(clave, valor) : params.delete(clave));
    poner('paso', filtroPaso);
    poner('atrasados', filtroAtrasados ? '1' : '');
    poner('opinion', filtroOpinionNegativa ? 'negativa' : '');
    poner('revision', filtroPorRevisar ? 'pendiente' : '');
    poner('complementarias', filtroComplementarias ? '1' : '');
    poner('inactivos', verInactivos ? '1' : '');
    poner('porvencer', filtroPorVencer ? '1' : '');
    poner('contador', filtroContador.join(','));
    poner('regimen', filtroRegimen.join(','));
    poner('industria', filtroIndustria.join(','));
    poner('q', busqueda.trim());
    const qs = params.toString();
    const nueva = `${pathname}${qs ? `?${qs}` : ''}${hash}`;
    if (nueva !== `${pathname}${search}${hash}`) {
      try { window.history.replaceState(window.history.state, '', nueva); } catch (e) { /* sin URL, los filtros siguen funcionando */ }
    }
  }, [currentRoute, filtroPaso, filtroAtrasados, filtroPorVencer, filtroOpinionNegativa, filtroPorRevisar, filtroComplementarias, verInactivos, filtroContador, filtroRegimen, filtroIndustria, busqueda]);

  // C-025: la última elección de Contador se recuerda en el navegador de cada persona
  // (la URL manda si trae una). Primero se lee y luego se guarda cada cambio.
  const contadorRecordado = useRef(false);
  useEffect(() => {
    if (!user?.id || contadorRecordado.current) return;
    contadorRecordado.current = true;
    if (leerFiltroUrl('contador')) return;
    try {
      const guardado = localStorage.getItem(`filtroContador:${user.id}`);
      if (guardado) setFiltroContador(guardado.split(',').filter(Boolean));
    } catch (e) { /* sin localStorage no se recuerda */ }
  }, [user?.id]);
  useEffect(() => {
    if (!user?.id || !contadorRecordado.current) return;
    try { localStorage.setItem(`filtroContador:${user.id}`, filtroContador.join(',')); } catch (e) { /* sin localStorage */ }
  }, [filtroContador, user?.id]);

  // Al hacer scroll solo quedan fijos la barra azul y los encabezados de la tabla (C-013).
  // La altura de la barra se mide en vivo y se pasa a CSS (--alto-encabezado) para que los
  // encabezados queden pegados justo debajo, sin una franja por donde se vea la tabla.
  const observadoresAltura = useRef({});
  const seguirAltura = useCallback((variable, el) => {
    observadoresAltura.current[variable]?.disconnect();
    delete observadoresAltura.current[variable];
    const raiz = document.documentElement;
    if (!el) {
      raiz.style.setProperty(variable, '0px');
      return;
    }
    const medir = () => raiz.style.setProperty(variable, `${el.offsetHeight}px`);
    medir();
    if (typeof ResizeObserver !== 'undefined') {
      const observador = new ResizeObserver(medir);
      observador.observe(el);
      observadoresAltura.current[variable] = observador;
    }
  }, []);
  const refEncabezado = useCallback((el) => seguirAltura('--alto-encabezado', el), [seguirAltura]);

  // Selector de mes y leyenda de la barra (C-013): se cierran con Esc; el clic fuera lo atrapa .menu-overlay
  useEffect(() => {
    if (selectorPeriodo === null && !showLeyenda && !showGuiaMes && !showAdminMenu && !showUserMenu) return;
    const alTeclear = (e) => {
      if (e.key !== 'Escape') return;
      setSelectorPeriodo(null);
      setShowLeyenda(false);
      setShowGuiaMes(false);
      setShowAdminMenu(false);
      setShowUserMenu(false);
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [selectorPeriodo, showLeyenda, showGuiaMes, showAdminMenu, showUserMenu]);

  // Menú de Opinión de cumplimiento: se cierra con Esc o con clic fuera (C-006)
  useEffect(() => {
    if (!menuOpinion) return;
    const alClic = (e) => {
      if (!e.target.closest || !e.target.closest('.opinion-wrap')) setMenuOpinion(null);
    };
    const alTeclear = (e) => { if (e.key === 'Escape') setMenuOpinion(null); };
    document.addEventListener('mousedown', alClic);
    document.addEventListener('keydown', alTeclear);
    return () => {
      document.removeEventListener('mousedown', alClic);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [menuOpinion]);

  useEffect(() => {
    const clientesOrdenados = [...clientes].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

    setCobranzaData((prevData) =>
      clientesOrdenados.map((cliente) => {
        const existing = prevData.find((item) => item.id === cliente.id);
        return {
          id: cliente.id,
          nombre: cliente.nombre,
          rfc: cliente.rfc || '',
          email: cliente.email || '',
          otros_ingresos: cliente.otros_ingresos || false,
          industria: cliente.industria || '',
          factura: existing?.factura ?? (Number(cliente.factura_mensual) || ''),
          monto: existing?.monto ?? (Number(cliente.factura_mensual) || ''),
        };
      })
    );
  }, [clientes]);

  // Cargar saldos y desglose de cobros pendientes al entrar a cobranza
  useEffect(() => {
    if (currentRoute !== '/cobranza' || !veCobranza(user)) return;
    fetchAPI('/cobranza/saldos-cobros')
      .then(data => { if (data?.saldos) setCobranzaSaldos(data.saldos); })
      .catch(() => {});
    fetchAPI('/cobranza/cobros-pendientes-todos')
      .then(data => { if (data?.cobros) setCobrosPendientesTodos(data.cobros); })
      .catch(() => {});
    fetchAPI('/clientes?cobranza=1')
      .then(data => {
        if (!data?.clientes) return;
        setCobranzaTodosClientes(data.clientes);
        // Agregar clientes solo_cobranza a cobranzaData para que sean editables
        setCobranzaData(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const missing = data.clientes
            .filter(cl => !existingIds.has(cl.id))
            .map(cl => ({
              id: cl.id,
              nombre: cl.nombre,
              rfc: cl.rfc || '',
              email: cl.email || '',
              otros_ingresos: cl.otros_ingresos || false,
              industria: cl.industria || '',
              factura: Number(cl.factura_mensual) || '',
              monto: Number(cl.factura_mensual) || '',
            }));
          return missing.length > 0 ? [...prev, ...missing] : prev;
        });
      })
      .catch(() => {});
    fetchAPI(`/cobranza/resumen-mensual?anio=${selectedAnio}`)
      .then(data => { if (data?.resumen) setResumenMensual(data.resumen); })
      .catch(() => {});
    // Solo al entrar a Cobranza o cambiar de año: fetchAPI se recrea en cada render y recargaría siempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute, selectedAnio]);

  useEffect(() => {
    if (currentRoute !== '/viento' || !veViento(user)) return;
    fetchAPI('/viento/departamentos')
      .then(data => { if (Array.isArray(data)) setVientoDepartamentos(data); })
      .catch(() => {});
    fetchAPI('/viento/saldos')
      .then(data => { if (data?.saldos) setVientoSaldos(data.saldos); })
      .catch(() => {});
    fetchAPI(`/viento/resumen-mensual?anio=${selectedAnioViento}`)
      .then(data => { if (Array.isArray(data)) setVientoResumenMensual(data); })
      .catch(() => {});
    // Solo al entrar a Viento o cambiar de año (ver el efecto de Cobranza arriba).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoute, selectedAnioViento]);

  const anioMaximo = new Date().getFullYear() + 1;
  const ejercicioMaximo = new Date().getFullYear(); // C-020: el ejercicio en curso (todavía no vence)
  const nombreMes = (i) => meses[i].charAt(0) + meses[i].slice(1).toLowerCase();
  const meses = ['ENERO','FEBRERO','MARZO','ABRIL','MAYO','JUNIO','JULIO','AGOSTO','SEPTIEMBRE','OCTUBRE','NOVIEMBRE','DICIEMBRE'];

  const fetchAPI = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    
    const timeoutId = setTimeout(() => {
      setReconectando(true);
    }, 5000);

    try {
      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers,
      });

      clearTimeout(timeoutId);
      setReconectando(false);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        const err = new Error(errorData.error || 'Error en la petición');
        err.status = response.status;
        throw err;
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      setReconectando(false);
      throw error;
    }
  };

  useEffect(() => {
    if(token) {
      verificarToken();
    } else {
      setVerificandoToken(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  //  inactividad de los 10 minutos
  useEffect(() => {
    if (!user) return;

    const TIEMPO_INACTIVIDAD = 10 * 60 * 1000; 

    const verificarInactividad = () => {
      const ultimaActividad = localStorage.getItem('ultimaActividad');
      if (ultimaActividad) {
        const tiempoTranscurrido = Date.now() - parseInt(ultimaActividad);
        if (tiempoTranscurrido > TIEMPO_INACTIVIDAD) {
          console.log('Sesión cerrada por inactividad');
          handleLogout();
        }
      }
    };

    
    const actualizarActividad = () => {
      localStorage.setItem('ultimaActividad', Date.now().toString());
    };

    
    window.addEventListener('mousedown', actualizarActividad);
    window.addEventListener('keydown', actualizarActividad);
    window.addEventListener('scroll', actualizarActividad);
    window.addEventListener('touchstart', actualizarActividad);

    
    const intervalo = setInterval(verificarInactividad, 60000);

    return () => {
      window.removeEventListener('mousedown', actualizarActividad);
      window.removeEventListener('keydown', actualizarActividad);
      window.removeEventListener('scroll', actualizarActividad);
      window.removeEventListener('touchstart', actualizarActividad);
      clearInterval(intervalo);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // C-016: cada quien entra a una sección que sí puede ver (Eva entra directo a Cobranza).
  useEffect(() => {
    if (!user) return;
    const secciones = seccionesDe(user);
    if (secciones.length > 0 && !secciones.some(sec => sec.ruta === currentRoute)) {
      navegarA(secciones[0].ruta);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentRoute]);

  useEffect(() => {
  if(user){
    cargarDatos();
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [user, selectedMes, selectedAnio]);

useEffect(() => {
  if(!showMessages || !user) return;

  const interval = setInterval(() => {
    cargarConversaciones();
  }, 15000);

  return ()=>clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showMessages, user]);

  // C-027: número de inactivos para la pastilla (Dueño y Supervisor)
  useEffect(() => {
    if (user && esAdministrador(user)) cargarInactivos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // C-028: acceso del contador al mes elegido (franja de solo lectura)
  const periodoRef = useRef({ mes: selectedMes, anio: selectedAnio });
  periodoRef.current = { mes: selectedMes, anio: selectedAnio };
  useEffect(() => {
    if (user?.rol === 'contador') cargarAccesoMes(selectedMes, selectedAnio);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedMes, selectedAnio]);

  // C-028: la respuesta a sus solicitudes le llega al contador sin volver a intentar entrar
  useEffect(() => {
    if (user?.rol !== 'contador') return;
    revisarAvisosPermiso();
    const interval = setInterval(revisarAvisosPermiso, 30000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // C-028: mientras tiene acceso temporal, se revisa cada minuto si falta una hora o si ya venció
  const avisoPorVencerDado = useRef(null);
  useEffect(() => {
    const sol = accesoMes.solicitud;
    if (user?.rol !== 'contador' || sol?.estado !== 'aprobado') return;
    const revisar = () => {
      setRelojPermiso(Date.now());
      const minutos = minutosRestantes(sol.fecha_expiracion);
      const mes = nombreMesPermiso(sol.mes, sol.anio);
      if (minutos <= 0) {
        avisar({ tipo: 'info', texto: `Tu acceso a ${mes} venció. ${mes.replace(/^./, l => l.toUpperCase())} vuelve a solo lectura.` });
        cargarAccesoMes();
      } else if (minutos <= 60 && avisoPorVencerDado.current !== sol.id) {
        avisoPorVencerDado.current = sol.id;
        avisar({ tipo: 'info', texto: `Tu acceso a ${mes} vence ${textoMinutos(minutos)}.` });
      }
    };
    revisar();
    const interval = setInterval(revisar, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accesoMes.solicitud]);

  // Cargar solicitudes de permiso pendientes (solo admin)
  useEffect(() => {
    if(user && esAdministrador(user)){
      cargarSolicitudesPendientes();
      const interval = setInterval(cargarSolicitudesPendientes, 30000); // Cada 30 segundos
      return ()=>clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // C-023: si se llegó por la dirección de la ficha (o con atrás/adelante), se abre al tener los clientes.
  useEffect(() => {
    if (!fichaPendiente || !user) return;
    const existe = clientes.some(c => c.id === fichaPendiente)
      || cobranzaTodosClientes.some(c => c.id === fichaPendiente);
    if (!existe && clientes.length === 0) return; // todavía cargando
    const id = fichaPendiente;
    setFichaPendiente(null);
    abrirProcesosDetalle(id, 'resumen', { conHistorial: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fichaPendiente, clientes, cobranzaTodosClientes, user]);

  // C-021: obligaciones adicionales del mes, para los avisos de la vista mensual
  useEffect(() => {
    if (!user || !veClientes(user)) return;
    apiObligaciones.listar(selectedMes + 1, selectedAnio)
      .then(res => setObligacionesMes(res.obligaciones || []))
      .catch(() => setObligacionesMes([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedMes, selectedAnio]);

useEffect(() => {
  if(showMessages && user){
    cargarConversaciones({ force: true });
    cargarUsuarios();
    cargarNotas(); // Todos pueden ver las notas
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showMessages]);


useEffect(() => {
  if (mensajesConversacion.length > 0) {
    setTimeout(() => {
      const chatMessages = document.querySelector('.chat-messages');
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    }, 100);
  }
}, [mensajesConversacion]);

  const verificarToken = async () => {
  try {
    await fetchAPI('/clientes');
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    setVerificandoToken(false);
    
    localStorage.setItem('ultimaActividad', Date.now().toString());
  } catch(err) {
    console.error('Error verificando token:', err);
    // Solo cerrar sesión si el error es 401 (token inválido/expirado), no por errores de red
    if (err.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('ultimaActividad');
      setToken(null);
      setUser(null);
    }
    setVerificandoToken(false);
   
    setLoginData({ username: '', password: '' });
  }
};

 

  const handleLogin = async (e) => {
    setAvisoLogin(null);
    e.preventDefault();
    try {
      setVerificandoToken(true);
      setLoadingMessage('Conectando al servidor...');

      const res = await fetchAPI('/login', {
        method: 'POST',
        body: JSON.stringify(loginData),
      });

      setLoadingMessage('Autenticando usuario...');
      setToken(res.token);
      setUser(res.user);
      localStorage.setItem('token', res.token);
      localStorage.setItem('user', JSON.stringify(res.user));
      localStorage.setItem('ultimaActividad', Date.now().toString());

      setLoadingMessage('Cargando datos...');
      // Limpiar campos de login después de inicio exitoso
      setLoginData({ username: '', password: '' });
    } catch(err){
      setVerificandoToken(false);
      // Mensaje amigable para errores de conexión
      if (err.message?.includes('Failed to fetch') || err.message?.includes('timeout')) {
        setAvisoLogin({ tipo: 'servidor', texto: 'El servidor está iniciando. Espera unos segundos e intenta de nuevo.' });
      } else {
        console.error('No se pudo iniciar sesión:', err);
        setAvisoLogin({
          tipo: 'error',
          texto: err.status >= 400 && err.status < 500 && err.message ? err.message : 'No se pudo iniciar sesión. Intenta de nuevo.',
        });
      }
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('ultimaActividad');
   
    setLoginData({ username: '', password: '' });
    
    const mesAnterior = calcularMesAnterior();
    setSelectedMes(mesAnterior.mes);
    setSelectedAnio(mesAnterior.anio);
  };

  const cargarDatos = async () => {
  try {
    // C-016: Cobranza (Eva) no ve la tabla de clientes; solo se cargan los usuarios para Mensajes.
    if (!veClientes(user)) {
      cargarUsuarios();
      setVerificandoToken(false);
      return;
    }
    setLoadingMessage('Cargando clientes y procesos...');
    const res = await fetchAPI(`/clientes?mes=${selectedMes+1}&anio=${selectedAnio}`);
    setClientes(res.clientes);
    setProcesos(res.procesos);

    setLoadingMessage('Casi listo...');
    
    cargarUsuarios();

   
    setTimeout(() => setVerificandoToken(false), 500);
  } catch(err){
    console.error(err);
    setVerificandoToken(false);
  }
};



  const cargarMensajes = async () => {
  try{
    const res = await fetchAPI('/mensajes');
    setMensajes(res);
    
   
    if (showMessages) {
      await fetchAPI('/mensajes/marcar-todos-leidos', {
        method: 'PUT',
        body: JSON.stringify({}),
      });
    }
  } catch(err){ console.error(err); }
};
    const cargarConversaciones = async ({ force = false } = {}) => {
  const ahora = Date.now();

  if (conversacionesRequestEnVueloRef.current) {
    return;
  }

  if (!force && ahora - ultimoFetchConversacionesRef.current < 3000) {
    return;
  }

  conversacionesRequestEnVueloRef.current = true;
  ultimoFetchConversacionesRef.current = ahora;

  try {
    const res = await fetchAPI('/mensajes/conversaciones');
    setConversaciones(res);
  } catch(err) { 
    console.error(err); 
  } finally {
    conversacionesRequestEnVueloRef.current = false;
  }
};

  const cargarMensajesConversacion = async (otroUsuarioId) => {
  try {
    setConversacionActiva(otroUsuarioId);
    setCargandoMensajes(true);
    
    const res = await fetchAPI(`/mensajes/conversacion/${otroUsuarioId}`);
    setMensajesConversacion(res);
    
    // Establecer el destinatario correcto
    setNewMessage({ 
      para_usuario_id: otroUsuarioId, 
      mensaje: '', 
      cliente_id: '', 
      mensaje_padre_id: null 
    });
    setRespondiendo(null); // Limpiar respuesta anterior
    
    
    await fetchAPI(`/mensajes/marcar-leidos/${otroUsuarioId}`, {
      method: 'PUT',
      body: JSON.stringify({}),
    });
    
    
    await cargarConversaciones({ force: true });
    
    // Hacer scroll al final del chat
    setTimeout(() => {
      const chatMessages = document.querySelector('.chat-messages');
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight;
      }
    }, 100);
    
  } catch(err) { 
    console.error('Error al cargar conversación:', err); 
  } finally {
    setCargandoMensajes(false);
  }
};
  const cargarUsuarios = async () => {
  try{
    const res = await fetchAPI('/usuarios');
    setUsuarios(res);
  } catch(err){ 
    // Si no es admin, cargar al menos el usuario actual
    console.error(err); 
  }
};

  const cambiarPassword = async (e) => {
    e.preventDefault();
    
    limpiarErrores('password-nueva', 'password-confirmar');
    if (passwordData.password_nueva !== passwordData.password_confirmar) {
      marcarError('password-confirmar', 'Las contraseñas no coinciden.');
      return;
    }

    if (passwordData.password_nueva.length < 6) {
      marcarError('password-nueva', 'La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    try {
      await fetchAPI('/usuarios/cambiar-password', {
        method: 'PUT',
        body: JSON.stringify({
          password_actual: passwordData.password_actual,
          password_nueva: passwordData.password_nueva
        }),
      });
      
      avisar({ texto: 'Contraseña cambiada.' });
      setPasswordData({ password_actual: '', password_nueva: '', password_confirmar: '' });
    } catch(err) {
      avisarError('cambiar la contraseña', err);
    }
  };

  const actualizarPerfil = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetchAPI('/usuarios/perfil', {
        method: 'PUT',
        body: JSON.stringify({ nombre: perfilData.nombre, email: perfilData.email }),
      });
      
      setUser(res.user);
      localStorage.setItem('user', JSON.stringify(res.user));
      avisar({ texto: 'Perfil actualizado.' });
    } catch(err) {
      avisarError('actualizar tu perfil', err);
    }
  };

  const crearUsuario = async (e) => {
    e.preventDefault();
    
    try {
      await fetchAPI('/usuarios', {
        method: 'POST',
        body: JSON.stringify(newUserData),
      });
      
      avisar({ texto: 'Usuario creado.' });
      setNewUserData({ username: '', password: '', nombre: '', email: '', rol: 'contador' });
      cargarUsuarios();
    } catch(err) {
      avisarError('crear el usuario', err);
    }
  };

  const editarUsuario = async (e) => {
    e.preventDefault();
    
    try {
      await fetchAPI(`/usuarios/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(editingUser),
      });
      
      avisar({ texto: 'Usuario actualizado.' });
      setEditingUser(null);
      cargarUsuarios();
    } catch(err) {
      avisarError('actualizar el usuario', err);
    }
  };

  const toggleActivoUsuario = async (id) => {
    try {
      const res = await fetchAPI(`/usuarios/${id}/toggle-activo`, {
        method: 'PUT',
        body: JSON.stringify({}),
      });
      
      avisar({ texto: res.message });
      cargarUsuarios();
    } catch(err) {
      avisarError('cambiar el estado del usuario', err);
    }
  };

  const eliminarUsuario = async (id) => {
    const usuario = usuarios.find(u => u.id === id);
    const ok = await confirmar({
      nivel: 3,
      peligro: true,
      titulo: `¿Eliminar al usuario ${usuario?.username || ''}?`.replace(' ?', '?'),
      cuerpo: 'Ya no podrá entrar al sistema. Esta acción no se puede deshacer.',
      accion: 'Eliminar usuario',
      confirmarTexto: usuario?.username,
    });
    if (!ok) return;

    try {
      await fetchAPI(`/usuarios/${id}`, { method: 'DELETE' });
      avisar({ texto: `Usuario ${usuario?.username || ''} eliminado.`.replace('  ', ' ') });
      cargarUsuarios();
    } catch(err) {
      avisarError('eliminar el usuario', err);
    }
  };

  const descargarRespaldo = async () => {
    setDescargandoRespaldo(true);
    try {
      const response = await fetch(`${API_URL}/admin/archivos/bulk-download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(err.error || 'Error al generar el respaldo');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `respaldo_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      avisarError('descargar el respaldo', err);
    } finally {
      setDescargandoRespaldo(false);
    }
  };

  // filtro (C-028): { usuario_id, mes, anio, titulo } para ver los cambios hechos con un acceso
  const cargarHistorial = async (filtro = null) => {
    try{
      const params = filtro ? `?usuario_id=${filtro.usuario_id}&mes=${filtro.mes}&anio=${filtro.anio}` : '';
      const res = await fetchAPI(`/historial${params}`);
      setHistorial(res);
      setFiltroHistorial(filtro);
      setShowHistory(true);
    } catch(err){ console.error(err); }
  };

  // ───── Documentos del cliente (C-015, ver Documentos.js) ─────
  // `api` es estable (useMemo sin dependencias) y lee lo vigente de un ref: los componentes la usan
  // en sus efectos sin volver a cargar en cada render.
  const docsRef = useRef({});
  docsRef.current = { fetchAPI, token, user, accesoMes, selectedMes, selectedAnio };
  const urlArchivo = async (archivo, ver) => {
    const { token: t } = docsRef.current;
    const response = await fetch(`${API_URL}/archivos/download/${archivo.id}${ver ? '?ver=1' : ''}`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw Object.assign(new Error(data.error || 'Error al obtener archivo'), { status: response.status });
    return data;
  };
  const apiDocumentos = useMemo(() => ({
    listar: (clienteId, { mes, anio }) =>
      docsRef.current.fetchAPI(`/archivos/${clienteId}?${mes ? `mes=${mes}&` : ''}anio=${anio}`),
    subir: async (file, tipo, clienteId, mes, anio) => {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('cliente_id', clienteId);
      formData.append('mes', mes);
      formData.append('anio', anio);
      formData.append('tipo_documento', tipo);
      const response = await fetch(`${API_URL}/archivos`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${docsRef.current.token}` },
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw Object.assign(new Error(data.error || 'Error al subir archivo'), { status: response.status });
      return data;
    },
    cambiarTipo: (id, tipo) =>
      docsRef.current.fetchAPI(`/archivos/${id}/tipo`, { method: 'PUT', body: JSON.stringify({ tipo_documento: tipo }) }),
    eliminar: (id) => docsRef.current.fetchAPI(`/archivos/${id}`, { method: 'DELETE' }),
    // PDF e imágenes en una pestaña nueva; lo demás se descarga con su nombre.
    abrir: async (archivo) => {
      const ver = seAbreEnNavegador(archivo.nombre_archivo);
      // La pestaña se abre antes de esperar al servidor para que el navegador no la bloquee.
      const pestana = ver ? window.open('', '_blank') : null;
      try {
        const data = await urlArchivo(archivo, ver);
        if (pestana) pestana.location.href = data.url;
        // Si el navegador bloqueó la pestaña, el botón del aviso la abre (es un clic nuevo).
        else if (ver) avisar({ tipo: 'info', texto: `${archivo.nombre_archivo} está listo.`, accion: { texto: 'Abrir', alHacer: () => window.open(data.url, '_blank') } });
        else descargarDesdeUrl(data.url, data.nombre || archivo.nombre_archivo);
      } catch (err) {
        if (pestana) pestana.close();
        avisarError(ver ? 'abrir el archivo' : 'descargar el archivo', err);
      }
    },
    descargar: async (archivo) => {
      try {
        const data = await urlArchivo(archivo, false);
        descargarDesdeUrl(data.url, data.nombre || archivo.nombre_archivo);
      } catch (err) {
        avisarError('descargar el archivo', err);
      }
    },
    puedeEliminar: (archivo) => {
      const { user: u } = docsRef.current;
      return esAdministrador(u) || archivo.subido_por === u?.id;
    },
    // C-028: un contador no cambia un mes pasado sin acceso. Solo se conoce el del mes elegido;
    // para otros meses, el servidor responde con el motivo.
    soloLectura: (mes, anio) => {
      const { user: u, accesoMes: acceso, selectedMes: m, selectedAnio: a } = docsRef.current;
      return u?.rol === 'contador' && mes === m + 1 && anio === a && !acceso.editable;
    },
    avisarSoloLectura: () => avisarSoloLecturaRef.current(),
  }), []);
  // C-021: obligaciones adicionales. Estable, como apiDocumentos.
  const apiObligaciones = useMemo(() => ({
    listar: (mes, anio) => docsRef.current.fetchAPI(`/obligaciones?mes=${mes}&anio=${anio}`),
    guardar: (clienteId, procesoId, estado, mes, anio) => docsRef.current.fetchAPI('/estados', {
      method: 'PUT',
      body: JSON.stringify({ cliente_id: clienteId, proceso_id: procesoId, mes, anio, estado }),
    }),
    soloLectura: () => {
      const { user: u, accesoMes: acceso } = docsRef.current;
      return u?.rol === 'contador' && !acceso.editable;
    },
    avisarSoloLectura: () => avisarSoloLecturaRef.current(),
  }), []);
  // C-023: lo que lee el Resumen de la ficha. Cobranza solo para quien la ve.
  const puedeVerCobranza = veCobranza(user);
  const apiResumen = useMemo(() => ({
    estadosAnio: (id, anio) => docsRef.current.fetchAPI(`/clientes/${id}/estados-anio?anio=${anio}`),
    anual: (ej, id) => docsRef.current.fetchAPI(`/anual?ejercicio=${ej}&cliente_id=${id}`),
    cobranza: puedeVerCobranza ? (id) => docsRef.current.fetchAPI(`/cobranza/cliente/${id}/historial`) : null,
    documentos: (id, anio) => docsRef.current.fetchAPI(`/archivos/${id}?anio=${anio}`),
    abrirArchivo: (a) => apiDocumentos.abrir(a),
  }), [puedeVerCobranza]); // eslint-disable-line react-hooks/exhaustive-deps

  // C-020: declaración anual. Estable, como apiDocumentos.
  const apiAnual = useMemo(() => ({
    listar: (ej) => docsRef.current.fetchAPI(`/anual?ejercicio=${ej}`),
    guardar: (clienteId, ej, paso, estado) => docsRef.current.fetchAPI('/anual', {
      method: 'PUT',
      body: JSON.stringify({ cliente_id: clienteId, ejercicio: ej, paso, estado }),
    }),
  }), []);
  // Al cambiar los archivos de un cliente, la columna Documentos se actualiza sin recargar la tabla.
  const alCambiarDocumentos = useCallback((clienteId, total) => {
    setClientes(prev => prev.map(c => (c.id === clienteId ? { ...c, archivos_count: total } : c)));
  }, []);


  
  
 const cambiarEstado = async (cliente_id, proceso_id) => {
  const cliente = clientes.find(c => c.id === cliente_id);
  const estadoActual = cliente.estados.find(e => e.proceso_id === proceso_id)?.estado || 'rojo';
  const proceso = procesos.find(p => p.id === proceso_id);
  // "Presentado en $0" solo aplica a Declaración Presentada (C-002).
  const permitePresentadoCero = esDeclaracionPresentada(proceso);
  // "Revisado" solo existe en los pasos que Pedro revisa en este cliente (C-014).
  const revisable = !!proceso && esPasoRevisable(cliente, proceso);


  let nuevoEstado;

  if (estadoActual === 'rojo') {
    nuevoEstado = 'amarillo';
  } else if (estadoActual === 'amarillo') {
    nuevoEstado = 'verde';
  } else if (estadoActual === 'verde') {
    if (revisable && marcaRevisado(user)) { // C-014 / C-016: solo el Supervisor marca Revisado
      nuevoEstado = 'azul';
    } else {
      nuevoEstado = permitePresentadoCero ? 'rosa' : 'rojo'; // Contador: verde → presentado $0
    }
  } else if (estadoActual === 'azul') {
    nuevoEstado = permitePresentadoCero ? 'rosa' : 'rojo'; // Admin: azul → presentado $0
  } else if (estadoActual === 'rosa') {
    nuevoEstado = 'rojo'; // Regresa a pendiente
  } else {
    nuevoEstado = 'rojo';
  }

  // C-034: con pasos anteriores sin terminar, la declaración solo se puede presentar en ceros
  // (para evitar la multa): el clic va directo a "Presentada en $0".
  if (permitePresentadoCero && estadoActual === 'rojo') {
    const actual = calcularPasoActual(cliente, procesos, fechasLimite, periodoSeleccionado);
    const indiceDeclaracion = procesos.filter(p => !esOpinionCumplimiento(p)).findIndex(p => p.id === proceso_id);
    if (actual && actual.indice < indiceDeclaracion) nuevoEstado = 'rosa';
  }

  // C-034: "Presentada en $0" pregunta el motivo; los provisionales quedan con complementaria pendiente.
  let motivo = null;
  if (nuevoEstado === 'rosa') {
    motivo = await confirmar({
      titulo: `¿Por qué se presenta en $0 la declaración de ${cliente.nombre}?`,
      cuerpo: 'Solo "Sin movimientos" cuenta como terminada. En los otros dos casos queda "Complementaria pendiente" hasta que se presente la correcta.',
      eleccion: { etiqueta: 'Motivo', opciones: MOTIVOS_CERO.map(m => ({ valor: m.valor, texto: m.texto, detalle: m.detalle })) },
      accion: 'Marcar Presentada en $0',
    });
    if (!motivo) return;
  }

  // Los pasos que siguen a uno que Pedro revisa esperan su revisión; regresar a Por hacer sí se puede (C-014).
  // La declaración en ceros no espera: se presenta para evitar la multa (C-034).
  const pendiente = proceso && nuevoEstado !== 'rojo' && nuevoEstado !== 'rosa' ? revisionPendienteAntes(cliente, proceso, procesos) : null;
  if (pendiente) {
    const nombre = nombreVisibleProceso(pendiente);
    avisar({
      tipo: 'error',
      texto: estadoGuardadoDe(cliente, pendiente.id) === 'verde'
        ? `Este paso espera la revisión de Pedro en ${nombre}.`
        : `Primero termina ${nombre}: Pedro lo revisa antes de seguir.`,
    });
    return;
  }

  const guardado = await guardarEstado(cliente_id, proceso_id, nuevoEstado, motivo);
  // C-037: quien completa un paso que Pedro revisa sabe que ya le llegó el aviso
  if (guardado && nuevoEstado === 'verde' && revisable && !marcaRevisado(user)) {
    avisar({ tipo: 'info', texto: `${nombreVisibleProceso(proceso)} de ${cliente.nombre} quedó Por revisar. Pedro recibirá el aviso.` });
  }
};

 const estadoGuardadoDe = (cliente, proceso_id) =>
  cliente?.estados?.find(e => e.proceso_id === proceso_id)?.estado || 'rojo';

 // Guarda un estado (optimista) y lo manda al servidor. Lo usan el ciclo de clics y el menú de Opinión.
 const guardarEstado = async (cliente_id, proceso_id, nuevoEstado, motivoCero = null) => {
  // C-028: un mes pasado sin acceso se ve, pero no se cambia
  if (soloLectura()) { avisarSoloLectura(); return; }
  // Actualizar tabla principal 
  setClientes(prevClientes => 
    prevClientes.map(c => {
      if (c.id === cliente_id) {
        const estadosActualizados = c.estados.filter(e => e.proceso_id !== proceso_id);
        estadosActualizados.push({
          proceso_id,
          estado: nuevoEstado,
          motivo_cero: motivoCero,
          actualizado_por: user.id,
          usuario_nombre: user.nombre,
          fecha_actualizacion: new Date().toISOString()
        });
        return { ...c, estados: estadosActualizados };
      }
      return c;
    })
  );
  
  
  if (showProcesosDetalle && clienteSeleccionado?.id === cliente_id) {
    setProcesosDetalle(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(categoria => {
        updated[categoria].procesos = updated[categoria].procesos.map(p => 
          p.id === proceso_id 
            ? { ...p, estado: nuevoEstado, fecha_actualizacion: new Date().toISOString(), actualizado_por_nombre: user.nombre }
            : p
        );
      });
      return updated;
    });
  }
  
 
  try {
    await fetchAPI('/estados', {
      method: 'PUT',
      body: JSON.stringify({ 
        cliente_id, 
        proceso_id, 
        mes: selectedMes + 1, 
        anio: selectedAnio, 
        estado: nuevoEstado,
        motivo_cero: motivoCero,
      }),
    });
    return true;
  } catch (error) {
    avisarError('cambiar el estado; se regresó al anterior', error);
    cargarDatos();
    return false;
  }
};

 const enviarMensaje = async (e) => {
  e.preventDefault();
  
  
  const mensajeAEnviar = {
    para_usuario_id: conversacionActiva,
    mensaje: newMessage.mensaje,
    cliente_id: newMessage.cliente_id || null,
    mensaje_padre_id: respondiendo ? respondiendo.id : null
  };
  
  limpiarErrores('mensaje-texto');
  if (!mensajeAEnviar.para_usuario_id) {
    marcarError('mensaje-texto', 'Elige una conversación antes de escribir.');
    return;
  }
  
  if (!mensajeAEnviar.mensaje.trim()) {
    marcarError('mensaje-texto', 'Escribe un mensaje.');
    return;
  }
  
  try{
    await fetchAPI('/mensajes', {
      method: 'POST',
      body: JSON.stringify(mensajeAEnviar),
    });
    
   
    setNewMessage({ 
      para_usuario_id: conversacionActiva, 
      mensaje: '', 
      cliente_id: '', 
      mensaje_padre_id: null 
    });
    setRespondiendo(null);
    
  
    await cargarConversaciones({ force: true });
    
    
    if (conversacionActiva) {
      await cargarMensajesConversacion(conversacionActiva);
    }
    
  } catch(err) { 
    console.error('Error al enviar:', err);
    avisarError('enviar el mensaje', err); 
  }
};

const cargarNotas = async () => {
  try {
    const res = await fetchAPI('/notas-admin');
    setNotasAdmin(res.notas || '');
    setPuedeEditarNotas(res.puede_editar || false);
  } catch(err) {
    console.error('Error al cargar notas:', err);
  }
};

const guardarNotas = async () => {
  setGuardandoNotas(true);
  try {
    await fetchAPI('/notas-admin', {
      method: 'PUT',
      body: JSON.stringify({ notas: notasAdmin })
    });
    // Recargar notas para confirmar persistencia
    await cargarNotas();
    avisar({ texto: 'Notas guardadas.' });
  } catch(err) {
    console.error('Error al guardar notas:', err);
    avisarError('guardar las notas', err);
  } finally {
    setGuardandoNotas(false);
  }
};

// ========== FUNCIONES PARA PERMISOS ==========

// C-028: { tiene_permiso, es_mes_trabajo, es_futuro, solo_lectura, solicitud }
const verificarPermiso = async (mes, anio) => {
  try {
    return await fetchAPI(`/permisos/verificar/${mes}/${anio}`);
  } catch(err) {
    console.error('Error al verificar permiso:', err);
    return { tiene_permiso: false, solo_lectura: true, solicitud: null };
  }
};

// Acceso del contador al mes elegido: el mes de trabajo y los meses con permiso vigente se editan;
// los demás meses pasados se ven en solo lectura (C-028).
// Se lee el mes elegido de un ref: los avisos y el reloj del acceso corren en intervalos que
// se crearon con el mes de ese momento, y deben revisar el mes que se ve ahora.
const cargarAccesoMes = async (mes = periodoRef.current.mes, anio = periodoRef.current.anio) => {
  if (!user || user.rol !== 'contador') { setAccesoMes({ editable: true, esMesTrabajo: true, solicitud: null }); return; }
  const res = await verificarPermiso(mes, anio);
  // Si mientras tanto cambió el mes, esta respuesta ya no aplica
  if (mes !== periodoRef.current.mes || anio !== periodoRef.current.anio) return;
  setAccesoMes({ editable: !!res.tiene_permiso, esMesTrabajo: !!res.es_mes_trabajo, solicitud: res.solicitud || null });
};

const mesElegidoTexto = () => nombreMesPermiso(selectedMes, selectedAnio);
const mesElegidoTitulo = () => mesElegidoTexto().replace(/^./, l => l.toUpperCase());

// El contador intenta cambiar algo de un mes en solo lectura
const avisarSoloLectura = () => {
  if (accesoMes.solicitud?.estado === 'pendiente') {
    avisar({ tipo: 'info', texto: `Tu solicitud de acceso a ${mesElegidoTexto()} sigue esperando respuesta.` });
    return;
  }
  avisar({
    tipo: 'info',
    texto: `${mesElegidoTitulo()} es solo lectura.`,
    accion: { texto: 'Pedir acceso…', alHacer: () => pedirAcceso() },
  });
};

const soloLectura = () => user?.rol === 'contador' && !accesoMes.editable;
const avisarSoloLecturaRef = useRef(avisarSoloLectura);
avisarSoloLecturaRef.current = avisarSoloLectura;

const pedirAcceso = (mes = selectedMes, anio = selectedAnio) => {
  setMesParaPermiso(mes);
  setAnioParaPermiso(anio);
  setMotivoPermiso('');
  setErrorMotivoPermiso('');
  setShowSolicitarPermiso(true);
};

const cancelarSolicitudPermiso = async () => {
  const sol = accesoMes.solicitud;
  if (!sol) return;
  try {
    await fetchAPI(`/permisos/${sol.id}/cancelar`, { method: 'POST' });
    avisar({ texto: 'Cancelaste tu solicitud.' });
    cargarAccesoMes();
  } catch (err) { avisarError('cancelar la solicitud', err); }
};


// Selector de periodo (C-013): mes y año juntos, con la misma regla de permisos que cambiarAnio:
// el admin cambia directo; un contador necesita permiso para ese mes.
const cambiarPeriodo = async (nuevoMes, nuevoAnio) => {
  if (!user) return;
  if (nuevoAnio < ANIO_MINIMO || nuevoAnio > anioMaximo) return;
  // C-017: Cobranza y Viento llevan su propio mes (abren en el mes en curso), sin permisos de mes
  if (periodoPropio) {
    setPeriodoCobranza({ mes: nuevoMes, anio: nuevoAnio });
    return;
  }
  if (nuevoMes === selectedMes && nuevoAnio === selectedAnio) return;

  // C-028: los meses pasados se abren en solo lectura; los que todavía no se trabajan, no
  if (user.rol === 'contador') {
    const res = await verificarPermiso(nuevoMes, nuevoAnio);
    if (res.es_futuro) {
      avisar({ tipo: 'info', texto: `${nombreMesPermiso(nuevoMes, nuevoAnio).replace(/^./, l => l.toUpperCase())} todavía no se trabaja.` });
      return;
    }
    setAccesoMes({ editable: !!res.tiene_permiso, esMesTrabajo: !!res.es_mes_trabajo, solicitud: res.solicitud || null });
  }
  setSelectedMes(nuevoMes);
  setSelectedAnio(nuevoAnio);
};

const moverPeriodo = (paso) => {
  if (periodoAnual) {
    // C-020: en la Declaración anual se mueve el ejercicio
    const nuevo = ejercicio + paso;
    if (nuevo >= EJERCICIO_MINIMO && nuevo <= ejercicioMaximo) setEjercicio(nuevo);
    return;
  }
  const total = anioBarra * 12 + mesBarra + paso;
  cambiarPeriodo(total % 12, Math.floor(total / 12));
};

const solicitarPermisoMes = async (e) => {
  e.preventDefault();
  if (!motivoPermiso.trim()) {
    setErrorMotivoPermiso('Escribe qué necesitas corregir, para que quien apruebe sepa qué está aprobando.');
    return;
  }
  try {
    await fetchAPI('/permisos/solicitar', {
      method: 'POST',
      body: JSON.stringify({
        mes: mesParaPermiso,
        anio: anioParaPermiso,
        motivo: motivoPermiso.trim()
      })
    });
    avisar({ texto: 'Enviamos tu solicitud a Pedro y Agustín. Te avisamos aquí cuando respondan.' });
    setShowSolicitarPermiso(false);
    setMotivoPermiso('');
    cargarAccesoMes();
  } catch(err) {
    avisarError('enviar la solicitud de acceso', err);
  }
};

// Respuestas a las solicitudes del contador: le llegan una sola vez (C-028)
const revisarAvisosPermiso = async () => {
  try {
    const avisos = await fetchAPI('/permisos/mis-avisos');
    if (!Array.isArray(avisos) || avisos.length === 0) return;
    for (const a of avisos) {
      const mes = nombreMesPermiso(a.mes, a.anio);
      if (a.estado === 'aprobado') {
        avisar({ texto: `${a.respondio} aprobó tu acceso a ${mes}. Puedes editar hasta ${fechaHora(a.fecha_expiracion)}.` });
      } else if (a.estado === 'rechazado') {
        avisar({ tipo: 'info', texto: `${a.respondio} no aprobó tu acceso a ${mes}.${a.nota ? ` "${a.nota}"` : ''}` });
      } else if (a.estado === 'quitado') {
        avisar({ tipo: 'info', texto: `${a.quitado_por || 'Quien administra'} quitó tu acceso a ${mes}. ${mes.replace(/^./, l => l.toUpperCase())} vuelve a solo lectura.` });
      }
    }
    cargarAccesoMes();
  } catch (err) {
    console.error('Error al revisar avisos de permisos:', err);
  }
};

const cargarSolicitudesRespondidas = async () => {
  try {
    const res = await fetchAPI('/permisos/respondidas');
    setSolicitudesRespondidas(Array.isArray(res) ? res : []);
  } catch (err) { console.error('Error al cargar solicitudes respondidas:', err); }
};

const abrirSolicitudesPermiso = () => {
  setShowPermisosPanel(true);
  setRechazandoSolicitud(null);
  cargarSolicitudesPendientes();
  cargarSolicitudesRespondidas();
};

const quitarAccesoPermiso = async (sol) => {
  const ok = await confirmar({
    titulo: `¿Quitar el acceso de ${sol.contador_nombre} a ${nombreMesPermiso(sol.mes, sol.anio)}?`,
    cuerpo: 'El acceso termina en este momento y el mes vuelve a solo lectura para el contador. Le avisamos.',
    accion: 'Quitar acceso',
  });
  if (!ok) return;
  try {
    await fetchAPI(`/permisos/${sol.id}/quitar`, { method: 'POST' });
    avisar({ texto: `Quitaste el acceso de ${sol.contador_nombre} a ${nombreMesPermiso(sol.mes, sol.anio)}.` });
    cargarSolicitudesRespondidas();
  } catch (err) { avisarError('quitar el acceso', err); cargarSolicitudesRespondidas(); }
};

const verCambiosConAcceso = (sol) => {
  setShowPermisosPanel(false);
  cargarHistorial({
    usuario_id: sol.contador_id,
    mes: sol.mes + 1,
    anio: sol.anio,
    titulo: `${nombreMesPermiso(sol.mes, sol.anio)} · acceso de ${sol.contador_nombre}`,
  });
};

// C-037: pasos que quedaron "Por revisar" desde la última consulta. Un aviso por paso; si son
// varios, uno solo agrupado. Si el número del mes no cuadra con la tabla, alguien más cambió algo.
const revisarAvisosRevision = async () => {
  try {
    const res = await fetchAPI(`/revision/avisos?mes=${selectedMes + 1}&anio=${selectedAnio}`);
    const avisos = Array.isArray(res?.avisos) ? res.avisos : [];
    if (avisos.length === 1) {
      const a = avisos[0];
      avisar({
        tipo: 'info',
        fijo: true,
        texto: `${a.quien || 'Alguien'} dejó para revisión ${nombreVisibleProceso(a.proceso_nombre)} de ${a.cliente_nombre}.`,
        accion: { texto: 'Ver', alHacer: () => verPorRevisar(a) },
      });
    } else if (avisos.length > 1) {
      const nombres = [...new Set(avisos.map(a => a.cliente_nombre))];
      const mismoMes = avisos.every(a => a.mes === avisos[0].mes && a.anio === avisos[0].anio);
      avisar({
        tipo: 'info',
        fijo: true,
        texto: `${avisos.length} pasos nuevos por revisar: ${nombres.slice(0, 3).join(', ')}${nombres.length > 3 ? ` y ${nombres.length - 3} más` : ''}.`,
        accion: { texto: 'Ver', alHacer: () => verPorRevisar(mismoMes ? avisos[0] : null) },
      });
    }
    const enTabla = clientes.reduce((n, c) => n + pasosPorRevisar(c, procesos).length, 0);
    if (avisos.length > 0 || (typeof res?.pendientes === 'number' && res.pendientes !== enTabla)) cargarDatos();
  } catch (err) {
    console.error('Error al revisar avisos de revisión:', err);
  }
};
const revisarAvisosRevisionRef = useRef(revisarAvisosRevision);
revisarAvisosRevisionRef.current = revisarAvisosRevision;

useEffect(() => {
  if (!marcaRevisado(user)) return;
  const revisar = () => revisarAvisosRevisionRef.current();
  revisar();
  const interval = setInterval(revisar, 30000);
  return () => clearInterval(interval);
}, [user]);

// "Ver" del aviso: Clientes con "Por revisar" activo, en el mes del paso.
const verPorRevisar = (aviso) => {
  setShowProcesosDetalle(false);
  navegarA('/');
  setViewMode('mensual');
  limpiarFiltros();
  setFiltroPorRevisar(true);
  if (aviso && (aviso.mes - 1 !== selectedMes || aviso.anio !== selectedAnio)) {
    setSelectedMes(aviso.mes - 1);
    setSelectedAnio(aviso.anio);
  }
};

const cargarSolicitudesPendientes = async () => {
  try {
    const solicitudes = await fetchAPI('/permisos/pendientes');
    setSolicitudesPendientes(solicitudes);
  } catch(err) {
    console.error('Error al cargar solicitudes:', err);
  }
};

const responderSolicitud = async (sol, estado, nota = '') => {
  try {
    const res = await fetchAPI(`/permisos/${sol.id}`, {
      method: 'PUT',
      body: JSON.stringify({ estado, nota })
    });
    const mes = nombreMesPermiso(sol.mes, sol.anio);
    const deshacer = async () => {
      try {
        await fetchAPI(`/permisos/${sol.id}/deshacer`, { method: 'POST' });
        cargarSolicitudesPendientes();
        cargarSolicitudesRespondidas();
      } catch (err) { avisarError('deshacer la respuesta', err); }
    };
    if (estado === 'aprobado') {
      avisar({ texto: `Aprobaste el acceso de ${sol.contador_nombre} a ${mes} hasta ${fechaHora(res.fecha_expiracion)}. Le avisamos.`, deshacer });
    } else {
      avisar({ texto: `Rechazaste la solicitud de ${sol.contador_nombre}. Le avisamos.`, deshacer });
    }
    setRechazandoSolicitud(null);
  } catch(err) {
    avisarError('responder la solicitud', err);
  }
  cargarSolicitudesPendientes();
  cargarSolicitudesRespondidas();
};

const responderMensaje = (mensaje) => {
  setRespondiendo(mensaje);
  
  setNewMessage({
    ...newMessage,
    mensaje: '',
    mensaje_padre_id: mensaje.id
  });
 
  setTimeout(() => {
    document.querySelector('.chat-input-form textarea')?.focus();
  }, 100);
};

const cancelarRespuesta = () => {
  setRespondiendo(null);

  setNewMessage({ 
    para_usuario_id: conversacionActiva, 
    mensaje: '', 
    cliente_id: '', 
    mensaje_padre_id: null 
  });
};

  // C-026: lo llama FormularioCliente con los datos ya validados; si falla, el formulario sigue abierto.
  const crearCliente = async (datos) => {
    await fetchAPI('/clientes', {
      method: 'POST',
      body: JSON.stringify(datos),
    });
    setNewClient(CLIENTE_NUEVO);
    setShowNewClient(false);
    cargarDatos();
    avisar({ texto: `Cliente "${datos.nombre}" creado.` });
  };

  const abrirEditarCliente = (cliente) => {
    setEditingClient({
      id: cliente.id,
      nombre: cliente.nombre,
      tipo: cliente.tipo,
      contador_id: cliente.contador_id || '',
      email: cliente.email || '',
      telefono: cliente.telefono || '',
      regimen: cliente.regimen || 'resico',
      industria: cliente.industria || '',
      tiene_imss: cliente.tiene_imss || false,
      tiene_contabilidad_digital: cliente.tiene_contabilidad_digital || false,
      tiene_180_mensual: cliente.tiene_180_mensual || false,
      tiene_180_trimestral: cliente.tiene_180_trimestral || false,
      tiene_hospedaje: cliente.tiene_hospedaje || false,
      solo_cobranza: cliente.solo_cobranza || false,
      otros_ingresos: cliente.otros_ingresos || false,
      revision_pedro: normalizarRevision(cliente.revision_pedro),
      activo: cliente.activo !== false,
      desactivado_el: cliente.desactivado_el || null,
    });
    setShowEditClient(true);
  };

  const actualizarCliente = async (datos) => {
    await fetchAPI(`/clientes/${editingClient.id}`, {
      method: 'PUT',
      // Activo no se cambia aquí (eso es "Desactivar cliente…"): se manda el que tiene.
      body: JSON.stringify({ ...datos, id: editingClient.id, activo: editingClient.activo }),
    });
    setEditingClient(null);
    setShowEditClient(false);
    cargarDatos();
    fetchAPI('/clientes?cobranza=1').then(data => {
      if (data?.clientes) setCobranzaTodosClientes(data.clientes);
    }).catch(() => {});
    avisar({ texto: 'Cambios guardados.' });
  };


  const navegarA = (ruta) => {
    if (window.location.pathname !== ruta) {
      window.history.pushState({}, '', ruta);
    }
    setCurrentRoute(ruta);
  };

  const pedirConfirmacionPass = (callback) => {
    setConfirmPassValue('');
    setConfirmPassError('');
    setConfirmPassCallback(() => callback);
    setShowConfirmPass(true);
  };

  const ejecutarConConfirmacion = async () => {
    try {
      const data = await fetchAPI('/auth/verify-password', {
        method: 'POST',
        body: JSON.stringify({ password: confirmPassValue }),
      });
      if (data?.valid) {
        setShowConfirmPass(false);
        confirmPassCallback && confirmPassCallback();
      } else {
        setConfirmPassError('Contraseña incorrecta');
      }
    } catch {
      setConfirmPassError('Error al verificar contraseña');
    }
  };

  const enviarCobranzaMasiva = async () => {
    // Resumen y resultado del envío de links (C-010)
    const conMonto = cobranzaData.filter(c => c.activo !== false && Number(c.factura) > 0);
    const conFactura = conMonto.filter(c => correosValidos(c.email).length > 0);
    const sinCorreo = conMonto
      .filter(c => correosValidos(c.email).length === 0)
      .map(c => ({ id: c.id, nombre: c.nombre, motivo: c.email ? 'correo no válido' : 'sin correo' }));
    if (conFactura.length === 0) {
      avisar({ tipo: 'info', texto: 'No hay clientes con correo válido y factura mensual configurada.' });
      return;
    }
    const hoy = new Date();
    const mesCobro = hoy.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }).replace(/^./, l => l.toUpperCase()).replace(' de ', ' ');
    await confirmar({
      nivel: 3,
      titulo: `¿Enviar los links de pago de ${mesCobro}?`,
      cuerpo: (
        <ResumenEnvio
          mes={mesCobro}
          etiquetaMes="Mes que se cobra"
          destinatarios={`${plural(conFactura.length, 'cliente', 'clientes')} con factura mensual, por correo`}
          sinContacto={sinCorreo}
          nota="Cada cliente recibe un correo con su monto y su link de pago (válido 72 horas). Una vez enviado no se puede deshacer."
        />
      ),
      accion: `Enviar a ${plural(conFactura.length, 'cliente', 'clientes')}`,
      accionEnCurso: 'Enviando links…',
      ejecutar: async () => {
        setEnviandoMasivo(true);
        try {
          const data = await fetchAPI('/cobranza/envio-masivo', {
            method: 'POST',
            body: JSON.stringify({}),
          });
          // Recargar saldos desde cobros_mensuales después del envío masivo
          const saldosData = await fetchAPI('/cobranza/saldos-cobros');
          if (saldosData?.saldos) setCobranzaSaldos(saldosData.saldos);
          const fallidos = data.fallidos || [];
          if (fallidos.length === 0) {
            avisar({ texto: `Links de pago enviados a ${plural(data.enviados || 0, 'cliente', 'clientes')}.` });
            return null;
          }
          return { resultado: <ResultadoEnvio enviados={data.enviados || 0} unidad={['link', 'links']} fallidos={fallidos} /> };
        } finally {
          setEnviandoMasivo(false);
        }
      },
    });
  };

  // Nivel 1 (C-009): se revierte directo y el aviso ofrece Deshacer con la fecha de pago original.
  // ── Funciones Viento ────────────────────────────────────────────────────────
  const recargarViento = async () => {
    const [deptos, saldos, resumen] = await Promise.all([
      fetchAPI('/viento/departamentos').catch(() => null),
      fetchAPI('/viento/saldos').catch(() => null),
      fetchAPI(`/viento/resumen-mensual?anio=${selectedAnioViento}`).catch(() => null),
    ]);
    if (Array.isArray(deptos)) setVientoDepartamentos(deptos);
    if (saldos?.saldos) setVientoSaldos(saldos.saldos);
    if (Array.isArray(resumen)) setVientoResumenMensual(resumen);
  };

  const toggleVientoHistorial = async (deptoId) => {
    if (expandedViento.has(deptoId)) {
      setExpandedViento(prev => { const s = new Set(prev); s.delete(deptoId); return s; });
      return;
    }
    setExpandedViento(prev => new Set([...prev, deptoId]));
    if (vientoHistoriales[deptoId]) return;
    setCargandoVientoHistorial(prev => new Set([...prev, deptoId]));
    try {
      const data = await fetchAPI(`/viento/historial/${deptoId}`);
      if (data?.cobros) setVientoHistoriales(prev => ({ ...prev, [deptoId]: data.cobros }));
    } catch { } finally {
      setCargandoVientoHistorial(prev => { const s = new Set(prev); s.delete(deptoId); return s; });
    }
  };

  const crearDepto = async (e) => {
    e.preventDefault();
    try {
      await fetchAPI('/viento/departamentos', { method: 'POST', body: JSON.stringify({ ...newDepto, renta_mensual: Number(newDepto.renta_mensual) || 0 }) });
      setNewDepto({ codigo: '', nombre: '', email: '', telefono: '', renta_mensual: '', notas: '', activo: true });
      setShowNuevoDepto(false);
      await recargarViento();
    } catch (e) { avisarError('crear el departamento', e); }
  };

  const actualizarDepto = async (e) => {
    e.preventDefault();
    try {
      await fetchAPI(`/viento/departamentos/${editingDepto.id}`, { method: 'PUT', body: JSON.stringify({ ...editingDepto, renta_mensual: Number(editingDepto.renta_mensual) || 0 }) });
      setEditingDepto(null);
      setShowEditDepto(false);
      await recargarViento();
    } catch (e) { avisarError('actualizar el departamento', e); }
  };

  const abrirGenerarCobroViento = (depto) => {
    setDeptoGenerarCobro(depto);
    setFormCobroViento({ mes: new Date().getMonth() + 1, anio: new Date().getFullYear(), monto: depto.renta_mensual || '' });
    setShowGenerarCobroViento(true);
  };

  const confirmarGenerarCobroViento = async () => {
    if (!deptoGenerarCobro) return;
    const monto = Number(formCobroViento.monto);
    limpiarErrores('viento-monto');
    if (!monto || monto <= 0) return marcarError('viento-monto', 'Escribe un monto mayor a cero.');
    try {
      const res = await fetchAPI('/viento/cobros/generar', { method: 'POST', body: JSON.stringify({ departamento_id: deptoGenerarCobro.id, mes: Number(formCobroViento.mes), anio: Number(formCobroViento.anio), monto }) });
      if (!res?.success) return avisarError('registrar el cobro', { status: 400, message: res?.error });
      setShowGenerarCobroViento(false);
      const hist = await fetchAPI(`/viento/historial/${deptoGenerarCobro.id}`);
      if (hist?.cobros) {
        setVientoHistoriales(prev => ({ ...prev, [deptoGenerarCobro.id]: hist.cobros }));
        setVientoSaldos(prev => ({ ...prev, [deptoGenerarCobro.id]: hist.saldo }));
      }
      fetchAPI(`/viento/resumen-mensual?anio=${selectedAnioViento}`).then(d => { if (Array.isArray(d)) setVientoResumenMensual(d); }).catch(() => {});
    } catch (e) { avisarError('registrar el cobro', e); }
  };

  const marcarVientoPagado = async (cobro, deptoId) => {
    const ok = await confirmar({
      titulo: `¿Marcar ${MESES_NOMBRE[cobro.mes - 1]} ${cobro.anio} como pagado?`,
      cuerpo: 'Se registra con la fecha de hoy.',
      accion: 'Marcar pagado',
    });
    if (!ok) return;
    try {
      await fetchAPI(`/viento/cobro/${cobro.id}/pagar`, { method: 'PATCH', body: JSON.stringify({}) });
      const data = await fetchAPI(`/viento/historial/${deptoId}`);
      if (data?.cobros) { setVientoHistoriales(prev => ({ ...prev, [deptoId]: data.cobros })); setVientoSaldos(prev => ({ ...prev, [deptoId]: data.saldo })); }
      fetchAPI(`/viento/resumen-mensual?anio=${selectedAnioViento}`).then(d => { if (Array.isArray(d)) setVientoResumenMensual(d); }).catch(() => {});
    } catch (e) { avisarError('marcar el cobro como pagado', e); }
  };

  const revertirVientoPagado = async (cobro, deptoId) => {
    const ok = await confirmar({
      titulo: `¿Regresar ${MESES_NOMBRE[cobro.mes - 1]} ${cobro.anio} a pendiente?`,
      cuerpo: 'Se borra la fecha de pago registrada.',
      accion: 'Regresar a pendiente',
    });
    if (!ok) return;
    try {
      await fetchAPI(`/viento/cobro/${cobro.id}/revertir`, { method: 'PATCH' });
      const data = await fetchAPI(`/viento/historial/${deptoId}`);
      if (data?.cobros) { setVientoHistoriales(prev => ({ ...prev, [deptoId]: data.cobros })); setVientoSaldos(prev => ({ ...prev, [deptoId]: data.saldo })); }
      fetchAPI(`/viento/resumen-mensual?anio=${selectedAnioViento}`).then(d => { if (Array.isArray(d)) setVientoResumenMensual(d); }).catch(() => {});
    } catch (e) { avisarError('regresar el cobro a pendiente', e); }
  };

  const guardarFechaViento = async () => {
    if (!editandoVientoFecha) return;
    const { id, fecha, cobro, deptoId, deptoNombre } = editandoVientoFecha;
    limpiarErrores('viento-fecha');
    if (!fecha) return marcarError('viento-fecha', 'Elige la fecha de pago.');
    const anterior = cobro.fecha_pago ? new Date(cobro.fecha_pago).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—';
    const [ay, my, dy] = fecha.split('-');
    const ok = await confirmar({
      titulo: '¿Cambiar la fecha de pago?',
      cuerpo: (
        <>
          <p>Depto {deptoNombre} · {MESES_NOMBRE[cobro.mes - 1]} {cobro.anio} · ${Number(cobro.monto).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          <p className="dialogo-cambio"><span>{anterior}</span> <ArrowRight size={14} role="img" aria-label="cambia a" /> <strong>{dy}/{my}/{ay}</strong></p>
        </>
      ),
      accion: 'Cambiar fecha',
    });
    if (!ok) return;
    try {
      await fetchAPI(`/viento/cobro/${id}/fecha-pago`, { method: 'PATCH', body: JSON.stringify({ fecha_pago: fecha }) });
      setEditandoVientoFecha(null);
      const data = await fetchAPI(`/viento/historial/${deptoId}`);
      if (data?.cobros) setVientoHistoriales(prev => ({ ...prev, [deptoId]: data.cobros }));
    } catch (e) { avisarError('cambiar la fecha de pago', e); }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const enviarConArchivos = async () => {
    if (!cobroAdjuntos) return;
    limpiarErrores('adjuntos');
    if (!archivoPDF && !archivoXML) return marcarError('adjuntos', 'Adjunta al menos un archivo: la factura en PDF o el XML.');
    try {
      const formData = new FormData();
      if (archivoPDF) formData.append('factura_pdf', archivoPDF);
      if (archivoXML) formData.append('factura_xml', archivoXML);
      const res = await fetch(`${API_URL}/pagos/enviar-cobro-archivos/${cobroAdjuntos.cobro.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        avisar({ texto: `Correo enviado a ${data.cliente} con ${plural(data.adjuntos, 'archivo adjunto', 'archivos adjuntos')}.` });
        setShowAdjuntosModal(false);
        setArchivoPDF(null);
        setArchivoXML(null);
        setCobroAdjuntos(null);
      } else {
        avisarError('enviar el correo con la factura', { status: 400, message: data?.error });
      }
    } catch (e) { avisarError('enviar el correo con la factura', e); }
  };

  // Copiar link: el mismo link de OpenPay que "Enviar link", para mandarlo a mano (WhatsApp) a quien no tiene correo.
  const copiarLinkCobro = async (cobroId) => {
    try {
      const data = await fetchAPI(`/pagos/generar-link-cobro/${cobroId}`, { method: 'POST', body: JSON.stringify({}) });
      if (!data?.success) return avisarError('generar el link de pago', { status: 400, message: data?.error });
      const copiar = () => navigator.clipboard.writeText(data.link);
      const monto = Number(data.monto).toLocaleString('es-MX', { minimumFractionDigits: 2 });
      try {
        await copiar();
        avisar({ texto: `Link copiado: ${data.concepto}, $${monto} MXN. Pégalo en WhatsApp o donde lo vayas a mandar.`, accion: { texto: 'Copiar otra vez', alHacer: copiar } });
      } catch {
        avisar({ tipo: 'info', texto: `El navegador no dejó copiarlo solo. Selecciona y copia el link: ${data.link}` });
      }
    } catch (e) { avisarError('generar el link de pago', e); }
  };

  const enviarLinkCobro = async (cobro, clienteId) => {
    try {
      const data = await fetchAPI(`/pagos/enviar-cobro/${cobro.id}`, { method: 'POST', body: JSON.stringify({}) });
      if (data.success) {
        avisar({ texto: `Link de pago enviado por correo a ${data.cliente}: ${data.concepto}, $${Number(data.monto).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN.` });
      } else {
        avisarError('enviar el link de pago', { status: 400, message: data?.error });
      }
    } catch (e) { avisarError('enviar el link de pago', e); }
  };

  const MESES_NOMBRE = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const siglas = (nombre) => {
    if (!nombre) return '';
    return nombre.trim().split(/\s+/).map(w => w[0]?.toUpperCase() || '').join('').slice(0, 3);
  };

  const getEstadoColor = (cliente_id, proceso_id) => {
    const cliente = clientes.find(c=>c.id===cliente_id);
    return cliente?.estados.find(e=>e.proceso_id===proceso_id)?.estado || 'rojo';
  };

  const getFechaActualizacion = (cliente_id, proceso_id) => {
    const cliente = clientes.find(c=>c.id===cliente_id);
    return cliente?.estados.find(e=>e.proceso_id===proceso_id)?.fecha_actualizacion || null;
  };

  // Cuándo y quién cambió el estado, para el tooltip del círculo (C-022).
  const cambioDeEstado = (cliente_id, proceso_id) => {
    const cliente = clientes.find(c=>c.id===cliente_id);
    const e = cliente?.estados.find(x=>x.proceso_id===proceso_id);
    return { fecha: e?.fecha_actualizacion || null, por: e?.usuario_nombre || null };
  };

  // C-018: el selector de la barra muestra el año solo en la vista Anual de Clientes; en lo demás, el mes.
  // La ficha del cliente trabaja con el mes (Este mes, mes por mes, obligaciones), aunque se abra desde la anual.
  const periodoAnual = currentRoute === '/' && viewMode === 'anual' && !showProcesosDetalle;
  // C-017: Cobranza y Viento usan su propio mes, que abre en el mes en curso; Clientes, el de trabajo.
  const periodoPropio = currentRoute === '/cobranza' || currentRoute === '/viento';
  const mesBarra = periodoPropio ? periodoCobranza.mes : selectedMes;
  const anioBarra = periodoPropio ? periodoCobranza.anio : selectedAnio;

  // C-018: Ajustes se abre desde el usuario (Mi perfil, Cambiar contraseña) o desde Administración
  // (Usuarios, Respaldo de archivos), cada uno solo con sus pestañas.
  const abrirAjustes = (grupo, pestana) => {
    setSettingsGrupo(grupo);
    setSettingsTab(pestana);
    setShowSettings(true);
    if (grupo === 'personal') setPerfilData({ nombre: user.nombre, email: user.email || '' });
    if (grupo === 'despacho') cargarUsuarios();
  };

  // ───── Estados visibles (C-003 / C-004, ver src/estados.js) ─────
  const fechasLimite = construirFechasLimite(procesos);
  const periodoSeleccionado = { mes: selectedMes + 1, anio: selectedAnio };

  const contextoEstado = (cliente, { cascada = true, estado } = {}) => {
    const hoy = new Date();
    return {
      cascada,
      estado,
      hoy,
      fechasLimite,
      periodo: periodoSeleccionado,
      pasoActual: !cascada ? null
        : pasoPorCliente.has(cliente?.id) ? pasoPorCliente.get(cliente.id)
        : calcularPasoActual(cliente, procesos, fechasLimite, periodoSeleccionado, hoy),
    };
  };

  // Opinión de cumplimiento: resultado (Positiva / Negativa) o Sin sacar, con menú (C-006)
  const renderCeldaOpinion = (cliente, p) => {
    const estado = getEstadoColor(cliente.id, p.id);
    const resultado = resultadoOpinion(estado);
    const visible = estadoVisible(cliente, p, contextoEstado(cliente));
    const fechaActualizacion = getFechaActualizacion(cliente.id, p.id);
    const clave = `${cliente.id}-${p.id}`;
    const abierto = menuOpinion === clave;
    const texto = resultado ? visible.nombre : 'Sin sacar';
    const ayuda = tooltipCirculo(
      { ...visible, tooltip: resultado ? `Opinión ${texto.toLowerCase()}` : 'Sin sacar: clic para registrar la opinión' },
      cambioDeEstado(cliente.id, p.id)
    );
    // Siempre etiqueta, nunca círculo: el único círculo azul de la fila es el paso actual (C-014).
    const elegido = (opcion) =>
      opcion.estado === 'verde' ? resultado === 'positiva'
        : opcion.estado === OPINION_NEGATIVA ? resultado === 'negativa'
        : !resultado;
    return (
      <td key={p.id} className="cell-estado col-opinion">
        <div className="estado-container-table">
          <span className="opinion-wrap estado-circulo-wrap">
            <button
              type="button"
              className={`opinion-pill ${resultado || 'sin-sacar'}`}
              data-tooltip={ayuda}
              aria-label={`Opinión de cumplimiento: ${texto}. Cambiar`}
              aria-haspopup="menu"
              aria-expanded={abierto}
              onClick={() => setMenuOpinion(abierto ? null : clave)}
            >
              {texto}
            </button>
            {abierto && (
              <div className="opinion-menu" role="menu" aria-label="Resultado de la opinión">
                {OPCIONES_OPINION.map(opcion => (
                  <button
                    key={opcion.estado}
                    type="button"
                    role="menuitemradio"
                    aria-checked={elegido(opcion)}
                    className={`opinion-opcion ${opcion.estado}`}
                    onClick={() => {
                      setMenuOpinion(null);
                      if (!elegido(opcion)) guardarEstado(cliente.id, p.id, opcion.estado);
                    }}
                  >
                    {opcion.texto}
                  </button>
                ))}
              </div>
            )}
          </span>
          {fechaActualizacion && (
            <span className="estado-fecha-table">
              Actualizado el {fechaCorta(fechaActualizacion)}
            </span>
          )}
        </div>
      </td>
    );
  };

  const renderCeldaEstado = (cliente, p, { cascada = true } = {}) => {
    if (cascada && esOpinionCumplimiento(p)) return renderCeldaOpinion(cliente, p);
    const visible = estadoVisible(cliente, p, contextoEstado(cliente, { cascada }));
    const fechaActualizacion = getFechaActualizacion(cliente.id, p.id);
    const ayuda = tooltipCirculo(visible, cambioDeEstado(cliente.id, p.id));
    return (
      <td key={p.id} className={`cell-estado${cascada && p.id === procesoResaltadoId ? ' col-resaltada' : ''}`}>
        <div className="estado-container-table">
          <span className="estado-circulo-wrap">
            <button
              onClick={()=>cambiarEstado(cliente.id,p.id)}
              className={`estado-circle ${visible.clave}${visible.atrasado ? ' atrasado' : ''}`}
              data-tooltip={ayuda}
              aria-label={`${nombreVisibleProceso(p)}: ${ayuda}`}
            >
              <IconoEstado clave={visible.clave} />
            </button>
            <SelloRevision tipo={visible.sello} />
            {visible.atrasado && (
              <span className="atraso-badge" aria-hidden="true">+{visible.dias}d</span>
            )}
            {visible.porVencer && (
              <span className="vence-badge" aria-hidden="true"><Hourglass size={10} aria-hidden="true" />{visible.diasRestantes}d</span>
            )}
          </span>
          {fechaActualizacion && (
            <span className="estado-fecha-table">
              Actualizado el {fechaCorta(fechaActualizacion)}
            </span>
          )}
          {visible.sello === 'complementaria' && (
            <span className="estado-fecha-table complementaria">Complementaria pendiente</span>
          )}
          {visible.esperaRevision && (
            <span className="estado-fecha-table espera-revision">Espera revisión</span>
          )}
        </div>
      </td>
    );
  };

  // ───── Filtros de la pantalla principal (C-012, ver diseno/filtros.md) ─────
  // Cada cliente está detenido en un solo paso (su paso actual); el embudo cuenta cuántos hay en cada uno.
  const hoyFiltros = new Date();
  const pasosDelEmbudo = pasosCascada(procesos);
  const pasoPorCliente = new Map(
    clientes.map(c => [c.id, calcularPasoActual(c, procesos, fechasLimite, periodoSeleccionado, hoyFiltros)])
  );
  const slugDelCliente = (cliente) => {
    const pasoActual = pasoPorCliente.get(cliente.id);
    if (!pasoActual) return PASO_TERMINADOS;
    return pasosDelEmbudo.find(p => p.proceso.id === pasoActual.proceso.id)?.slug;
  };
  const diasAtrasoDe = (cliente) => pasoPorCliente.get(cliente.id)?.diasAtraso || 0;
  // C-024: días que faltan si el paso actual vence en 0-3 días; null si no está por vencer.
  const diasPorVencerDe = (cliente) => {
    const paso = pasoPorCliente.get(cliente.id);
    const faltan = paso && !paso.diasAtraso ? paso.diasParaVencer : null;
    return faltan !== null && faltan !== undefined && faltan <= DIAS_POR_VENCER ? faltan : null;
  };
  // C-021: obligaciones adicionales atrasadas por cliente ("IMSS atrasado ›" bajo el nombre)
  const obligacionesAtrasadas = atrasosPorCliente(obligacionesMes, periodoSeleccionado, hoyFiltros);
  const verObligacionesAtrasadas = () => {
    setObligAtrasadas(true);
    setViewMode('especiales');
  };

  // Contador, régimen, industria y búsqueda: base de los conteos del embudo.
  const textoBusqueda = normalizarNombre(busqueda.trim());
  const clientesBase = clientes.filter(c =>
    (!filtroContador.length || filtroContador.includes(String(c.contador_id))) &&
    (!filtroRegimen.length || filtroRegimen.includes(c.regimen)) &&
    (!filtroIndustria.length || filtroIndustria.includes(c.industria)) &&
    (!textoBusqueda || normalizarNombre(c.nombre).includes(textoBusqueda) || normalizarNombre(c.rfc).includes(textoBusqueda))
  );
  const embudo = [...pasosDelEmbudo, { slug: PASO_TERMINADOS, numero: null, corto: 'Terminados' }].map(paso => {
    const detenidos = clientesBase.filter(c => slugDelCliente(c) === paso.slug);
    return { ...paso, detenidos: detenidos.length, atrasados: detenidos.filter(c => diasAtrasoDe(c) > 0).length };
  });
  const procesoOpinion = procesos.find(esOpinionCumplimiento);
  const tieneOpinionNegativa = (c) =>
    !!procesoOpinion && resultadoOpinion(getEstadoColor(c.id, procesoOpinion.id)) === 'negativa';
  const coincidePaso = (c) => !filtroPaso || slugDelCliente(c) === filtroPaso;
  const totalAtrasados = clientesBase.filter(c => coincidePaso(c) && diasAtrasoDe(c) > 0).length;
  const totalPorVencer = clientesBase.filter(c => coincidePaso(c) && diasPorVencerDe(c) !== null).length;
  // Atrasados y Por vencer se pueden prender juntos: queda lo que cumpla cualquiera de los dos.
  const pasaUrgencia = (c) =>
    (!filtroAtrasados && !filtroPorVencer) ||
    (filtroAtrasados && diasAtrasoDe(c) > 0) ||
    (filtroPorVencer && diasPorVencerDe(c) !== null);
  const totalOpinionNegativa = clientesBase.filter(c => coincidePaso(c) && tieneOpinionNegativa(c)).length;
  // "Por revisar" (C-014): solo Dueño y Supervisor. Cuenta sellos ámbar, no clientes, de los
  // clientes que quedan con los demás filtros: al activarlo se ven exactamente esos sellos.
  const muestraPorRevisar = esAdministrador(user);
  const porRevisarDe = (c) => pasosPorRevisar(c, procesos).length;
  const pasaOtrosFiltros = (c) =>
    coincidePaso(c) &&
    pasaUrgencia(c) &&
    (!filtroOpinionNegativa || tieneOpinionNegativa(c));
  const totalPorRevisar = clientesBase.filter(pasaOtrosFiltros).reduce((n, c) => n + porRevisarDe(c), 0);
  // C-037: el número de la pestaña Clientes, solo para quien marca Revisado; sin filtros
  const pasosPorRevisarMes = marcaRevisado(user) ? clientes.reduce((n, c) => n + porRevisarDe(c), 0) : 0;
  // C-034: clientes con la declaración presentada en $0 de forma provisional (falta la complementaria)
  const procesoDeclaracion = procesos.find(esDeclaracionPresentada);
  const tieneComplementaria = (c) => !!procesoDeclaracion && complementariaPendiente(c, procesoDeclaracion);
  const totalComplementarias = clientesBase.filter(c => pasaOtrosFiltros(c) && tieneComplementaria(c)).length;
  const clientesFiltrados = clientesBase.filter(c =>
    pasaOtrosFiltros(c) &&
    (!(muestraPorRevisar && filtroPorRevisar) || porRevisarDe(c) > 0) &&
    (!filtroComplementarias || tieneComplementaria(c))
  );
  // Lo más atrasado primero; luego lo que vence antes.
  if (filtroAtrasados || filtroPorVencer) {
    const urgencia = (c) => (diasAtrasoDe(c) > 0 ? 1000 + diasAtrasoDe(c) : diasPorVencerDe(c) !== null ? 100 - diasPorVencerDe(c) : 0);
    clientesFiltrados.sort((a, b) => urgencia(b) - urgencia(a));
  }
  const pasoFiltrado = pasosDelEmbudo.find(p => p.slug === filtroPaso) || (filtroPaso === PASO_TERMINADOS ? { corto: 'Terminados' } : null);
  const procesoResaltadoId = pasoFiltrado?.proceso?.id;

  // C-027: inactivos que coinciden con la búsqueda
  const inactivosQueCoinciden = clientesInactivos.filter(c =>
    !textoBusqueda || normalizarNombre(c.nombre).includes(textoBusqueda));

  const contadoresConClientes = [...new Map(
    clientes.filter(c => c.contador_id && c.contador_nombre)
      .map(c => [c.contador_id, { id: c.contador_id, nombre: c.contador_nombre }])
  ).values()].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

  const elegirPaso = (slug, soloAtrasados) => {
    if (soloAtrasados) {
      const yaActivo = filtroPaso === slug && filtroAtrasados;
      setFiltroPaso(yaActivo ? null : slug);
      setFiltroAtrasados(!yaActivo);
    } else {
      setFiltroPaso(filtroPaso === slug ? null : slug);
    }
  };

  const limpiarFiltros = () => {
    setFiltroPaso(null);
    setFiltroAtrasados(false);
    setFiltroOpinionNegativa(false);
    setFiltroPorRevisar(false);
    setFiltroComplementarias(false);
    setFiltroPorVencer(false);
    setFiltroContador([]);
    setFiltroRegimen([]);
    setFiltroIndustria([]);
    setBusqueda('');
  };

  // C-025: cada opción del filtro dice cuántos clientes tiene en el mes; en Contador, también atrasados.
  const textoCuantosClientes = (n) => `${n} ${n === 1 ? 'cliente' : 'clientes'}`;
  const detalleContador = (id) => {
    const suyos = clientes.filter(c => String(c.contador_id) === String(id));
    const atrasados = suyos.filter(c => diasAtrasoDe(c) > 0).length;
    return `${textoCuantosClientes(suyos.length)}${atrasados ? ` · ${atrasados} ${atrasados === 1 ? 'atrasado' : 'atrasados'}` : ''}`;
  };
  const opcionesContador = contadoresConClientes.map(u => ({ valor: String(u.id), texto: u.nombre, corto: u.nombre.split(' ')[0], detalle: detalleContador(u.id) }));

  // C-025: el nombre de los contadores elegidos ("Mis clientes" para uno mismo)
  const nombreContador = (id) => (String(id) === String(user?.id) ? 'Mis clientes'
    : contadoresConClientes.find(u => String(u.id) === String(id))?.nombre || id);
  const filtrosActivos = [
    pasoFiltrado && { clave: 'paso', texto: `Detenidos en: ${pasoFiltrado.corto}` },
    filtroAtrasados && { clave: 'atrasados', texto: 'Atrasados' },
    filtroPorVencer && { clave: 'porvencer', texto: 'Por vencer' },
    filtroOpinionNegativa && { clave: 'opinion', texto: 'Opinión negativa' },
    muestraPorRevisar && filtroPorRevisar && { clave: 'revision', texto: 'Por revisar' },
    filtroComplementarias && { clave: 'complementarias', texto: 'Complementarias' },
    filtroContador.length > 0 && { clave: 'contador', texto: `Contador: ${filtroContador.map(nombreContador).join(', ')}` },
    filtroRegimen.length > 0 && { clave: 'regimen', texto: `Régimen: ${filtroRegimen.map(r => REGIMENES[r] || r).join(', ')}` },
    filtroIndustria.length > 0 && { clave: 'industria', texto: `Industria: ${filtroIndustria.map(r => INDUSTRIAS[r] || r).join(', ')}` },
    busqueda.trim() && { clave: 'busqueda', texto: `Búsqueda: "${busqueda.trim()}"` },
  ].filter(Boolean);

  // C-024: con el filtro de Contador en una sola persona, las filas no repiten su nombre.
  const mostrarContadorEnFila = filtroContador.length !== 1;
  const renderFilaCliente = (cliente, { mostrarTipo = false } = {}) => {
    const { terminados, total } = avancePasos(cliente, procesos);
    return (
    <tr key={cliente.id}>
      <td className="client-name">
        <CeldaCliente
          cliente={cliente}
          onAbrir={() => abrirProcesosDetalle(cliente.id)}
          onNotas={() => abrirProcesosDetalle(cliente.id, 'resumen')}
          onEditar={esAdministrador(user) ? () => abrirEditarCliente(cliente) : null}
          mostrarContador={mostrarContadorEnFila}
          avance={{ terminados, total, ayuda: `${terminados} de ${total} pasos terminados. La opinión no cuenta.` }}
        >
          {mostrarTipo && (
            <span className="tipo-persona" data-tooltip={cliente.tipo === 'moral' ? 'Persona moral' : 'Persona física'}>
              {cliente.tipo === 'moral' ? 'P.M.' : 'P.F.'}
            </span>
          )}
          {etiquetaRevision(cliente) && (
            <span className="etiqueta-revision">{etiquetaRevision(cliente)}</span>
          )}
          {(obligacionesAtrasadas.get(cliente.id) || []).map(nombre => (
            <button key={nombre} type="button" className="aviso-obligacion" onClick={verObligacionesAtrasadas}>
              {nombre} atrasado ›
            </button>
          ))}
        </CeldaCliente>
      </td>

      {procesos.map(p => renderCeldaEstado(cliente, p))}

      {renderCeldaDocumentos(cliente)}
    </tr>
    );
  };

  // C-015: columna Documentos, neutral: "5 archivos" o "Sin archivos", sin colores de alerta.
  const renderCeldaDocumentos = (cliente) => {
    const n = Number(cliente.archivos_count) || 0;
    return (
      <td className="cell-documentos">
        {/* C-024: abajo de 1280 px solo ícono y número (el texto lo esconde el CSS) */}
        <button
          type="button"
          className={`docs-btn${n ? '' : ' vacio'}`}
          aria-label={`Documentos de ${cliente.nombre} en ${meses[selectedMes].toLowerCase()}: ${textoArchivos(n).toLowerCase()}`}
          data-tooltip={`${n} ${n === 1 ? 'documento' : 'documentos'} de ${meses[selectedMes].toLowerCase()} · Abrir`}
          onClick={() => setDocsMes(cliente)}
        >
          <FileText size={14} aria-hidden="true" />
          <span className="docs-num">{n}</span>
          <span className="docs-texto">{textoArchivos(n)}</span>
        </button>
      </td>
    );
  };

  const renderFilaSinResultados = () => {
    // "Todo va a tiempo" solo es cierto cuando "Atrasados" es el único filtro.
    const soloAtrasados = filtroAtrasados && filtrosActivos.length === 1;
    const titulo = soloAtrasados ? 'Ningún cliente atrasado.'
      : filtrosActivos.length > 1 ? 'Ningún cliente cumple todos los filtros.'
      : 'Ningún cliente coincide con este filtro.';
    return (
    <tr>
      <td colSpan={procesos.length + 2} className="filtros-vacio">
        <b>{titulo}</b>
        {soloAtrasados && <span> Todo va a tiempo.</span>}
        <button type="button" className="filtros-limpiar" onClick={limpiarFiltros}>Limpiar filtros</button>
      </td>
    </tr>
  );
  };

  

  const mensajesNoLeidos = conversaciones.reduce((total, conv) => total + (conv.no_leidos || 0), 0);

 
// ───── Desactivar o eliminar cliente (C-008, ver diseno/dialogos.md secciones A y B) ─────

const cambiarActivoCliente = async (cliente_id) => {
  const data = await fetchAPI(`/clientes/${cliente_id}/toggle-activo`, { method: 'PUT' });
  cargarDatos();
  cargarInactivos();
  fetchAPI('/clientes?cobranza=1').then(d => {
    if (d?.clientes) setCobranzaTodosClientes(d.clientes);
  }).catch(() => {});
  return data;
};

// ───── Clientes inactivos: verlos y reactivarlos (C-027, ver diseno/referencias/propuesta-inactivos.html) ─────

const cargarInactivos = async () => {
  if (!esAdministrador(user)) return;
  try {
    const res = await fetchAPI('/clientes/inactivos');
    setClientesInactivos(Array.isArray(res) ? res : []);
  } catch (err) { console.error('Error al cargar clientes inactivos:', err); }
};

const reactivarCliente = async (cliente) => {
  const ok = await confirmar({
    nivel: 3,
    titulo: `¿Reactivar a ${cliente.nombre}?`,
    cuerpo: (
      <ul className="dialogo-lista">
        <li>Vuelve a la tabla mensual desde el mes que se está trabajando.</li>
        <li>Vuelve a recibir la solicitud de información y los recordatorios.</li>
        <li>Se le vuelven a generar cobros mensuales.</li>
        <li>Su contador sigue siendo el mismo (se cambia en Editar cliente).</li>
      </ul>
    ),
    accion: 'Reactivar cliente',
    accionEnCurso: 'Reactivando…',
    ejecutar: () => cambiarActivoCliente(cliente.id),
  });
  if (!ok) return;
  if (editingClient?.id === cliente.id) {
    setShowEditClient(false);
    setEditingClient(null);
  }
  avisar({
    texto: `${cliente.nombre} reactivado. Ya está en la tabla de ${nombreMes(selectedMes).toLowerCase()}.`,
    deshacer: () => cambiarActivoCliente(cliente.id)
      .catch(() => avisar({ tipo: 'error', texto: `No se pudo volver a desactivar a ${cliente.nombre}. Intenta de nuevo.` })),
  });
};

const desactivarCliente = async (cliente) => {
  const ok = await confirmar({
    titulo: `¿Desactivar a ${cliente.nombre}?`,
    cuerpo: (
      <ul className="dialogo-lista">
        <li>Deja de aparecer en la tabla mensual y en los envíos automáticos.</li>
        <li>No se le generan cobros nuevos.</li>
        <li>Su historial, archivos y cobros anteriores se conservan.</li>
      </ul>
    ),
    accion: 'Desactivar cliente',
    accionEnCurso: 'Desactivando…',
    ejecutar: () => cambiarActivoCliente(cliente.id),
  });
  if (!ok) return;
  setShowEditClient(false);
  setEditingClient(null);
  avisar({
    texto: `${cliente.nombre} se desactivó.`,
    deshacer: () => cambiarActivoCliente(cliente.id)
      .catch(() => avisar({ tipo: 'error', texto: `No se pudo reactivar a ${cliente.nombre}. Intenta de nuevo.` })),
  });
};

const eliminarCliente = async (cliente) => {
  const resultado = await confirmar({
    nivel: 3,
    peligro: true,
    titulo: `¿Eliminar a ${cliente.nombre}?`,
    alternativa: {
      titulo: '¿Ya no trabajan con este cliente? Desactívalo mejor.',
      descripcion: 'Deja de aparecer en la tabla mensual y no se le generan cobros, pero conserva su historial y archivos, y se puede reactivar.',
      texto: 'Desactivar en su lugar',
    },
    cuerpo: (
      <>
        <p><strong>Eliminar borra para siempre:</strong></p>
        <ul className="dialogo-lista">
          <li>Los archivos subidos</li>
          <li>El historial de estados de todos los meses</li>
          <li>Los mensajes y comentarios</li>
          <li>Sus cobros registrados</li>
        </ul>
        <p>Esta acción no se puede deshacer.</p>
      </>
    ),
    confirmarTexto: cliente.nombre,
    accion: 'Eliminar para siempre',
    accionEnCurso: 'Eliminando…',
    ejecutar: () => fetchAPI(`/clientes/${cliente.id}`, { method: 'DELETE' }),
  });

  if (resultado === 'alternativa') {
    desactivarCliente(cliente);
    return;
  }
  if (!resultado) return;

  setShowEditClient(false);
  setEditingClient(null);
  cargarDatos();
  if (docsMes?.id === cliente.id) setDocsMes(null);
  avisar({ texto: `${cliente.nombre} se eliminó.` });
};


  const cargarReporteEmails = async (dias = 30) => {
    if (!esAdministrador(user)) return;

    try {
      setCargandoReporteEmails(true);
      const res = await fetchAPI(`/notificaciones/reporte-emails?dias=${dias}`);
      setReporteEmails(res);
    } catch (err) {
      console.error('Error al cargar reporte de emails:', err);
      avisarError('cargar el reporte de correos', err);
    } finally {
      setCargandoReporteEmails(false);
    }
  };

  const enviarNotificacionesManuales = async (tipo = 'solicitud_inicial') => {
  
  await enviarNotificacionesManuales2(tipo);
};


const editarContacto = async (e) => {
  e.preventDefault();
  try {
    await fetchAPI(`/clientes/${selectedClientContact.id}/contacto`, {
      method: 'PUT',
      body: JSON.stringify(contactData),
    });
    avisar({ texto: 'Contacto actualizado.' });
    setShowEditContact(false);
    cargarDatos();
  } catch(err) {
    avisarError('actualizar el contacto', err);
  }
};


// Tarjeta de un proceso en la pestaña Procesos de la ficha.
const renderTarjetaProceso = (proceso) => {
  const estado = proceso.estado || 'rojo';
  const clienteTabla = clientes.find(c => c.id === clienteSeleccionado?.id);
  const enCascada = procesos.some(p => p.id === proceso.id);
  const visible = estadoVisible(clienteTabla, proceso, contextoEstado(clienteTabla, { cascada: enCascada, estado }));
  const ayuda = tooltipCirculo(visible, { fecha: proceso.fecha_actualizacion, por: proceso.actualizado_por_nombre });
  return (
    <div key={proceso.id} className="proceso-card">
      <div className="proceso-info">
        <h4>{nombreVisibleProceso(proceso)}</h4>
        {proceso.actualizado_por_nombre && (
          <div className="proceso-meta">
            <span><User size={14} aria-hidden="true" /> {proceso.actualizado_por_nombre}</span>
          </div>
        )}
      </div>

      <div className="estado-container">
        <span className="estado-circulo-wrap">
          <button
            onClick={() => cambiarEstado(clienteSeleccionado.id, proceso.id)}
            className={`estado-circle-large ${visible.clave}${visible.atrasado ? ' atrasado' : ''}`}
            data-tooltip={ayuda}
            aria-label={`${nombreVisibleProceso(proceso)}: ${ayuda}`}
          >
            <IconoEstado clave={visible.clave} size={24} />
          </button>
          <SelloRevision tipo={visible.sello} />
          {visible.atrasado && (
            <span className="atraso-badge" aria-hidden="true">+{visible.dias}d</span>
          )}
          {visible.porVencer && (
            <span className="vence-badge" aria-hidden="true"><Hourglass size={10} aria-hidden="true" />{visible.diasRestantes}d</span>
          )}
        </span>
        {proceso.fecha_actualizacion && (
          <span className="estado-fecha">
            Actualizado el {fechaCorta(proceso.fecha_actualizacion)}
          </span>
        )}
      </div>
    </div>
  );
};

// Los pasos de la tabla mensual del cliente (sin Opinión, que tiene su propio menú en la tabla).
const pasosDelMesEnFicha = () => {
  const c = clientes.find(x => x.id === clienteSeleccionado?.id);
  if (!c) return [];
  return procesos.filter(p => !esOpinionCumplimiento(p)).map(p => {
    const e = c.estados.find(x => x.proceso_id === p.id);
    return { ...p, estado: e?.estado, fecha_actualizacion: e?.fecha_actualizacion, actualizado_por_nombre: e?.usuario_nombre };
  });
};

// Abre la ficha del cliente en una pestaña: 'resumen', 'documentos' o 'procesos' (C-015).
// C-023: es una página con su dirección; `conHistorial: false` cuando ya se llegó por la URL.
const abrirProcesosDetalle = async (cliente_id, pestana = 'resumen', { conHistorial = true } = {}) => {
  if (conHistorial && idFichaEnUrl() !== cliente_id) {
    try { window.history.pushState({ ficha: cliente_id }, '', `/clientes/${cliente_id}${window.location.search}`); } catch (e) { /* sin URL la ficha igual abre */ }
  }
  console.log(' 1. Abriendo detalle para cliente:', cliente_id);

  // C-027: también se abre desde la lista de inactivos, que no están en la tabla
  const cliente = clientes.find(c => c.id === cliente_id)
    || cobranzaTodosClientes.find(c => c.id === cliente_id)
    || clientesInactivos.find(c => c.id === cliente_id);
  console.log(' 2. Cliente encontrado:', cliente);

  if (!cliente) {
    avisar({ tipo: 'error', texto: 'No se encontró ese cliente.' });
    return;
  }
  setClienteSeleccionado(cliente);
  setNotasAdmin(cliente?.notas_admin || '');

  try {
    const url = `/procesos-completos/${cliente_id}?mes=${selectedMes+1}&anio=${selectedAnio}`;
    console.log(' 3. URL:', url);

    const res = await fetchAPI(url);
    console.log(' 4. Respuesta completa:', res);
    console.log(' 5. Categorías:', res.categorias);
    console.log(' 6. Keys de categorías:', Object.keys(res.categorias || {}));

    // Verificar si hay datos

    // Verificar cada categoría
    Object.keys(res.categorias || {}).forEach(cat => {
      console.log(`📋 ${cat}:`, res.categorias[cat].procesos.length, 'procesos');
    });

    // Filtrar categorías: excluir Legal, Fiscal y Comercio
    const categoriasPermitidas = ['Vinícola', 'Comentarios']; // Comercio desactivado
    const categoriasFiltradas = {
      
      'General': {
        icono: '📋',
        procesos: [],
        es_info_cliente: true
      }
    };

    Object.keys(res.categorias || {}).forEach(categoria => {
      if (categoriasPermitidas.includes(categoria)) {
        categoriasFiltradas[categoria] = res.categorias[categoria];
      }
    });

    setProcesosDetalle(categoriasFiltradas);
    setPuedeAgregarComentarios(res.puede_agregar_comentarios || false);
    setPuedeEditarNotas(veClientes(user));
    cargarComentarios(cliente_id);

    setPestanaFicha(pestana);
    setShowProcesosDetalle(true);

    

    console.log(' Proceso completado');

  } catch(err) {
    console.error(' ERROR:', err);
    avisarError('abrir el detalle del cliente', err);
  }
};

// C-023: "Volver a la tabla" regresa a la misma vista, periodo, filtros y scroll (la tabla nunca se fue).
const cerrarFicha = () => {
  if (window.history.state && window.history.state.ficha) {
    window.history.back(); // el popstate cierra la ficha
    return;
  }
  try { window.history.replaceState(null, '', `${currentRoute === '/cobranza' ? '/cobranza' : '/'}${window.location.search}`); } catch (e) { /* sin URL */ }
  setShowProcesosDetalle(false);
};

// "Ver en la tabla →" del Resumen: la vista que corresponde, filtrada a este cliente.
const verClienteEnTabla = (vista, mes) => {
  const c = clienteSeleccionado;
  const destino = vista === 'cobranza' ? '/cobranza' : '/';
  try { window.history.replaceState(null, '', `${destino}${destino === '/' ? window.location.search : ''}`); } catch (e) { /* sin URL */ }
  setShowProcesosDetalle(false);
  setCurrentRoute(destino);
  if (vista === 'mensual') {
    setViewMode('mensual');
    limpiarFiltros();
    setBusqueda(c.nombre);
    if (mes && mes !== selectedMes + 1) cambiarPeriodo(mes - 1, selectedAnio);
  } else if (vista === 'obligaciones') {
    setObligAtrasadas(false);
    setObligBuscar(c.nombre);
    setViewMode('especiales');
  } else if (vista === 'anual') {
    setAnualCliente(c.id);
    setViewMode('anual');
  }
  window.scrollTo(0, 0);
};

// ========== FUNCIONES DE COMENTARIOS ==========

const cargarComentarios = async (cliente_id) => {
  try {
    const res = await fetchAPI(`/comentarios/${cliente_id}`);
    setComentariosCliente(res.comentarios || []);
  } catch(err) {
    console.error('Error al cargar comentarios:', err);
  }
};

const agregarComentario = async () => {
  limpiarErrores('comentario');
  if (!nuevoComentario.trim()) {
    marcarError('comentario', 'Escribe la nota antes de guardarla.');
    return;
  }

  if (nuevoComentario.length > 2000) {
    marcarError('comentario', 'La nota es demasiado larga: máximo 2000 caracteres.');
    return;
  }

  setGuardandoComentario(true);
  try {
    const res = await fetchAPI('/comentarios', {
      method: 'POST',
      body: JSON.stringify({
        cliente_id: clienteSeleccionado.id,
        comentario: nuevoComentario
      })
    });

    
    setComentariosCliente([res.comentario, ...comentariosCliente]);
    setNuevoComentario('');
    setClientes(prev => prev.map(c =>
      c.id === clienteSeleccionado.id
        ? { ...c, comentarios_count: (parseInt(c.comentarios_count) || 0) + 1 }
        : c
    ));
  } catch(err) {
    console.error('Error al agregar comentario:', err);
    avisarError('guardar la nota', err);
  } finally {
    setGuardandoComentario(false);
  }
};

const eliminarComentario = async (comentario_id) => {
  const ok = await confirmar({
    peligro: true,
    titulo: '¿Eliminar este comentario?',
    cuerpo: 'Se borra de las notas del cliente para todos.',
    accion: 'Eliminar comentario',
  });
  if (!ok) return;

  try {
    await fetchAPI(`/comentarios/${comentario_id}`, {
      method: 'DELETE'
    });

    // Remover el comentario de la lista
    setComentariosCliente(comentariosCliente.filter(c => c.id !== comentario_id));
    setClientes(prev => prev.map(c =>
      c.id === clienteSeleccionado.id
        ? { ...c, comentarios_count: Math.max(0, (parseInt(c.comentarios_count) || 0) - 1) }
        : c
    ));
  } catch(err) {
    console.error('Error al eliminar comentario:', err);
    avisarError('eliminar el comentario', err);
  }
};

// ========== FUNCIONES DE WHATSAPP ==========


// eslint-disable-next-line no-unused-vars
const cargarDocumentosFaltantes = async (clienteId) => {
  try {
    const res = await fetchAPI(`/clientes/${clienteId}/documentos-faltantes?mes=${selectedMes+1}&anio=${selectedAnio}`);
    setDocumentosFaltantes(prev => ({
      ...prev,
      [clienteId]: res.documentos_faltantes
    }));
  } catch(err) {
    console.error('Error al cargar documentos faltantes:', err);
  }
};

const enviarNotificacionesSeleccionadas = async () => {
  limpiarErrores('envio-clientes', 'envio-mensaje');
  if (envioManual.clientes_seleccionados.length === 0) {
    marcarError('envio-clientes', 'Selecciona al menos un cliente.');
    return;
  }

  if (envioManual.tipo === 'personalizado' && !envioManual.mensaje_personalizado.trim()) {
    marcarError('envio-mensaje', 'Escribe el mensaje que quieres enviar.');
    return;
  }

  // Resumen por canal y progreso cliente por cliente (C-010)
  const seleccion = clientes.filter(c => envioManual.clientes_seleccionados.includes(c.id));
  const conCorreo = seleccion.filter(c => correosValidos(c.email).length > 0);
  const soloTelefono = seleccion.filter(c => correosValidos(c.email).length === 0 && tieneTelefono(c.telefono));
  const sinContacto = seleccion
    .filter(c => correosValidos(c.email).length === 0 && !tieneTelefono(c.telefono))
    .map(c => ({ id: c.id, nombre: c.nombre, motivo: c.email ? 'correo no válido' : 'sin correo ni teléfono' }));
  const totalCorreos = new Set(conCorreo.flatMap(c => correosValidos(c.email).map(e => e.toLowerCase()))).size;

  if (conCorreo.length === 0) {
    avisar({ tipo: 'info', texto: 'Ninguno de los clientes seleccionados tiene correo válido.' });
    return;
  }

  const hoy = new Date();
  const periodo = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
  const mesTexto = periodo.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }).replace(/^./, l => l.toUpperCase()).replace(' de ', ' ');
  const QUE_SE_ENVIA = {
    solicitud_inicial: 'Solicitud de documentos',
    recordatorio_faltantes: 'Recordatorio de documentos',
    personalizado: 'Mensaje personalizado',
  };
  const tipoEnvio = envioManual.tipo;
  const mensajePersonalizado = envioManual.mensaje_personalizado;
  const aEnviar = [...conCorreo, ...soloTelefono];

  await confirmar({
    nivel: 3,
    titulo: tipoEnvio === 'personalizado'
      ? `¿Enviar el mensaje a ${plural(conCorreo.length, 'cliente', 'clientes')}?`
      : `¿Enviar ${QUE_SE_ENVIA[tipoEnvio].toLowerCase()} de ${mesTexto} a ${plural(conCorreo.length, 'cliente', 'clientes')}?`,
    cuerpo: (
      <ResumenEnvio
        mes={tipoEnvio === 'personalizado' ? null : mesTexto}
        etiquetaMes={tipoEnvio === 'solicitud_inicial' ? 'Mes que se solicita' : 'Mes que se recuerda'}
        destinatarios={`${plural(conCorreo.length, 'cliente', 'clientes')} por correo (${plural(totalCorreos, 'dirección', 'direcciones')})`}
        soloTelefono={soloTelefono.map(c => ({ id: c.id, nombre: c.nombre }))}
        sinContacto={sinContacto}
        nota="Una vez enviado no se puede deshacer."
      />
    ),
    accion: `Enviar a ${plural(conCorreo.length, 'cliente', 'clientes')}`,
    accionEnCurso: 'Enviando…',
    ejecutar: async (reportar) => {
      setEnviandoNotificaciones(true);
      let enviados = 0;
      const fallidos = [];
      try {
        for (let i = 0; i < aEnviar.length; i++) {
          const cliente = aEnviar[i];
          reportar(`Enviando… ${i + 1} de ${aEnviar.length}`);
          try {
            const res = await fetchAPI('/notificaciones/enviar-individual', {
              method: 'POST',
              body: JSON.stringify({ cliente_id: cliente.id, tipo: tipoEnvio, mensaje_personalizado: mensajePersonalizado }),
            });
            const correos = res?.resultados?.email || [];
            if (correos.some(r => r.success)) enviados++;
            else if (correos.length > 0) {
              fallidos.push({ nombre: cliente.nombre, razon: correos.find(r => !r.success)?.error || 'No se pudo enviar' });
            }
          } catch (err) {
            fallidos.push({ nombre: cliente.nombre, razon: err.message || 'No se pudo enviar' });
          }
        }
      } finally {
        setEnviandoNotificaciones(false);
      }
      setEnvioManual({ clientes_seleccionados: [], tipo: 'solicitud_inicial', mensaje_personalizado: '' });
      if (tipoEnvio === 'solicitud_inicial') cargarDatos();
      if (fallidos.length === 0) {
        avisar({ texto: `${QUE_SE_ENVIA[tipoEnvio]} enviado a ${plural(enviados, 'cliente', 'clientes')}.` });
        return null;
      }
      return { resultado: <ResultadoEnvio enviados={enviados} unidad={['cliente', 'clientes']} fallidos={fallidos} /> };
    },
  });
};

// Envío masivo de solicitud o recordatorio (C-010, ver diseno/dialogos.md sección C).
// El servidor dice antes a quién le llegaría y si ya se envió; durante el envío se ve el progreso.
const enviarNotificacionesManuales2 = async (tipo = 'solicitud_inicial') => {
  const esSolicitud = tipo === 'solicitud_inicial';
  let resumen;
  try {
    resumen = await fetchAPI(`/notificaciones/resumen-envio?tipo=${tipo}`);
  } catch (err) {
    console.error(err);
    avisar({ tipo: 'error', texto: 'No se pudo preparar el envío. Revisa la conexión e intenta de nuevo.' });
    return;
  }

  if (resumen.enCurso) {
    avisar({ tipo: 'info', texto: `Ya hay un envío en curso (${textoProgreso(resumen.enCurso)}). Espera a que termine.` });
    return;
  }

  const mesTexto = `${resumen.nombreMes} ${resumen.anio}`;
  const destinatarios = resumen.conCorreo.length;
  if (destinatarios === 0) {
    avisar({
      tipo: 'info',
      texto: esSolicitud
        ? `Ningún cliente activo tiene correo válido para enviarle la solicitud de ${mesTexto}.`
        : `Nadie tiene documentos pendientes de ${mesTexto} con correo válido; no hay recordatorios que enviar.`,
    });
    return;
  }

  const textoFinal = (enviados) => esSolicitud
    ? `Solicitud de ${mesTexto} enviada: ${plural(enviados, 'correo', 'correos')}.`
    : `Recordatorio de ${mesTexto} enviado: ${plural(enviados, 'correo', 'correos')}.`;

  const respuesta = await confirmar({
    nivel: 3,
    titulo: esSolicitud ? `¿Enviar la solicitud de documentos de ${mesTexto}?` : `¿Enviar el recordatorio de documentos de ${mesTexto}?`,
    cuerpo: (
      <ResumenEnvio
        mes={mesTexto}
        etiquetaMes={esSolicitud ? 'Mes que se solicita' : 'Mes que se recuerda'}
        destinatarios={`${plural(destinatarios, 'cliente', 'clientes')} por correo (${plural(resumen.totalCorreos, 'dirección', 'direcciones')})`}
        soloTelefono={resumen.soloTelefono}
        sinContacto={resumen.sinContacto}
        yaEnviado={resumen.yaEnviado}
        nota={!esSolicitud && resumen.yaAlCorriente > 0
          ? `${plural(resumen.yaAlCorriente, 'cliente ya entregó sus documentos y no lo recibirá', 'clientes ya entregaron sus documentos y no lo recibirán')}.`
          : null}
      />
    ),
    accion: `Enviar a ${plural(destinatarios, 'cliente', 'clientes')}`,
    accionEnCurso: 'Enviando…',
    segundoPlano: 'Seguir en segundo plano',
    ejecutar: async (reportar, { sigueAbierto }) => {
      await fetchAPI(esSolicitud ? '/notificaciones/enviar-mensuales' : '/notificaciones/enviar-recordatorio-faltantes', {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const final = await esperarEnvioMasivo(() => fetchAPI('/notificaciones/progreso'), reportar);
      const enviados = final.enviados || 0;
      const fallidos = final.fallidos || [];
      cargarDatos();
      if (fallidos.length === 0) {
        avisar({ texto: textoFinal(enviados) });
        return null;
      }
      if (!sigueAbierto()) {
        avisar({ tipo: 'error', texto: `${textoFinal(enviados)} ${plural(fallidos.length, 'falló', 'fallaron')}; revisa el reporte de entrega de correos.` });
        return null;
      }
      return {
        resultado: (
          <ResultadoEnvio
            enviados={enviados}
            unidad={['correo', 'correos']}
            fallidos={fallidos}
            extra="El detalle completo está en Correos a clientes, pestaña Reporte."
          />
        ),
      };
    },
  });

  if (respuesta === 'segundo-plano') {
    avisar({ tipo: 'info', texto: 'El envío sigue en segundo plano. Te aviso cuando termine.' });
  }
};

const toggleClienteSeleccionado = (clienteId) => {
  setEnvioManual(prev => {
    const yaSeleccionado = prev.clientes_seleccionados.includes(clienteId);
    return {
      ...prev,
      clientes_seleccionados: yaSeleccionado
        ? prev.clientes_seleccionados.filter(id => id !== clienteId)
        : [...prev.clientes_seleccionados, clienteId]
    };
  });
};

const seleccionarTodosClientes = () => {
  const todosLosIds = clientes.map(c => c.id);
  const todosMarcados = envioManual.clientes_seleccionados.length === todosLosIds.length;
  
  setEnvioManual(prev => ({
    ...prev,
    clientes_seleccionados: todosMarcados ? [] : todosLosIds
  }));
};


// jsPDF y ExcelJS pesan mucho: se cargan solo al exportar, no al abrir la app
const generarPDF = async () => {
  let jsPDF;
  try {
    ({ default: jsPDF } = await import('jspdf'));
  } catch (e) { return avisarError('preparar el PDF', e); }
  const doc = new jsPDF();
  const fecha = new Date().toLocaleString('es-MX');
  let yPos = 20;

  const ESTADOS_COMPLETADOS = new Set(['verde', 'azul']);
  const ESTADO_EN_PROCESO = 'amarillo';
  const procesosGenerales = Array.isArray(procesos) ? procesos : [];
  const procesosGeneralesIds = new Set(procesosGenerales.map(proceso => proceso.id));

  // ===== ENCABEZADO =====
  doc.setFillColor(41, 128, 185);
  doc.rect(0, 0, 210, 25, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text("REPORTE DE ACTIVIDADES", 105, 12, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`${meses[selectedMes]} ${selectedAnio}`, 105, 19, { align: 'center' });

  // ===== INFO GENERAL =====
  yPos = 35;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(9);
  doc.text(`Generado: ${fecha}`, 14, yPos);
  yPos += 10;
  // C-018: se exporta lo que se está viendo, con los filtros puestos
  if (filtrosActivos.length > 0) {
    doc.text(`Filtros: ${filtrosActivos.map(f => f.texto).join(' · ')}`, 14, yPos - 5);
    yPos += 5;
  }

  
  const clientesCompletados = [];
  const clientesEnProceso = [];
  const clientesPendientes = [];

  clientesFiltrados.forEach(cliente => {
    // Calcular total solo con procesos generales visibles en la tabla mensual
    const totalProcesos = procesosGenerales.length;

    let completados = 0;
    let enProceso = 0;
    const estadosCliente = [];

    // Contar solo estados de procesos generales para evitar contaminar con actividades especiales
    if (Array.isArray(cliente.estados)) {
      cliente.estados.forEach(estado => {
        if (!procesosGeneralesIds.has(estado.proceso_id)) return;

        if (ESTADOS_COMPLETADOS.has(estado.estado)) {
          completados++;
        } else if (estado.estado === ESTADO_EN_PROCESO) {
          enProceso++;
        }
      });
    }

    procesosGenerales.forEach(proceso => {
      const estado = getEstadoColor(cliente.id, proceso.id);
      estadosCliente.push({ proceso: nombreVisibleProceso(proceso), estado: estado });
    });

    const pendientes = Math.max(totalProcesos - (completados + enProceso), 0);

    const clienteData = {
      id: cliente.id,
      nombre: cliente.nombre,
      tipo: cliente.tipo === 'moral' ? 'P. Moral' : 'P. Física',
      contador: cliente.contador_nombre || 'Sin asignar',
      completados,
      enProceso,
      pendientes,
      porcentaje: totalProcesos > 0 ? Math.round((completados / totalProcesos) * 100) : 0,
      estados: estadosCliente,
      actividades: cliente.actividades || [] , // <-- intenta leer actividades asociadas
      regimen: cliente.regimen || '',
      totalProcesos: totalProcesos
    };

    if (completados === totalProcesos && totalProcesos > 0) {
      clientesCompletados.push(clienteData);
    } else if (enProceso > 0 || completados > 0) {
      clientesEnProceso.push(clienteData);
    } else {
      clientesPendientes.push(clienteData);
    }
  });

  // ===== RESUMEN EJECUTIVO =====
  // eslint-disable-next-line no-unused-vars
  const totalClientes = clientesFiltrados.length;

  // Calcular total de tareas sumando los procesos específicos de cada cliente
  const totalTareas = [...clientesCompletados, ...clientesEnProceso, ...clientesPendientes]
    .reduce((sum, c) => sum + c.totalProcesos, 0);

  // Contar TODAS las tareas completadas (General + específicas)
  const tareasCompletadas = [...clientesCompletados, ...clientesEnProceso, ...clientesPendientes]
    .reduce((sum, c) => sum + c.completados, 0);

  doc.setFillColor(240, 240, 240);
  doc.rect(14, yPos, 182, 25, "F");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text("RESUMEN EJECUTIVO", 105, yPos + 6, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(34, 139, 34);
  doc.text(`  ${clientesCompletados.length} Completados`, 25, yPos + 14);
  doc.setTextColor(255, 165, 0);
  doc.text(`  ${clientesEnProceso.length} En proceso`, 90, yPos + 14);
  doc.setTextColor(220, 53, 69);
  doc.text(`  ${clientesPendientes.length} Pendientes`, 160, yPos + 14);

  doc.setTextColor(60, 60, 60);
  doc.setFontSize(9);
  doc.text(`Total de tareas: ${tareasCompletadas}/${totalTareas} completadas (${ totalTareas ? Math.round((tareasCompletadas/totalTareas)*100) : 0 }%)`, 105, yPos + 21, { align: 'center' });
  yPos += 35;

  // ==== Helper para paginado sencillo ====
  const ensureSpace = (needed = 30) => {
    if (yPos + needed > 275) {
      doc.addPage();
      yPos = 20;
    }
  };

  // ===== SECCIONES POR GRUPO =====
  const renderGrupo = (titulo, grupo, fillColor) => {
    if (grupo.length === 0) return;
    ensureSpace(40);
    doc.setFillColor(...fillColor);
    doc.rect(14, yPos, 182, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text(`${titulo} (${grupo.length})`, 16, yPos + 5.5);
    yPos += 12;

    grupo.forEach(cliente => {
      ensureSpace(36);
      doc.setFillColor(255, 255, 255);
      doc.rect(14, yPos, 182, 12, "F");
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(cliente.nombre, 16, yPos + 5);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      doc.text(`${cliente.tipo} | ${cliente.contador}`, 16, yPos + 9);

      // indicadores (pequeños números)
      doc.setFontSize(8);
      doc.setTextColor(34, 139, 34);
      doc.text(`${cliente.completados}`, 16, yPos + 14);
      doc.setTextColor(255, 165, 0);
      doc.text(` ${cliente.enProceso}`, 38, yPos + 14);
      doc.setTextColor(220, 53, 69);
      doc.text(` ${cliente.pendientes}`, 58, yPos + 14);

      doc.setTextColor(255, 140, 0);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.text(`${cliente.porcentaje}%`, 170, yPos + 7, { align: 'right' });

      yPos += 19;
    });

    yPos += 6;
  };

  renderGrupo('CLIENTES COMPLETADOS', clientesCompletados, [34, 139, 34]);
  renderGrupo('CLIENTES EN PROCESO', clientesEnProceso, [255, 165, 0]);
  renderGrupo('CLIENTES PENDIENTES', clientesPendientes, [220, 53, 69]);

  // ===== SECCIÓN: ACTIVIDADES DETALLE (SI EXISTE clienteSeleccionado) =====
  // Esta parte agrega las actividades que vienen del modal "Detalle".
  if (clienteSeleccionado && Array.isArray(clienteSeleccionado.actividades) && clienteSeleccionado.actividades.length > 0) {
    ensureSpace(30);
    doc.setFillColor(52, 152, 219);
    doc.rect(14, yPos, 182, 8, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    const regimenText = clienteSeleccionado.regimen ? clienteSeleccionado.regimen : "RÉGIMEN";
    doc.text(`DETALLE DE ACTIVIDADES – ${regimenText}`, 16, yPos + 5.5);
    yPos += 12;

    // Cabecera tabla simple
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);

    clienteSeleccionado.actividades.forEach((act, idx) => {
      ensureSpace(36);

      // Nombre actividad
      doc.setFont(undefined, 'bold');
      doc.setFontSize(10);
      doc.text(`${idx + 1}. ${act.nombre || 'Actividad sin nombre'}`, 16, yPos);

      // Descripción y monto (si existen)
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);
      if (act.descripcion) {
        doc.text(`Descripción: ${act.descripcion}`, 16, yPos + 6);
        if (act.monto !== undefined) {
          doc.text(`Monto: $${Number(act.monto).toLocaleString('es-MX')}`, 140, yPos + 6, { align: 'right' });
        }
        yPos += 14;
      } else {
        if (act.monto !== undefined) {
          doc.text(`Monto: $${Number(act.monto).toLocaleString('es-MX')}`, 16, yPos + 6);
          yPos += 12;
        } else {
          yPos += 12;
        }
      }
    });

    yPos += 6;
  }

 
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Página ${i} de ${totalPages}`, 105, 290, { align: 'center' });
  }

 
  const mesNombre = meses[selectedMes] || 'ANUAL';
  doc.save(`reporte_${mesNombre}_${selectedAnio}.pdf`);
};

// ===== EXPORTAR EXCEL: REPORTE DE COBRANZA =====
// Genera el workbook 100% en el cliente, a partir de los datos de cobranza ya cargados
// en memoria. Siempre exporta el listado COMPLETO de clientes (ignora el buscador y el
// filtro por contador de la tabla) para que sirva como reporte completo, no como "vista
// actual".
const COLOR_EXCEL = {
  primary: 'FF0891B2',
  success: 'FF16A34A',
  successBg: 'FFDCFCE7',
  danger: 'FFDC2626',
  dangerBg: 'FFFEE2E2',
  warning: 'FFF59E0B',
  slate900: 'FF0F172A',
  slate600: 'FF475569',
  slate200: 'FFE5E7EB',
  slate50: 'FFF8FAFC',
  white: 'FFFFFFFF',
};
const MONEY_FMT_EXCEL = '"$"#,##0.00';

const generarExcelCobranza = async () => {
  setGenerandoExcel(true);
  try {
    const OTROS_INGRESOS_NOMBRES = new Set([
      'SERVICIO LOS OLIVOS',
      'SERVICIOS LOS OLIVOS',
      'SERVICIOS LOS OLIVOS 2',
      'SERVICIO BUSTRI',
      'INDUSTRIALIZADORA Y MADERERA LOS OLIVOS',
      'INDUSTRIALIZADORA Y MADEDERIA LOS OLIVOS',
      'TARIMERA LOS OLIVOS',
    ]);
    const esOtrosIngresos = (c) => {
      const n = (c.nombre || '').trim().toUpperCase().replace(/\s+/g, ' ');
      return c.otros_ingresos || OTROS_INGRESOS_NOMBRES.has(n) ||
        (n.startsWith('SERVICIO') && n.includes('LOS OLIVOS'));
    };

    // Listado completo de clientes, igual que la fuente de la tabla de Cobranza pero
    // SIN aplicar el buscador ni el filtro por contador.
    const base = (cobranzaTodosClientes.length > 0
      ? cobranzaTodosClientes.map(cl => {
          const existing = cobranzaData.find(c => c.id === cl.id);
          return {
            id: cl.id,
            nombre: cl.nombre,
            rfc: cl.rfc || '',
            otros_ingresos: cl.otros_ingresos || false,
            activo: cl.activo !== false,
            factura: existing?.factura ?? (Number(cl.factura_mensual) || 0),
            contador_nombre: cl.contador_nombre || '',
          };
        })
      : cobranzaData
    ).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }));

    const clientesRegular = base.filter(c => !esOtrosIngresos(c));
    const clientesOtros = base.filter(c => esOtrosIngresos(c));
    const saldoDe = (c) => Number(cobranzaSaldos[c.id]) || 0;

    const totalFacturaRegular = clientesRegular.reduce((s, c) => s + Number(c.factura || 0), 0);
    const totalSaldoRegular = clientesRegular.reduce((s, c) => s + saldoDe(c), 0);
    const totalFacturaOtros = clientesOtros.reduce((s, c) => s + Number(c.factura || 0), 0);
    const totalSaldoOtros = clientesOtros.reduce((s, c) => s + saldoDe(c), 0);
    const totalFacturaGeneral = totalFacturaRegular + totalFacturaOtros;
    const totalSaldoGeneral = totalSaldoRegular + totalSaldoOtros;

    const totalCobradoAnio = resumenMensual.reduce((s, r) => s + Number(r.cobrado || 0), 0);
    const totalPendienteAnio = resumenMensual.reduce((s, r) => s + Number(r.pendiente || 0), 0);
    const totalAnio = totalCobradoAnio + totalPendienteAnio;
    const pctCobradoAnio = totalAnio > 0 ? totalCobradoAnio / totalAnio : 0;
    const clientesActivos = base.filter(c => c.activo !== false).length;

    const resumenRows = resumenMensual.map(r => {
      const total = Number(r.total || 0);
      const pct = total > 0 ? Number(r.cobrado) / total : 0;
      return [MESES_NOMBRE[r.mes - 1], Number(r.cobrado || 0), Number(r.pendiente || 0), total, pct];
    });

    // Bundle de navegador: exceljs "a secas" intenta resolver módulos de Node (fs, etc.)
    // que Webpack 5 (react-scripts 5) ya no polyfillea, y rompe el build.
    const { default: ExcelJS } = await import('exceljs/dist/exceljs.min.js');
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Activo Contadores';
    wb.created = new Date();

    // ===== HOJA 1: RESUMEN (dashboard con KPIs y navegación) =====
    const wsResumen = wb.addWorksheet('Resumen', { properties: { tabColor: { argb: COLOR_EXCEL.primary } } });
    wsResumen.columns = [{ width: 26 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }];

    wsResumen.mergeCells('A1:E2');
    const titleCell = wsResumen.getCell('A1');
    titleCell.value = 'REPORTE DE COBRANZA';
    titleCell.font = { bold: true, size: 20, color: { argb: COLOR_EXCEL.white } };
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
    for (let r = 1; r <= 2; r++) {
      for (let c = 1; c <= 5; c++) {
        wsResumen.getRow(r).getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_EXCEL.primary } };
      }
    }

    wsResumen.mergeCells('A3:E3');
    const subCell = wsResumen.getCell('A3');
    subCell.value = `Periodo: ${selectedAnio}  ·  Generado: ${new Date().toLocaleString('es-MX')}`;
    subCell.font = { italic: true, size: 10, color: { argb: COLOR_EXCEL.slate600 } };
    subCell.alignment = { horizontal: 'center' };

    const kpis = [
      { label: 'Facturación total', value: totalFacturaGeneral, sheet: 'Clientes', color: COLOR_EXCEL.primary, fmt: MONEY_FMT_EXCEL },
      { label: 'Saldo pendiente', value: totalSaldoGeneral, sheet: 'Cobros pendientes', color: COLOR_EXCEL.danger, fmt: MONEY_FMT_EXCEL },
      { label: `Cobrado ${selectedAnio}`, value: totalCobradoAnio, sheet: 'Resumen mensual', color: COLOR_EXCEL.success, fmt: MONEY_FMT_EXCEL },
      { label: '% Cobrado del año', value: pctCobradoAnio, sheet: 'Resumen mensual', color: COLOR_EXCEL.warning, fmt: '0%' },
      { label: 'Clientes activos', value: clientesActivos, sheet: 'Clientes', color: COLOR_EXCEL.slate900, fmt: '0' },
    ];
    kpis.forEach((k, i) => {
      const col = i + 1;
      const labelCell = wsResumen.getRow(5).getCell(col);
      labelCell.value = { text: k.label, hyperlink: `#'${k.sheet}'!A1`, tooltip: `Ir a ${k.sheet}` };
      labelCell.font = { bold: true, size: 9, color: { argb: COLOR_EXCEL.white } };
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: k.color } };
      labelCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      const valCell = wsResumen.getRow(6).getCell(col);
      valCell.value = k.value;
      valCell.numFmt = k.fmt;
      valCell.font = { bold: true, size: 14, color: { argb: k.color } };
      valCell.alignment = { horizontal: 'center' };
    });
    wsResumen.getRow(5).height = 28;
    wsResumen.getRow(6).height = 22;

    const startRow = 9;
    wsResumen.getCell(`A${startRow - 1}`).value = 'Resumen por mes';
    wsResumen.getCell(`A${startRow - 1}`).font = { bold: true, size: 12, color: { argb: COLOR_EXCEL.slate900 } };

    wsResumen.addTable({
      name: 'ResumenPorMes',
      ref: `A${startRow}`,
      headerRow: true,
      totalsRow: true,
      style: { theme: 'TableStyleMedium9', showRowStripes: true },
      columns: [
        { name: 'Mes', totalsRowLabel: 'Total' },
        { name: 'Cobrado', filterButton: true, totalsRowFunction: 'sum' },
        { name: 'Pendiente', filterButton: true, totalsRowFunction: 'sum' },
        { name: 'Total', filterButton: true, totalsRowFunction: 'sum' },
        { name: '% Cobrado', filterButton: true, totalsRowFunction: 'none' },
      ],
      rows: resumenRows,
    });
    for (let r = 0; r < resumenRows.length; r++) {
      const row = wsResumen.getRow(startRow + 1 + r);
      row.getCell(2).numFmt = MONEY_FMT_EXCEL;
      row.getCell(3).numFmt = MONEY_FMT_EXCEL;
      row.getCell(4).numFmt = MONEY_FMT_EXCEL;
      row.getCell(5).numFmt = '0%';
    }
    const totalsRowIdxResumen = startRow + 1 + resumenRows.length;
    [2, 3, 4].forEach(c => { wsResumen.getRow(totalsRowIdxResumen).getCell(c).numFmt = MONEY_FMT_EXCEL; });

    if (resumenRows.length > 0) {
      wsResumen.addConditionalFormatting({
        ref: `E${startRow + 1}:E${startRow + resumenRows.length}`,
        rules: [{
          type: 'colorScale',
          cfvo: [{ type: 'min' }, { type: 'max' }],
          color: [{ argb: COLOR_EXCEL.danger }, { argb: COLOR_EXCEL.success }],
        }],
      });
    }
    wsResumen.views = [{ state: 'frozen', ySplit: startRow }];

    // ===== HOJA 2: CLIENTES (tabla nativa filtrable/ordenable) =====
    const wsClientes = wb.addWorksheet('Clientes', { properties: { tabColor: { argb: COLOR_EXCEL.primary } } });
    wsClientes.getCell('A1').value = { text: '← Volver a resumen', hyperlink: `#'Resumen'!A1` };
    wsClientes.getCell('A1').font = { color: { argb: COLOR_EXCEL.primary }, italic: true, size: 9 };

    const clienteRows = [
      ...clientesRegular.map(c => [c.nombre, c.rfc || '', c.contador_nombre || '', Number(c.factura || 0), saldoDe(c), c.activo !== false ? 'Activo' : 'Inactivo', 'Cobranza']),
      ...clientesOtros.map(c => [c.nombre, c.rfc || '', c.contador_nombre || '', Number(c.factura || 0), saldoDe(c), c.activo !== false ? 'Activo' : 'Inactivo', 'Otros ingresos']),
    ];

    wsClientes.addTable({
      name: 'ClientesCobranza',
      ref: 'A3',
      headerRow: true,
      totalsRow: true,
      style: { theme: 'TableStyleMedium9', showRowStripes: true },
      columns: [
        { name: 'Cliente' },
        { name: 'RFC' },
        { name: 'Contador' },
        { name: 'Factura mensual', filterButton: true, totalsRowFunction: 'sum' },
        { name: 'Saldo pendiente', filterButton: true, totalsRowFunction: 'sum' },
        { name: 'Estado', filterButton: true },
        { name: 'Grupo', filterButton: true },
      ],
      rows: clienteRows,
    });
    for (let r = 0; r < clienteRows.length; r++) {
      const row = wsClientes.getRow(4 + r);
      row.getCell(4).numFmt = MONEY_FMT_EXCEL;
      row.getCell(5).numFmt = MONEY_FMT_EXCEL;
      const saldoVal = Number(row.getCell(5).value || 0);
      row.getCell(5).font = { bold: true, color: { argb: saldoVal > 0 ? COLOR_EXCEL.danger : COLOR_EXCEL.success } };
      if (row.getCell(6).value === 'Inactivo') {
        row.eachCell(cell => { cell.font = { ...(cell.font || {}), italic: true, color: { argb: COLOR_EXCEL.slate600 } }; });
      }
    }
    const totalsRowIdxClientes = 3 + clienteRows.length + 1;
    [4, 5].forEach(c => { wsClientes.getRow(totalsRowIdxClientes).getCell(c).numFmt = MONEY_FMT_EXCEL; });
    if (clienteRows.length > 0) {
      wsClientes.addConditionalFormatting({
        ref: `E4:E${3 + clienteRows.length}`,
        rules: [{
          type: 'dataBar',
          minLength: 0,
          maxLength: 100,
          color: { argb: COLOR_EXCEL.danger },
          cfvo: [{ type: 'min' }, { type: 'max' }],
        }],
      });
    }
    [28, 18, 18, 16, 16, 12, 14].forEach((w, i) => { wsClientes.getColumn(i + 1).width = w; });
    wsClientes.views = [{ state: 'frozen', ySplit: 3 }];

    // ===== HOJA 3: COBROS PENDIENTES (agrupada y colapsable por cliente) =====
    const wsPend = wb.addWorksheet('Cobros pendientes', {
      properties: { tabColor: { argb: COLOR_EXCEL.danger }, outlineProperties: { summaryBelow: false } },
    });
    wsPend.getCell('A1').value = { text: '← Volver a resumen', hyperlink: `#'Resumen'!A1` };
    wsPend.getCell('A1').font = { color: { argb: COLOR_EXCEL.primary }, italic: true, size: 9 };
    wsPend.columns = [{ width: 32 }, { width: 12 }, { width: 18 }];

    const headerRowPend = wsPend.getRow(3);
    headerRowPend.values = ['Cliente / Mes', 'Año', 'Monto'];
    headerRowPend.eachCell(cell => {
      cell.font = { bold: true, color: { argb: COLOR_EXCEL.white } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_EXCEL.danger } };
    });

    const gruposPend = Object.values(cobrosPendientesTodos.reduce((acc, c) => {
      if (!acc[c.cliente_id]) acc[c.cliente_id] = { cliente_id: c.cliente_id, nombre: c.nombre, cobros: [], total: 0 };
      acc[c.cliente_id].cobros.push(c);
      acc[c.cliente_id].total += Number(c.monto);
      return acc;
    }, {}));
    gruposPend.sort((a, b) => {
      const ca = a.cobros[0]; const cb = b.cobros[0];
      return (ca.anio * 100 + ca.mes) - (cb.anio * 100 + cb.mes);
    });

    let rIdxPend = 4;
    gruposPend.forEach(g => {
      const groupRow = wsPend.getRow(rIdxPend);
      groupRow.values = [g.nombre, '', g.total];
      groupRow.font = { bold: true };
      groupRow.getCell(3).numFmt = MONEY_FMT_EXCEL;
      groupRow.getCell(3).font = { bold: true, color: { argb: COLOR_EXCEL.danger } };
      rIdxPend++;
      g.cobros.forEach(c => {
        const detailRow = wsPend.getRow(rIdxPend);
        detailRow.values = [`   ${MESES_NOMBRE[c.mes - 1]}`, c.anio, Number(c.monto)];
        detailRow.getCell(3).numFmt = MONEY_FMT_EXCEL;
        detailRow.font = { color: { argb: COLOR_EXCEL.slate600 } };
        detailRow.outlineLevel = 1;
        detailRow.hidden = true;
        rIdxPend++;
      });
    });
    const totalGlobalPend = gruposPend.reduce((s, g) => s + g.total, 0);
    const totalRowPend = wsPend.getRow(rIdxPend);
    totalRowPend.values = ['Total general', '', totalGlobalPend];
    totalRowPend.font = { bold: true };
    totalRowPend.getCell(3).numFmt = MONEY_FMT_EXCEL;
    totalRowPend.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_EXCEL.slate50 } };
      cell.border = { top: { style: 'medium', color: { argb: COLOR_EXCEL.slate200 } } };
    });

    wsPend.views = [{ state: 'frozen', ySplit: 3 }];

    // ===== HOJA 4: RESUMEN MENSUAL (tabla plana filtrable, coincide con selectedAnio) =====
    const wsMensual = wb.addWorksheet('Resumen mensual', { properties: { tabColor: { argb: COLOR_EXCEL.success } } });
    wsMensual.getCell('A1').value = { text: '← Volver a resumen', hyperlink: `#'Resumen'!A1` };
    wsMensual.getCell('A1').font = { color: { argb: COLOR_EXCEL.primary }, italic: true, size: 9 };
    wsMensual.getCell('A2').value = `Año ${selectedAnio}`;
    wsMensual.getCell('A2').font = { bold: true, size: 11, color: { argb: COLOR_EXCEL.slate900 } };

    wsMensual.addTable({
      name: 'ResumenMensualDetalle',
      ref: 'A4',
      headerRow: true,
      totalsRow: true,
      style: { theme: 'TableStyleMedium9', showRowStripes: true },
      columns: [
        { name: 'Mes' },
        { name: 'Cobrado', filterButton: true, totalsRowFunction: 'sum' },
        { name: 'Pendiente', filterButton: true, totalsRowFunction: 'sum' },
        { name: 'Total', filterButton: true, totalsRowFunction: 'sum' },
        { name: '% Cobrado', filterButton: true, totalsRowFunction: 'none' },
      ],
      rows: resumenRows,
    });
    for (let r = 0; r < resumenRows.length; r++) {
      const row = wsMensual.getRow(5 + r);
      row.getCell(2).numFmt = MONEY_FMT_EXCEL;
      row.getCell(3).numFmt = MONEY_FMT_EXCEL;
      row.getCell(4).numFmt = MONEY_FMT_EXCEL;
      row.getCell(5).numFmt = '0%';
      if (Number(row.getCell(3).value) > 0) {
        row.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLOR_EXCEL.dangerBg } };
      }
    }
    const totalsRowIdxMensual = 4 + resumenRows.length + 1;
    [2, 3, 4].forEach(c => { wsMensual.getRow(totalsRowIdxMensual).getCell(c).numFmt = MONEY_FMT_EXCEL; });
    wsMensual.getColumn(1).width = 16;
    [2, 3, 4, 5].forEach(i => { wsMensual.getColumn(i).width = 16; });
    wsMensual.views = [{ state: 'frozen', ySplit: 4 }];

    // ===== Descargar =====
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fecha = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `cobranza_${selectedAnio}_${fecha}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Error al generar el Excel de cobranza:', err);
    avisarError('generar el Excel de cobranza', err);
  } finally {
    setGenerandoExcel(false);
  }
};

  if (window.location.pathname === '/pago-completado') {
    return <PagoCompletado />;
  }

  if (window.location.pathname.startsWith('/pagar/')) {
    const pagoToken = window.location.pathname.replace('/pagar/', '');
    return <PagarCheckout token={pagoToken} />;
  }

  if (window.location.pathname === '/privacidad') {
    return <PrivacidadPage />;
  }

  if (window.location.pathname === '/terminos') {
    return <TerminosPage />;
  }

  if(verificandoToken){
    return(
      <div className="login-container">
        <div className="login-box">
          <div className="logo"><img src="/logo.jpeg" alt="Activo Contadores" style={{ height: 48, objectFit: 'contain' }} /></div>
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{loadingMessage}</p>
            <small style={{color: 'var(--slate-500)', marginTop: '10px'}}>
              {loadingMessage.includes('Conectando') && 'Esto puede tomar unos segundos la primera vez...'}
            </small>
          </div>
        </div>
      </div>
    );
  }

  if(!token){
    return(
      <div className="login-container">
        <div className="login-box">
          <div className="logo"><img src="/logo.jpeg" alt="Activo Contadores" style={{ height: 90, objectFit: 'contain' }} /></div>
          <h2>Iniciar sesión</h2>
          {avisoLogin?.tipo === 'servidor' && <div className="banner-info" role="status">{avisoLogin.texto}</div>}
          <form onSubmit={handleLogin}>
            <input type="text" placeholder="Usuario" value={loginData.username} onChange={e=>setLoginData({...loginData, username:e.target.value})} required />
            <input type="password" placeholder="Contraseña" value={loginData.password} onChange={e=>setLoginData({...loginData, password:e.target.value})} required />
            {avisoLogin?.tipo === 'error' && <p className="error-campo" role="alert">{avisoLogin.texto}</p>}
            <button type="submit">Entrar</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Toast de reconectando */}
      {reconectando && (
        <div className="toast-reconectando">
          <div className="toast-spinner"></div>
          <span>Reconectando al servidor, espera un momento...</span>
        </div>
      )}
  {/* BARRA AZUL (C-013, ver diseno/referencias/propuesta-encabezado.html): una sola fila, fija al hacer scroll */}
    <header className="header" ref={refEncabezado}>
    <div className="logo"><img src="/logo.jpeg" alt="Activo Contadores" /></div>

    {/* C-016: solo las secciones que el rol puede ver; con una sola no hacen falta pestañas */}
    {seccionesDe(user).length > 1 && (
      <nav className="barra-secciones" aria-label="Secciones">
        {seccionesDe(user).map(sec => (
          <button
            key={sec.ruta}
            type="button"
            className={currentRoute === sec.ruta ? 'activa' : ''}
            aria-current={currentRoute === sec.ruta ? 'page' : undefined}
            onClick={() => navegarA(sec.ruta)}
          >
            {sec.nombre}
            {/* C-037: pasos por revisar del mes, a la vista también desde Cobranza o Viento */}
            {sec.ruta === '/' && pasosPorRevisarMes > 0 && (
              <span className="notification-badge" aria-label={`${pasosPorRevisarMes} ${pasosPorRevisarMes === 1 ? 'paso' : 'pasos'} por revisar`}>{pasosPorRevisarMes}</span>
            )}
          </button>
        ))}
      </nav>
    )}

    {/* C-018: el periodo siempre está en el mismo lugar; año solo en la vista Anual */}
    <div className="periodo" role="group" aria-label="Periodo">
      <BotonIcono
        type="button"
        className="periodo-flecha"
        etiqueta={periodoAnual ? 'Ejercicio anterior' : 'Mes anterior'}
        disabled={periodoAnual ? ejercicio <= EJERCICIO_MINIMO : anioBarra <= ANIO_MINIMO && mesBarra === 0}
        onClick={() => moverPeriodo(-1)}
      >
        <ChevronLeft size={18} aria-hidden="true" />
      </BotonIcono>
      {periodoAnual ? (
        <span className="periodo-actual">Ejercicio {ejercicio}</span>
      ) : (
        <button
          type="button"
          className="periodo-actual"
          aria-haspopup="true"
          aria-expanded={selectorPeriodo !== null}
          onClick={() => setSelectorPeriodo(selectorPeriodo === null ? anioBarra : null)}
          data-tooltip={currentRoute === '/' ? `${nombreMes(mesBarra)} se declara y se trabaja en ${nombreMes((mesBarra + 1) % 12).toLowerCase()}. Clic para elegir otro mes.` : undefined}
        >
          <span className="periodo-textos">
            <span>{nombreMes(mesBarra)} {anioBarra}</span>
            {/* C-024: el mes que se declara no es el mes en que se trabaja */}
            {currentRoute === '/' && <small>se trabaja en {nombreMes((mesBarra + 1) % 12).toLowerCase()}</small>}
          </span>
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      )}
      <BotonIcono
        type="button"
        className="periodo-flecha"
        etiqueta={periodoAnual ? 'Ejercicio siguiente' : 'Mes siguiente'}
        disabled={periodoAnual ? ejercicio >= ejercicioMaximo : anioBarra >= anioMaximo && mesBarra === 11}
        onClick={() => moverPeriodo(1)}
      >
        <ChevronRight size={18} aria-hidden="true" />
      </BotonIcono>

      {selectorPeriodo !== null && !periodoAnual && (
        <>
          <div className="menu-overlay" onClick={() => setSelectorPeriodo(null)} />
          <div
            className="periodo-panel"
            role="dialog"
            aria-label="Elegir mes"
          >
            <div className="periodo-anio">
              <BotonIcono
                type="button"
                etiqueta="Año anterior"
                disabled={selectorPeriodo <= ANIO_MINIMO}
                onClick={() => setSelectorPeriodo(a => a - 1)}
              >
                <ChevronLeft size={16} aria-hidden="true" />
              </BotonIcono>
              <span>{selectorPeriodo}</span>
              <BotonIcono
                type="button"
                etiqueta="Año siguiente"
                disabled={selectorPeriodo >= anioMaximo}
                onClick={() => setSelectorPeriodo(a => a + 1)}
              >
                <ChevronRight size={16} aria-hidden="true" />
              </BotonIcono>
            </div>
            <div className="periodo-meses">
              {meses.map((m, i) => {
                const actual = i === mesBarra && selectorPeriodo === anioBarra;
                return (
                  <button
                    key={m}
                    type="button"
                    className={actual ? 'activo' : ''}
                    aria-pressed={actual}
                    aria-label={`${nombreMes(i)} ${selectorPeriodo}`}
                    onClick={() => { cambiarPeriodo(i, selectorPeriodo); setSelectorPeriodo(null); }}
                  >
                    {nombreMes(i).slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>

    <div className="header-user">
      {/* C-018: Mensajes, a la vista para todos, con su número de no leídos */}
      <BotonIcono
        type="button"
        className="menu-btn barra-icono barra-mensajes"
        etiqueta={mensajesNoLeidos > 0 ? `Mensajes, ${mensajesNoLeidos} sin leer` : 'Mensajes'}
       
        onClick={() => { setShowMessages(true); cargarMensajes(); cargarUsuarios(); }}
      >
        <MessageSquare size={20} aria-hidden="true" />
        {mensajesNoLeidos > 0 && <span className="notification-badge">{mensajesNoLeidos}</span>}
      </BotonIcono>

      {/* C-018: lo del despacho, solo Dueño y Supervisor */}
      {esAdministrador(user) && (
        <div className="menu-container">
          <button
            type="button"
            className="menu-btn barra-admin-btn"
            aria-haspopup="menu"
            aria-expanded={showAdminMenu}
            onClick={() => { setShowAdminMenu(v => !v); setShowUserMenu(false); }}
          >
            <Settings size={16} aria-hidden="true" /> Administración
            {solicitudesPendientes.length > 0 && (
              <span className="notification-badge" aria-label={`${solicitudesPendientes.length} solicitudes de permiso pendientes`}>{solicitudesPendientes.length}</span>
            )}
            <ChevronDown size={14} aria-hidden="true" />
          </button>
          {showAdminMenu && (
            <>
              <div className="menu-overlay" onClick={() => setShowAdminMenu(false)} />
              <div className="dropdown-menu open menu-admin" role="menu" aria-label="Administración">
                <div className="menu-grupo">Clientes</div>
                <button type="button" role="menuitem" onClick={() => { setShowAdminMenu(false); setShowWhatsAppPanel(true); setNotificacionesTab('enviar'); cargarDatos(); }}>
                  <Mail size={16} aria-hidden="true" />
                  <span className="menu-texto">Correos a clientes<small>Solicitud, recordatorios y envíos</small></span>
                </button>
                <button type="button" role="menuitem" onClick={() => { setShowAdminMenu(false); abrirSolicitudesPermiso(); }}>
                  <Clock size={16} aria-hidden="true" />
                  <span className="menu-texto">Solicitudes de permiso<small>Acceso a meses pasados</small></span>
                  {solicitudesPendientes.length > 0 && <span className="badge-pendientes">{solicitudesPendientes.length}</span>}
                </button>
                <button type="button" role="menuitem" onClick={() => { setShowAdminMenu(false); cargarHistorial(); }}>
                  <ClipboardList size={16} aria-hidden="true" />
                  <span className="menu-texto">Historial de cambios<small>Quién cambió qué y cuándo</small></span>
                </button>
                <div className="menu-grupo">Despacho</div>
                <button type="button" role="menuitem" onClick={() => { setShowAdminMenu(false); abrirAjustes('despacho', 'usuarios'); }}>
                  <Users size={16} aria-hidden="true" />
                  <span className="menu-texto">Usuarios<small>Altas, roles y accesos</small></span>
                </button>
                <button type="button" role="menuitem" onClick={() => { setShowAdminMenu(false); abrirAjustes('despacho', 'respaldo'); }}>
                  <Download size={16} aria-hidden="true" />
                  <span className="menu-texto">Respaldo de archivos<small>ZIP con todos los archivos</small></span>
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* C-018: el menú del usuario solo tiene lo personal */}
      <div className="menu-container">
        <button
          type="button"
          className="menu-btn user-menu-btn"
          aria-haspopup="menu"
          aria-expanded={showUserMenu}
          onClick={() => { setShowUserMenu(v => !v); setShowAdminMenu(false); }}
        >
          <User size={16} aria-hidden="true" /> {user?.nombre}
          <ChevronDown size={14} aria-hidden="true" />
        </button>

        {showUserMenu && (
          <>
            <div className="menu-overlay" onClick={() => setShowUserMenu(false)} />
            <div className="dropdown-menu open user-dropdown" role="menu" aria-label="Tu cuenta">
              <div className="menu-quien">
                <b>{user?.nombre}</b>
                {user?.email && <span>{user.email}</span>}
                <span>{nombreRol(user?.rol)}</span>
              </div>
              <div className="divider" />
              <button type="button" role="menuitem" onClick={() => { setShowUserMenu(false); abrirAjustes('personal', 'perfil'); }}>
                <User size={16} aria-hidden="true" /> Mi perfil
              </button>
              <button type="button" role="menuitem" onClick={() => { setShowUserMenu(false); abrirAjustes('personal', 'password'); }}>
                <Lock size={16} aria-hidden="true" /> Cambiar contraseña
              </button>
              <div className="divider" />
              <button type="button" role="menuitem" onClick={() => { setShowUserMenu(false); handleLogout(); }} className="logout-option">
                <LogOut size={16} aria-hidden="true" /> Cerrar sesión
              </button>
            </div>
          </>
        )}
      </div>
    </div>
</header>

   {currentRoute === '/' && (
<>
   {/* BARRA DE HERRAMIENTAS (C-013, C-025): vista a la izquierda; buscar, PDF y Nuevo cliente a la derecha */}
<div className={`barra-herramientas${verInactivos ? ' filtros-apagados' : ''}`} role="group" aria-label="Vista y acciones">
  <div className="vista-segmento" role="group" aria-label="Vista">
    {[['mensual', 'Mensual'], ['especiales', 'Obligaciones adicionales'], ['anual', 'Declaración anual']].map(([valor, texto]) => (
      <button
        key={valor}
        type="button"
        aria-pressed={viewMode === valor}
        onClick={() => { setViewMode(valor); setSelectorPeriodo(null); setObligAtrasadas(false); }}
      >
        {texto}
      </button>
    ))}
  </div>
  <div className="barra-derecha">
    {viewMode === 'mensual' && (
      <label className="search-container">
        <Search size={14} aria-hidden="true" />
        <input
          type="search"
          className="search-input"
          placeholder="Buscar cliente o RFC…"
          aria-label="Buscar cliente o RFC"
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </label>
    )}
    {/* C-025: el botón dice qué descarga; el tooltip, con qué filtros */}
    {viewMode === 'mensual' && (
    <button
      type="button"
      className="btn-herramienta"
      onClick={generarPDF}
      data-tooltip={`Descarga ${nombreMes(selectedMes).toLowerCase()} ${selectedAnio} ${filtrosActivos.length ? `con estos filtros: ${filtrosActivos.map(f => f.texto).join(' · ')}` : 'sin filtros'}`}
    >
      <FileText size={16} aria-hidden="true" /> PDF de {nombreMes(selectedMes).toLowerCase()}
    </button>
    )}
    {esAdministrador(user) && (
      <button type="button" className="btn-herramienta principal" onClick={() => { setShowNewClient(true); cargarUsuarios(); }}>
        <Plus size={16} aria-hidden="true" /> Nuevo cliente
      </button>
    )}
  </div>
</div>

   {/* EMBUDO EN UNA FILA (C-012 + C-013): cada paso sigue siendo un filtro */}
 {viewMode === 'mensual' && (
 <div className={`embudo-fila${verInactivos ? ' filtros-apagados' : ''}`} role="group" aria-label={`Detenidos en cada paso, ${nombreMes(selectedMes).toLowerCase()} ${selectedAnio}`}>
  <span className="embudo-titulo">Detenidos en:</span>
  {embudo.map(paso => (
    <div
      key={paso.slug}
      className={`embudo-paso${paso.slug === PASO_TERMINADOS ? ' terminados' : ''}${filtroPaso === paso.slug ? ' seleccionado' : ''}`}
    >
      <button
        type="button"
        className="embudo-paso-btn"
        aria-pressed={filtroPaso === paso.slug}
        data-tooltip={paso.numero ? `Paso ${paso.numero}: ${paso.corto}` : 'Terminaron todos los pasos'}
        onClick={() => elegirPaso(paso.slug, false)}
      >
        <span className={`embudo-cuantos${paso.detenidos === 0 ? ' cero' : ''}`}>{paso.detenidos}</span>
        <span className="embudo-nombre">{paso.corto}</span>
      </button>
      {paso.atrasados > 0 && (
        <button
          type="button"
          className="embudo-atrasados"
          aria-pressed={filtroPaso === paso.slug && filtroAtrasados}
          aria-label={`${paso.atrasados} ${paso.atrasados === 1 ? 'atrasado' : 'atrasados'} en ${paso.corto}`}
          onClick={() => elegirPaso(paso.slug, true)}
        >
          {paso.atrasados} atr.
        </button>
      )}
    </div>
  ))}
</div>
 )}

 {/* FILTROS (C-025): de prender y apagar, y con opciones (Contador, Régimen, Industria) */}
 {viewMode === 'mensual' && (
 <div className={`filtros-fila${verInactivos ? ' filtros-apagados' : ''}`} role="group" aria-label="Filtros">
  <button
    type="button"
    className="pastilla atrasados"
    aria-pressed={filtroAtrasados}
    onClick={() => setFiltroAtrasados(v => !v)}
  >
    {filtroAtrasados ? <Check size={14} aria-hidden="true" /> : <Clock size={14} aria-hidden="true" className="icono-rojo" />}
    Atrasados <span className="pastilla-num rojo">{totalAtrasados}</span>
  </button>
  <button
    type="button"
    className="pastilla por-vencer"
    aria-pressed={filtroPorVencer}
    onClick={() => setFiltroPorVencer(v => !v)}
    data-tooltip="El paso actual vence hoy o en los próximos 3 días"
  >
    {filtroPorVencer ? <Check size={14} aria-hidden="true" /> : <Hourglass size={14} aria-hidden="true" />}
    Por vencer <span className="pastilla-num">{totalPorVencer}</span>
  </button>
  <button
    type="button"
    className="pastilla opinion-negativa"
    aria-pressed={filtroOpinionNegativa}
    onClick={() => setFiltroOpinionNegativa(v => !v)}
  >
    {filtroOpinionNegativa && <Check size={14} aria-hidden="true" />}
    Opinión negativa <span className="pastilla-num rojo">{totalOpinionNegativa}</span>
  </button>
  {muestraPorRevisar && (
    <button
      type="button"
      className="pastilla por-revisar"
      aria-pressed={filtroPorRevisar}
      onClick={() => setFiltroPorRevisar(v => !v)}
      data-tooltip="Pasos que el contador terminó y le falta revisar a Pedro"
    >
      {filtroPorRevisar ? <Check size={14} aria-hidden="true" /> : <Clock size={14} aria-hidden="true" />}
      Por revisar <span className="pastilla-num">{totalPorRevisar}</span>
    </button>
  )}
  {(totalComplementarias > 0 || filtroComplementarias) && (
    <button
      type="button"
      className="pastilla complementarias"
      aria-pressed={filtroComplementarias}
      onClick={() => setFiltroComplementarias(v => !v)}
      data-tooltip="Declaraciones presentadas en $0 de forma provisional: falta la complementaria"
    >
      {filtroComplementarias ? <Check size={14} aria-hidden="true" /> : <FileClock size={14} aria-hidden="true" />}
      Complementarias <span className="pastilla-num">{totalComplementarias}</span>
    </button>
  )}
  <span className="filtros-separador" aria-hidden="true" />
  {user?.rol !== 'contador' && (
    <FiltroMenu
      etiqueta="Contador"
      opciones={opcionesContador.filter(o => o.valor !== String(user?.id) || !esAdministrador(user))}
      destacada={esAdministrador(user) ? { valor: String(user?.id), texto: 'Mis clientes', detalle: detalleContador(String(user?.id)) } : null}
      seleccion={filtroContador}
      onCambiar={setFiltroContador}
    />
  )}
  <FiltroMenu
    etiqueta="Régimen"
    opciones={Object.entries(REGIMENES).map(([valor, texto]) => ({ valor, texto, detalle: textoCuantosClientes(clientes.filter(c => c.regimen === valor).length) }))}
    seleccion={filtroRegimen}
    onCambiar={setFiltroRegimen}
  />
  <FiltroMenu
    etiqueta="Industria"
    textoTodos="Todas"
    opciones={Object.entries(INDUSTRIAS).map(([valor, texto]) => ({ valor, texto, detalle: textoCuantosClientes(clientes.filter(c => c.industria === valor).length) }))}
    seleccion={filtroIndustria}
    onCambiar={setFiltroIndustria}
  />
 </div>
 )}


{/* C-027: buscar no esconde a los inactivos */}
{esAdministrador(user) && !verInactivos && textoBusqueda && inactivosQueCoinciden.length > 0 && (
  <p className="aviso-inactivos" role="status">
    {inactivosQueCoinciden.length === 1 ? '1 cliente inactivo coincide' : `${inactivosQueCoinciden.length} clientes inactivos coinciden`} con "{busqueda.trim()}".{' '}
    <button type="button" className="btn-link" onClick={() => setVerInactivos(true)}>Ver inactivos</button>
  </p>
)}

{/* RESUMEN (C-025): cuántos se ven y, a la derecha, las ayudas */}
{viewMode === 'mensual' && (
  <div className="filtros-resumen">
    {!verInactivos && <>
    <span className="filtros-conteo">
      Mostrando <b>{clientesFiltrados.length}</b> de {clientes.length} {clientes.length === 1 ? 'cliente' : 'clientes'}
      {(filtroAtrasados || filtroPorVencer) && ' · los más urgentes primero'}
    </span>
    {filtrosActivos.length > 0 && (
      <button type="button" className="filtros-limpiar" onClick={limpiarFiltros}>Limpiar filtros</button>
    )}
    </>}
    <div className="resumen-ayudas">
      {esAdministrador(user) && (
        <button
          type="button"
          className="pastilla inactivos"
          aria-pressed={verInactivos}
          onClick={() => setVerInactivos(v => !v)}
          data-tooltip="Clientes desactivados: verlos y reactivarlos"
        >
          {verInactivos && <Check size={14} aria-hidden="true" />}
          Inactivos <span className="pastilla-num">{clientesInactivos.length}</span>
        </button>
      )}
      <div className="guia-wrap">
        <button
          type="button"
          className="leyenda-btn"
          aria-expanded={showGuiaMes}
          aria-controls="guia-mes"
          onClick={() => setShowGuiaMes(v => !v)}
        >
          <CircleHelp size={16} aria-hidden="true" /> ¿Cómo funciona el mes?
        </button>
        {showGuiaMes && (
          <>
            <div className="menu-overlay" onClick={() => setShowGuiaMes(false)} />
            <div id="guia-mes" className="leyenda-panel guia-mes" role="dialog" aria-labelledby="guia-mes-titulo">
              <BotonIcono className="guia-cerrar" etiqueta="Cerrar" onClick={() => setShowGuiaMes(false)}><X size={16} aria-hidden="true" /></BotonIcono>
              <h4 id="guia-mes-titulo">Cómo funciona el mes</h4>
              <p>Cada cliente pasa por estos pasos en orden. Un paso no empieza hasta que se termina el anterior: lo que sigue se ve punteado (bloqueado), no atrasado. La declaración es la excepción: se puede presentar en ceros aunque falten pasos, para evitar la multa.</p>
              <ol>
                {pasosDelEmbudo.map(paso => (
                  <li key={paso.slug}>
                    <b>{nombreVisibleProceso(paso.proceso)}.</b> {INFO_PASOS[paso.slug]?.que} {INFO_PASOS[paso.slug]?.quien}
                  </li>
                ))}
                {procesoOpinion && <li><b>{nombreVisibleProceso(procesoOpinion)}.</b> {INFO_PASOS.opinion.que} {INFO_PASOS.opinion.quien}</li>}
              </ol>
              <p>
                <span className="atraso-badge atraso-badge-leyenda">+3d</span> el paso actual ya pasó su fecha.{' '}
                <span className="vence-badge vence-badge-leyenda"><Hourglass size={10} aria-hidden="true" />2d</span> vence en 3 días o menos.
              </p>
            </div>
          </>
        )}
      </div>
    <div className="leyenda-wrap">
      <button
        type="button"
        className="leyenda-btn"
        aria-expanded={showLeyenda}
        aria-controls="leyenda-panel"
        onClick={() => setShowLeyenda(v => !v)}
      >
        <Info size={16} aria-hidden="true" /> Leyenda
      </button>
      {showLeyenda && (
        <>
          <div className="menu-overlay" onClick={() => setShowLeyenda(false)} />
          <div
            id="leyenda-panel"
            className="leyenda-panel"
            role="dialog"
            aria-label="Leyenda"
          >
            <h4>Estado</h4>
            <ul>
              {Object.entries(NOMBRES_ESTADO).filter(([clave]) => clave !== 'revisado').map(([clave, nombre]) => (
                <li key={clave} className="legend-item">
                  <span className={`estado-circle ${clave}`} aria-hidden="true"><IconoEstado clave={clave} size={11} /></span>
                  {nombre}
                </li>
              ))}
            </ul>
            <h4>Revisión de Pedro</h4>
            <ul>
              {Object.entries(NOMBRES_SELLO).filter(([tipo]) => tipo !== 'complementaria').map(([tipo, nombre]) => (
                <li key={tipo} className="legend-item">
                  <span className="estado-circulo-wrap">
                    <span className="estado-circle completado" aria-hidden="true"><IconoEstado clave="completado" size={11} /></span>
                    <SelloRevision tipo={tipo} />
                  </span>
                  {nombre}
                </li>
              ))}
            </ul>
            <h4>Presentada en $0</h4>
            <ul>
              <li className="legend-item">
                <span className="estado-circulo-wrap">
                  <span className="estado-circle presentado-cero" aria-hidden="true"><IconoEstado clave="presentado-cero" size={11} /></span>
                  <SelloRevision tipo="complementaria" />
                </span>
                {NOMBRES_SELLO.complementaria}: el cliente no pagó o falta información
              </li>
            </ul>
            <h4>Opinión</h4>
            <ul>
              <li><span className="opinion-pill sin-sacar leyenda">Sin sacar</span></li>
              <li><span className="opinion-pill positiva leyenda">Positiva</span></li>
              <li><span className="opinion-pill negativa leyenda">Negativa</span></li>
            </ul>
            <h4>Alerta</h4>
            <ul>
              <li className="legend-item"><span className="atraso-badge atraso-badge-leyenda">+Nd</span>Días de atraso</li>
            <li className="legend-item"><span className="vence-badge vence-badge-leyenda"><Hourglass size={10} aria-hidden="true" />Nd</span>Días para que venza (3 o menos)</li>
            </ul>
          </div>
        </>
      )}
    </div>
    </div>
  </div>
)}

{/* C-028: franja del contador en un mes pasado (solo lectura, solicitud o acceso temporal) */}
{user?.rol === 'contador' && !accesoMes.esMesTrabajo && (() => {
  const sol = accesoMes.solicitud;
  const clave = estadoFranja(sol, new Date(relojPermiso));
  const mes = mesElegidoTexto();
  const Mes = mesElegidoTitulo();
  const contenido = {
    lectura: { icono: <Eye size={18} aria-hidden="true" />, titulo: `${Mes} · solo lectura`, texto: 'Puedes revisar todo. Para corregir algo de este mes, pide acceso por 24 horas.',
      boton: <button type="button" className="franja-btn principal" onClick={() => pedirAcceso()}>Pedir acceso…</button> },
    pendiente: { icono: <Clock size={18} aria-hidden="true" />, titulo: `Pediste acceso a ${mes}`, texto: `Esperando respuesta de Pedro o Agustín · lo pediste ${fechaHora(sol?.fecha_solicitud)}.`,
      boton: <button type="button" className="franja-btn" onClick={cancelarSolicitudPermiso}>Cancelar solicitud</button> },
    acceso: { icono: <KeyRound size={18} aria-hidden="true" />, titulo: `Puedes editar ${mes} hasta ${fechaHora(sol?.fecha_expiracion)}`, texto: `Acceso temporal aprobado por ${sol?.respondio || 'quien administra'}. Lo que cambies queda registrado.` },
    'por-vencer': { icono: <TriangleAlert size={18} aria-hidden="true" />, titulo: `Tu acceso a ${mes} vence ${textoMinutos(minutosRestantes(sol?.fecha_expiracion, new Date(relojPermiso)))}`, texto: `Termina ${fechaHora(sol?.fecha_expiracion)}. Si necesitas más tiempo, pide acceso otra vez cuando venza.` },
    vencida: { icono: <Eye size={18} aria-hidden="true" />, titulo: sol?.estado === 'quitado' ? `${sol?.quitado_por || 'Quien administra'} quitó tu acceso a ${mes}` : `Tu acceso a ${mes} venció`,
      texto: `Terminó ${fechaHora(sol?.quitado_en || sol?.fecha_expiracion)}. ${Mes} vuelve a solo lectura.`,
      boton: <button type="button" className="franja-btn" onClick={() => pedirAcceso()}>Pedir acceso otra vez…</button> },
    rechazada: { icono: <Eye size={18} aria-hidden="true" />, titulo: `${sol?.respondio || 'Quien administra'} no aprobó tu acceso a ${mes}`,
      texto: sol?.nota ? `"${sol.nota}"` : 'No dejó motivo. Si lo necesitas, habla con quien lo revisó o pide otra vez.',
      boton: <button type="button" className="franja-btn" onClick={() => pedirAcceso()}>Pedir acceso otra vez…</button> },
  }[clave];
  return (
    <div className={`franja-permiso ${clave}`} role="status">
      {contenido.icono}
      <div className="franja-texto"><b>{contenido.titulo}</b> {contenido.texto}</div>
      {contenido.boton}
    </div>
  );
})()}
</>
)}

      {/* MODAL HISTORIAL */}
      {showHistory && (
        <div className="modal-overlay" onClick={()=>setShowHistory(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3><ClipboardList size={20} aria-hidden="true" /> Historial de cambios{filtroHistorial ? ` · ${filtroHistorial.titulo}` : ''}</h3>
              <BotonIcono onClick={()=>setShowHistory(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
            </div>
            {filtroHistorial && (
              <p className="historial-filtro">
                Solo los cambios de ese contador en ese mes. <button type="button" className="btn-link" onClick={() => cargarHistorial()}>Ver todo el historial</button>
              </p>
            )}
            <div className="historial-list">
              {historial.length===0?<p>No hay cambios registrados</p>:historial.map(h=> h.tipo === 'permiso' ? (
                // C-028: solicitudes de acceso a meses pasados
                <div key={h.id} className="historial-item historial-permiso">
                  <div className="hist-main">
                    <KeyRound size={14} aria-hidden="true" />{' '}
                    {h.evento === 'solicitud' && <>{h.usuario_nombre} pidió acceso a {nombreMesPermiso(h.mes - 1, h.anio)}{h.motivo ? `: "${h.motivo}"` : ''}</>}
                    {h.evento === 'aprobacion' && <>{h.usuario_nombre} aprobó el acceso de {h.contador_nombre} a {nombreMesPermiso(h.mes - 1, h.anio)} por 24 horas</>}
                    {h.evento === 'rechazo' && <>{h.usuario_nombre} no aprobó el acceso de {h.contador_nombre} a {nombreMesPermiso(h.mes - 1, h.anio)}{h.nota ? `: "${h.nota}"` : ''}</>}
                    {h.evento === 'quitado' && <>{h.usuario_nombre} quitó el acceso de {h.contador_nombre} a {nombreMesPermiso(h.mes - 1, h.anio)}</>}
                  </div>
                  <div className="hist-meta">{fechaHora(h.fecha)}</div>
                </div>
              ) : (
                <div key={h.id} className="historial-item">
                  <div className="hist-main"><strong>{h.cliente_nombre}</strong> - {nombreVisibleProceso(h.proceso_nombre)}</div>
                  <div className="hist-change">
                    <span className={`hist-estado ${claveVisibleDeGuardado(h.estado_anterior) || 'sin-estado'}`}>{nombreEstadoGuardado(h.estado_anterior)}</span> <ArrowRight size={14} role="img" aria-label="cambió a" /> 
                    <span className={`hist-estado ${claveVisibleDeGuardado(h.estado_nuevo) || 'sin-estado'}`}>{nombreEstadoGuardado(h.estado_nuevo)}</span>
                    {h.motivo_cero && <span className="hist-motivo"> · {MOTIVOS_CERO.find(m => m.valor === h.motivo_cero)?.pendiente
                      ? `Complementaria pendiente: ${MOTIVOS_CERO.find(m => m.valor === h.motivo_cero).pendiente}`
                      : 'Sin movimientos'}</span>}
                  </div>
                  <div className="hist-meta">
                    <User size={14} aria-hidden="true" /> {h.usuario_nombre} |  {new Date(h.fecha).toLocaleString('es-MX')}
                    {h.con_acceso_temporal && <span className="etiqueta-acceso">con acceso temporal</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* DOCUMENTOS DEL MES (C-015) */}
      {docsMes && (
        <DocumentosMes
          cliente={docsMes}
          mes={selectedMes + 1}
          anio={selectedAnio}
          api={apiDocumentos}
          onCambio={alCambiarDocumentos}
          onCerrar={() => setDocsMes(null)}
          onVerAnio={() => { const c = docsMes; setDocsMes(null); abrirProcesosDetalle(c.id, 'documentos'); }}
        />
      )}

     {/* MODAL MENSAJES */}
{showMessages && (
  <div className="modal-overlay" onClick={()=>setShowMessages(false)}>
    <div className="modal modal-messages-chat" onClick={e=>e.stopPropagation()}>
      <div className="modal-header">
        <h3><MessageSquare size={20} aria-hidden="true" /> Mensajes</h3>
        <BotonIcono onClick={()=>{setShowMessages(false); setConversacionActiva(null);}} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
      </div>

      <div className="chat-container">
        {/* LISTA DE CONVERSACIONES */}
        <div className="conversations-list">
          <div className="conversations-header">
            <h4>Conversaciones</h4>
          </div>
          
          {conversaciones.length === 0 ? (
            <div className="empty-conversations">
              <p>No tienes conversaciones</p>
              <small>Selecciona un usuario para iniciar</small>
            </div>
          ) : (
            conversaciones.map(conv => (
              <div 
                key={conv.otro_usuario_id} 
                className={`conversation-item ${conversacionActiva === conv.otro_usuario_id ? 'active' : ''} ${conv.no_leidos > 0 ? 'has-unread' : ''}`}
                onClick={() => cargarMensajesConversacion(conv.otro_usuario_id)}
              >
                <div className="conv-avatar">
                  {conv.otro_usuario_nombre.charAt(0).toUpperCase()}
                </div>
                <div className="conv-info">
                  <div className="conv-name">{conv.otro_usuario_nombre}</div>
                  <div className="conv-last-message">
                    {conv.ultimo_mensaje?.substring(0, 30)}
                    {conv.ultimo_mensaje?.length > 30 ? '...' : ''}
                  </div>
                </div>
                <div className="conv-meta">
                  {conv.no_leidos > 0 && (
                    <span className="conv-badge">{conv.no_leidos}</span>
                  )}
                  <span className="conv-time">
                    {new Date(conv.ultima_fecha).toLocaleDateString('es-MX', {day: 'numeric', month: 'short'})}
                  </span>
                </div>
              </div>
            ))
          )}

          {/* LISTA DE USUARIOS DISPONIBLES */}
          <div className="users-available">
            <h5>Iniciar conversación</h5>
            {usuarios
              .filter(u => u.id !== user.id && !conversaciones.find(c => c.otro_usuario_id === u.id))
              .map(u => (
                <div 
                  key={u.id} 
                  className="user-item"
                  onClick={() => {
                     cargarMensajesConversacion(u.id);
                     setNewMessage({ para_usuario_id: u.id, mensaje: '', cliente_id: '', mensaje_padre_id: null });
                      }}
                >
                  <div className="user-avatar">{u.nombre.charAt(0).toUpperCase()}</div>
                  <div className="user-name">{u.nombre}</div>
                </div>
              ))
            }
          </div>
        </div>

        {/* CHAT ACTIVO */}
        <div className="chat-area">
          {conversacionActiva ? (
            <>
              <div className="chat-header">
                <BotonIcono className="btn-back" onClick={() => setConversacionActiva(null)} etiqueta="Volver"><ArrowLeft size={18} aria-hidden="true" /></BotonIcono>
                <div className="chat-user-info">
                  <div className="chat-avatar">
                    {usuarios.find(u => u.id === conversacionActiva)?.nombre.charAt(0).toUpperCase()}
                  </div>
                  <div className="chat-user-name">
                    {usuarios.find(u => u.id === conversacionActiva)?.nombre}
                  </div>
                </div>
              </div>

                <div className="chat-messages">
      {cargandoMensajes ? (
    <div className="loading-chat">
      <div className="spinner"></div>
      <p>Cargando mensajes...</p>
    </div>
  ) : mensajesConversacion.length === 0 ? (
    <div className="empty-chat">
      <p>No hay mensajes aún</p>
      <small>Envía el primer mensaje</small>
    </div>
  ) : (
                  mensajesConversacion.map(m => {
                    const esMio = m.de_usuario_id === user.id;
                    return (
                      <div key={m.id} className={`chat-message ${esMio ? 'message-sent' : 'message-received'}`}>
                        {m.mensaje_padre_id && (
                          <div className="mensaje-padre">
                            <small><Reply size={14} aria-hidden="true" /> Responde a:</small>
                            <p>"{m.mensaje_padre_texto?.substring(0, 60)}..."</p>
                          </div>
                        )}
                        
                        <div className="message-bubble">
                          <div className="message-text">{m.mensaje}</div>
                          <div className="message-time">
                            {new Date(m.fecha).toLocaleTimeString('es-MX', {hour: '2-digit', minute: '2-digit'})}
                          </div>
                        </div>

                        {!esMio && (
                          <BotonIcono 
                            onClick={() => responderMensaje(m)} 
                            className="btn-reply-mini"
                           
                            etiqueta="Responder"
                          >
                            <Reply size={16} aria-hidden="true" />
                          </BotonIcono>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {respondiendo && (
                <div className="reply-indicator-chat">
                  <div className="reply-info">
                    <small>Respondiendo a:</small>
                    <p>"{respondiendo.mensaje.substring(0, 40)}..."</p>
                  </div>
                  <BotonIcono onClick={cancelarRespuesta} className="btn-cancel-reply" etiqueta="Cancelar respuesta"><X size={20} aria-hidden="true" /></BotonIcono>
                </div>
              )}

               <form onSubmit={enviarMensaje} className="chat-input-form">
  <div className="input-wrapper">
    <textarea 
      {...propsCampo('mensaje-texto')}
      placeholder="Escribe un mensaje..." 
      value={newMessage.mensaje} 
      onChange={e => setNewMessage({...newMessage, mensaje: e.target.value})}
      onKeyPress={e => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          enviarMensaje(e);
        }
      }}
      required
    />
  </div>
  <BotonIcono type="submit" etiqueta={respondiendo ? "Enviar respuesta" : "Enviar mensaje"}>
    {respondiendo ? <Reply size={18} aria-hidden="true" /> : <Send size={18} aria-hidden="true" />}
  </BotonIcono>
  {errorCampo('mensaje-texto')}
</form>
            </>
          ) : (
            <div className="no-chat-selected">
              <div className="admin-notes-container">
                <div className="admin-notes-header">
                  <div className="admin-notes-icon"><NotebookPen size={20} aria-hidden="true" /></div>
                  <h4>Notas de actividades</h4>
                  <p>{puedeEditarNotas ? 'Escribe aquí tus notas sobre las actividades del día' : 'Notas compartidas por el administrador'}</p>
                </div>

                {puedeEditarNotas ? (
                  <>
                    <textarea
                      className="admin-notes-textarea"
                      placeholder="Escribe tus notas aquí..."
                      value={notasAdmin}
                      onChange={(e) => setNotasAdmin(e.target.value)}
                    />
                    <button
                      className="btn-guardar-notas"
                      onClick={guardarNotas}
                      disabled={guardandoNotas}
                    >
                      {guardandoNotas ? ' Guardando...' : 'Guardar notas'}
                    </button>
                  </>
                ) : (
                  <div className="admin-notes-readonly">
                    {notasAdmin ? (
                      <div className="notes-content">{notasAdmin}</div>
                    ) : (
                      <div className="notes-empty">No hay notas disponibles</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
)}
      {/* MODAL AJUSTES */}
      {showSettings && (
        <div className="modal-overlay" onClick={()=>setShowSettings(false)}>
          <div className="modal modal-settings" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3>{settingsGrupo === 'despacho'
                ? <><Users size={20} aria-hidden="true" /> Despacho</>
                : <><User size={20} aria-hidden="true" /> Mi cuenta</>}</h3>
              <BotonIcono onClick={()=>setShowSettings(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
            </div>

            <div className="settings-tabs">
              {settingsGrupo === 'personal' && <button className={settingsTab==='perfil'?'active':''} onClick={()=>setSettingsTab('perfil')}> Mi perfil</button>}
              {settingsGrupo === 'personal' && <button className={settingsTab==='password'?'active':''} onClick={()=>setSettingsTab('password')}> Cambiar contraseña</button>}
              {settingsGrupo === 'despacho' && esAdministrador(user) && <button className={settingsTab==='usuarios'?'active':''} onClick={()=>setSettingsTab('usuarios')}> Usuarios</button>}
              {settingsGrupo === 'despacho' && esAdministrador(user) && <button className={settingsTab==='respaldo'?'active':''} onClick={()=>setSettingsTab('respaldo')}> Respaldo de archivos</button>}
            </div>

            <div className="settings-content">
              {settingsTab==='perfil' && (
                <form onSubmit={actualizarPerfil} className="settings-form">
                  <h4>Datos del perfil</h4>
                  <div className="form-group">
                    <label>Nombre completo</label>
                    <input type="text" value={perfilData.nombre} onChange={e=>setPerfilData({...perfilData, nombre:e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" value={perfilData.email} onChange={e=>setPerfilData({...perfilData, email:e.target.value})} placeholder="correo@dominio.com" />
                    <small>Se usará como reply-to en las notificaciones enviadas a tus clientes.</small>
                  </div>
                  <div className="form-group">
                    <label>Usuario</label>
                    <input type="text" value={user.username} disabled style={{background:'var(--slate-100)', cursor:'not-allowed'}} />
                    <small>El nombre de usuario no se puede cambiar</small>
                  </div>
                  <div className="form-group">
                    <label>Rol</label>
                    <input type="text" value={nombreRol(user.rol)} disabled style={{background:'var(--slate-100)', cursor:'not-allowed'}} />
                  </div>
                  <button type="submit" className="btn-primary">Guardar cambios</button>
                </form>
              )}

              {settingsTab==='password' && (
                <form onSubmit={cambiarPassword} className="settings-form">
                  <h4>Cambiar contraseña</h4>
                  <div className="form-group">
                    <label>Contraseña actual</label>
                    <input type="password" value={passwordData.password_actual} onChange={e=>setPasswordData({...passwordData, password_actual:e.target.value})} required />
                  </div>
                  <div className="form-group">
                    <label>Nueva contraseña</label>
                    <input type="password" {...propsCampo('password-nueva')} value={passwordData.password_nueva} onChange={e=>setPasswordData({...passwordData, password_nueva:e.target.value})} required minLength="6" />
                    {errorCampo('password-nueva') || <small>Mínimo 6 caracteres</small>}
                  </div>
                  <div className="form-group">
                    <label>Confirmar nueva contraseña</label>
                    <input type="password" {...propsCampo('password-confirmar')} value={passwordData.password_confirmar} onChange={e=>setPasswordData({...passwordData, password_confirmar:e.target.value})} required minLength="6" />
                    {errorCampo('password-confirmar')}
                  </div>
                  <button type="submit" className="btn-primary">Cambiar contraseña</button>
                </form>
              )}

              {settingsTab==='usuarios' && esAdministrador(user) && (
                <div className="users-management">
                  <div className="users-section">
                    <h4><Plus size={16} aria-hidden="true" /> Crear nuevo usuario</h4>
                    <form onSubmit={crearUsuario} className="user-form">
                      <input type="text" placeholder="Usuario (ej: juan.perez)" value={newUserData.username} onChange={e=>setNewUserData({...newUserData, username:e.target.value})} required />
                      <input type="password" placeholder="Contraseña (mín 6 caracteres)" value={newUserData.password} onChange={e=>setNewUserData({...newUserData, password:e.target.value})} required minLength="6" />
                      <input type="text" placeholder="Nombre completo" value={newUserData.nombre} onChange={e=>setNewUserData({...newUserData, nombre:e.target.value})} required />
                      <input type="email" placeholder="Email (reply-to)" value={newUserData.email} onChange={e=>setNewUserData({...newUserData, email:e.target.value})} />
                      <select value={newUserData.rol} onChange={e=>setNewUserData({...newUserData, rol:e.target.value})}>
                        {ROLES_ASIGNABLES.filter(rol => puedeGestionarRol(user, rol)).map(rol => <option key={rol} value={rol}>{nombreRol(rol)}</option>)}
                      </select>
                      <button type="submit" className="btn-success">Crear usuario</button>
                    </form>
                  </div>

                  <div className="users-section">
                    <h4><Users size={16} aria-hidden="true" /> Usuarios existentes</h4>
                    <div className="users-list">
                      {usuarios.map(u=>(
                        <div key={u.id} className={`user-card ${!u.activo?'inactive':''}`}>
                          {editingUser && editingUser.id===u.id ? (
                            <form onSubmit={editarUsuario} className="edit-user-form">
                              <input type="text" value={editingUser.nombre} onChange={e=>setEditingUser({...editingUser, nombre:e.target.value})} placeholder="Nombre" />
                              <input type="text" value={editingUser.username} onChange={e=>setEditingUser({...editingUser, username:e.target.value})} placeholder="Usuario" />
                              <input type="email" value={editingUser.email || ''} onChange={e=>setEditingUser({...editingUser, email:e.target.value})} placeholder="Email (reply-to)" />
                              <select value={editingUser.rol} onChange={e=>setEditingUser({...editingUser, rol:e.target.value})} aria-label="Rol">
                                {[...ROLES_ASIGNABLES.filter(rol => puedeGestionarRol(user, rol)), ...(ROLES_ASIGNABLES.includes(editingUser.rol) ? [] : [editingUser.rol])].map(rol => (
                                  <option key={rol} value={rol}>{nombreRol(rol)}</option>
                                ))}
                              </select>
                              <input type="password" placeholder="Nueva contraseña (opcional)" onChange={e=>setEditingUser({...editingUser, password:e.target.value})} />
                              <div className="edit-actions">
                                <button type="submit" className="btn-small btn-success"> Guardar</button>
                                <button type="button" onClick={()=>setEditingUser(null)} className="btn-small"> Cancelar</button>
                              </div>
                            </form>
                          ) : (
                            <>
                              <div className="user-info-card">
                                <div className="user-name">
                                  {u.nombre}
                                  {u.id===user.id && <span className="badge-you">Tú</span>}
                                  {!u.activo && <span className="badge-inactive">Inactivo</span>}
                                </div>
                                <div className="user-meta">
                                  @{u.username.length > 10 ? u.username.slice(0, 10) + '…' : u.username} | {' '}<User size={14} aria-hidden="true" /> {nombreRol(u.rol)}
                                </div>
                              </div>
                              {u.id!==user.id && puedeGestionarRol(user, u.rol) && (
                                <div className="user-actions">
                                  <BotonIcono onClick={() => pedirConfirmacionPass(() => setEditingUser({...u, password:''}))} className="btn-icon" etiqueta="Editar"><Pencil size={16} aria-hidden="true" /></BotonIcono>
                                  <BotonIcono onClick={()=>toggleActivoUsuario(u.id)} className="btn-icon" etiqueta={`${u.activo===1?'Desactivar':'Activar'} a ${u.nombre}`}>
                                    {u.activo===1?<Ban size={16} aria-hidden="true" />:<CircleCheck size={16} aria-hidden="true" />}
                                  </BotonIcono>
                                  <button onClick={()=>eliminarUsuario(u.id)} className="btn-icon btn-danger"><Trash2 size={16} aria-hidden="true" /> Eliminar</button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {settingsTab==='respaldo' && esAdministrador(user) && (
                <div className="backup-section">
                  <h4>Respaldo de archivos</h4>
                  <p style={{color:'var(--slate-500)', marginBottom:'12px', fontSize:'14px'}}>
                    Descarga un ZIP con todos los archivos e imágenes.
                  </p>
                  <button
                    onClick={() => pedirConfirmacionPass(descargarRespaldo)}
                    className="btn-primary"
                    disabled={descargandoRespaldo}
                    style={{width:'100%'}}
                  >
                    {descargandoRespaldo ? ' Generando ZIP, por favor espera...' : ' Descargar respaldo ZIP'}
                  </button>
                  {descargandoRespaldo && (
                    <p style={{color:'var(--slate-400)', fontSize:'13px', marginTop:'8px', textAlign:'center'}}>
                      Esto puede tardar unos minutos dependiendo de la cantidad de archivos.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

           {/* MODAL NUEVO CLIENTE */}
{showNewClient && (
  <FormularioCliente
    modo="nuevo"
    inicial={newClient}
    responsables={usuarios.filter(u => veClientes(u) && u.activo)}
    onGuardar={crearCliente}
    onCerrar={() => setShowNewClient(false)}
  />
)}

{/* MODAL CONFIRMACIÓN DE CONTRASEÑA */}
{showConfirmPass && (
  <div className="modal-overlay" onClick={() => setShowConfirmPass(false)}>
    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 340 }}>
      <div className="modal-header">
        <h3>Confirmar contraseña</h3>
        <BotonIcono onClick={() => setShowConfirmPass(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
      </div>
      <div style={{ padding: '16px 20px 20px' }}>
        <p style={{ fontSize: 14, color: 'var(--slate-600)', marginBottom: 16 }}>Ingresa tu contraseña para continuar.</p>
        <input
          type="password"
          autoFocus
          placeholder="Tu contraseña"
          value={confirmPassValue}
          onChange={e => { setConfirmPassValue(e.target.value); setConfirmPassError(''); }}
          onKeyDown={e => e.key === 'Enter' && ejecutarConConfirmacion()}
          style={{ width: '100%', padding: '8px 12px', border: `1px solid ${confirmPassError ? 'var(--red-600)' : 'var(--slate-200)'}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box', marginBottom: 6 }}
        />
        {confirmPassError && <p style={{ color: 'var(--red-600)', fontSize: 12, marginBottom: 8 }}>{confirmPassError}</p>}
        <button
          onClick={ejecutarConConfirmacion}
          style={{ width: '100%', marginTop: 8, padding: '8px', background: 'var(--morado-700)', color: 'var(--blanco)', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
        >
          Confirmar
        </button>
      </div>
    </div>
  </div>
)}

{/* PICKER: SELECCIONAR CLIENTE PARA EDITAR */}
{showPickerEditar && (
  <div className="modal-overlay" onClick={() => setShowPickerEditar(false)}>
    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
      <div className="modal-header">
        <h3>Seleccionar cliente</h3>
        <BotonIcono onClick={() => setShowPickerEditar(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
      </div>
      <div style={{ padding: '0 20px 12px' }}>
        <input
          type="text"
          autoFocus
          placeholder="Buscar cliente..."
          value={busquedaPicker}
          onChange={e => setBusquedaPicker(e.target.value)}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--slate-200)', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
        />
      </div>
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {(currentRoute === '/cobranza' ? cobranzaTodosClientes : clientes)
          .filter(c => (c.nombre || '').toLowerCase().includes(busquedaPicker.toLowerCase()))
          .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }))
          .map(c => (
            <div
              key={c.id}
              onClick={() => {
                abrirEditarCliente(c);
                setShowPickerEditar(false);
              }}
              style={{ padding: '10px 20px', cursor: 'pointer', borderBottom: '1px solid var(--slate-100)', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--slate-50)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}
            >
              <span style={{ color: c.activo === false ? 'var(--gray-400)' : 'var(--gray-900)' }}>{c.nombre}</span>
              {c.activo === false && <span style={{ fontSize: 11, color: 'var(--red-600)', fontWeight: 600 }}>Inactivo</span>}
            </div>
          ))
        }
      </div>
    </div>
  </div>
)}

{/* MODAL EDITAR CLIENTE */}
{showEditClient && editingClient && (
  <FormularioCliente
    key={editingClient.id}
    modo="editar"
    inicial={editingClient}
    responsables={usuarios.filter(u => veClientes(u) && u.activo)}
    onGuardar={actualizarCliente}
    onCerrar={() => { setShowEditClient(false); setEditingClient(null); }}
    zonaFinal={esAdministrador(user) && (
    <div className="zona-desactivar">
      {editingClient.activo === false ? (
        <p>
          {editingClient.desactivado_el ? `Inactivo desde el ${fechaCorta(editingClient.desactivado_el)}` : 'Inactivo'}: no aparece en la tabla mensual ni se le generan cobros.
        </p>
      ) : (
        <p>Si ya no trabajan con este cliente, desactívalo: conserva su historial y se puede reactivar.</p>
      )}
      <div className="zona-desactivar-botones">
        {editingClient.activo !== false ? (
          <button type="button" className="btn-desactivar" onClick={() => desactivarCliente(editingClient)}>
            Desactivar cliente…
          </button>
        ) : (
          <button type="button" className="btn-desactivar" onClick={() => reactivarCliente(editingClient)}>
            Reactivar cliente…
          </button>
        )}
        <button type="button" className="btn-eliminar" onClick={() => eliminarCliente(editingClient)}>
          Eliminar cliente…
        </button>
      </div>
    </div>
    )}
  />
)}

{/* MODAL NEUVOOOOOOOOOOOOOOOOOOOOOOOOOOOOO*/}
{/* C-023: la ficha es una página (/clientes/123) encima de la tabla, que se queda como estaba */}
{showProcesosDetalle && clienteSeleccionado && (
  <div className="ficha-pagina" role="region" aria-labelledby="ficha-titulo">
    <div className="ficha-contenido">
      <div className="ficha-cabecera">
        <button type="button" className="ficha-volver" onClick={cerrarFicha}>
          <ArrowLeft size={16} aria-hidden="true" /> Volver a la tabla
        </button>
        <div className="ficha-titulo-fila">
          <div>
            <h2 id="ficha-titulo">{clienteSeleccionado.nombre}</h2>
            <p className="ficha-sub">
              {[
                clienteSeleccionado.tipo === 'fisica' ? 'Persona física' : 'Persona moral',
                REGIMENES[clienteSeleccionado.regimen] || clienteSeleccionado.regimen,
                INDUSTRIAS[clienteSeleccionado.industria] || clienteSeleccionado.industria,
              ].filter(Boolean).join(' · ')}
              {clienteSeleccionado.contador_nombre && <> · <b>{clienteSeleccionado.contador_nombre}</b></>}
              {etiquetaRevision(clienteSeleccionado) && <span className="etiqueta-revision">{etiquetaRevision(clienteSeleccionado)}</span>}
            </p>
          </div>
          {esAdministrador(user) && (
            <button type="button" className="btn-herramienta" onClick={() => abrirEditarCliente(clientes.find(c => c.id === clienteSeleccionado.id) || clienteSeleccionado)}>
              <Pencil size={16} aria-hidden="true" /> Editar
            </button>
          )}
        </div>
      </div>

      {/* Pestañas de la ficha (C-015): Resumen · Documentos · Procesos */}
      <div className="procesos-tabs" role="tablist" aria-label="Ficha del cliente">
        {[['resumen', 'Resumen'], ['documentos', 'Documentos'], ['procesos', 'Procesos']].map(([valor, texto]) => (
          <button
            key={valor}
            type="button"
            role="tab"
            aria-selected={pestanaFicha === valor}
            className={pestanaFicha === valor ? 'active' : ''}
            onClick={() => setPestanaFicha(valor)}
          >
            {texto}
          </button>
        ))}
      </div>

      <div className="procesos-content" role="tabpanel" aria-labelledby="ficha-titulo">
        {pestanaFicha === 'documentos' ? (
          <DocumentosAnio cliente={clienteSeleccionado} anioInicial={selectedAnio} api={apiDocumentos} />
        ) : pestanaFicha === 'resumen' ? (
          // C-023: todo lo del cliente en un lugar (solo lectura)
          <ResumenCliente
            cliente={clientes.find(c => c.id === clienteSeleccionado.id) || clienteSeleccionado}
            procesos={procesos}
            fechasLimite={fechasLimite}
            periodo={periodoSeleccionado}
            ejercicio={ejercicio}
            obligaciones={obligacionesMes
              .map(o => ({ ...o, fila: o.clientes.find(c => c.id === clienteSeleccionado.id) }))
              .filter(o => o.fila)}
            api={apiResumen}
            verEnTabla={verClienteEnTabla}
            irDocumentos={() => setPestanaFicha('documentos')}
            Icono={IconoEstado}
            Sello={SelloRevision}
            notas={(
              <section className="res-bloque res-notas">
                <div className="historial-notas-section">
                  <div className="historial-notas-titulo">
                    <h3>
                      Notas
                      {comentariosCliente.length > 0 && (
                        <span className="historial-count-badge">
                          {comentariosCliente.length} {comentariosCliente.length === 1 ? 'nota' : 'notas'}
                        </span>
                      )}
                    </h3>
                  </div>

                  {puedeAgregarComentarios && (
                    <div className="comentario-form historial-form">
                      <textarea
                        {...propsCampo('comentario')}
                        value={nuevoComentario}
                        onChange={(e) => setNuevoComentario(e.target.value)}
                        placeholder="Agrega una nota sobre este cliente (ej: avisó que manda docs la próxima semana)..."
                        rows="3"
                        maxLength="2000"
                        disabled={guardandoComentario}
                      />
                      {errorCampo('comentario')}
                      <div className="comentario-form-footer">
                        <span className="char-count">{nuevoComentario.length}/2000</span>
                        <button
                          onClick={agregarComentario}
                          disabled={guardandoComentario || !nuevoComentario.trim()}
                          className="btn-agregar-comentario"
                        >
                          {guardandoComentario ? 'Guardando...' : 'Agregar nota'}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="comentarios-lista historial-lista">
                    {comentariosCliente.length === 0 ? (
                      <div className="comentarios-vacio">
                        <p>Sin notas registradas aún.</p>
                      </div>
                    ) : (
                      comentariosCliente.map(comentario => (
                        <div key={comentario.id} className="comentario-card">
                          <div className="comentario-header">
                            <div className="comentario-usuario">
                              <span className="usuario-nombre">{comentario.usuario_nombre}</span>
                              <span className={`usuario-badge ${comentario.usuario_rol}`}>
                                {nombreRol(comentario.usuario_rol)}
                              </span>
                            </div>
                            <div className="comentario-acciones">
                              <span className="comentario-fecha">
                                {new Date(comentario.fecha_creacion).toLocaleDateString('es-MX', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {(esAdministrador(user) || comentario.usuario_username === user?.username) && (
                                <button
                                  onClick={() => eliminarComentario(comentario.id)}
                                  className="btn-eliminar-comentario"
                                >
                                  <Trash2 size={16} aria-hidden="true" /> Eliminar
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="comentario-texto">
                            {comentario.comentario}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </section>
            )}
          />
        ) : (
          // Procesos: los pasos del mes y, si tiene, los procesos de su industria (lo que abría "Detalle")
          <>
            <h4 className="ficha-seccion">Mes de {meses[selectedMes].toLowerCase()} {selectedAnio}</h4>
            {pasosDelMesEnFicha().map(renderTarjetaProceso)}
            {Object.keys(procesosDetalle).filter(c => c !== 'General' && procesosDetalle[c]?.procesos?.length).map(categoria => (
              <React.Fragment key={categoria}>
                <h4 className="ficha-seccion">{categoria}</h4>
                {procesosDetalle[categoria].procesos.map(renderTarjetaProceso)}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      {/* Avance por categoría (C-023: una categoría sin procesos dice "Sin pasos asignados", no "0/0") */}
      {pestanaFicha === 'procesos' && (
      <div className="procesos-resumen">
        {Object.keys(procesosDetalle)
          .filter(categoria => categoria !== 'Comentarios' && !procesosDetalle[categoria].es_info_cliente)
          .map(categoria => {
            const total = procesosDetalle[categoria].procesos.length;
            const completados = procesosDetalle[categoria].procesos.filter(p => p.estado === 'verde' || p.estado === 'azul').length;
            const porcentaje = total ? Math.round((completados / total) * 100) : 0;

            return (
              <div key={categoria} className="resumen-card">
                <div className="resumen-nombre">{categoria}</div>
                {total === 0 ? (
                  <div className="resumen-progreso"><span className="sin-pasos">Sin pasos asignados</span></div>
                ) : (
                  <div className="resumen-progreso">
                    <div className="progreso-bar">
                      <div className="progreso-fill" style={{width: `${porcentaje}%`}}></div>
                    </div>
                    <span>{completados}/{total}</span>
                  </div>
                )}
              </div>
            );
          })}
      </div>
      )}
    </div>
  </div>
)}

 {/* ========== PANEL DE NOTIFICACIONES ========== */}
{showWhatsAppPanel && (
  <div className="modal-overlay" onClick={() => setShowWhatsAppPanel(false)}>
    <div className="modal modal-notificaciones" onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h3><Mail size={20} aria-hidden="true" /> Correos a clientes</h3>
        <BotonIcono onClick={() => setShowWhatsAppPanel(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
      </div>

      {/* TABS */}
      <div className="notifications-tabs">
        <button 
          className={notificacionesTab === 'enviar' ? 'active' : ''}
          onClick={() => setNotificacionesTab('enviar')}
        >
           Enviar
        </button>
        <button 
          className={notificacionesTab === 'contactos' ? 'active' : ''}
          onClick={() => setNotificacionesTab('contactos')}
        >
           Contactos
        </button>
        <button 
          className={notificacionesTab === 'programadas' ? 'active' : ''}
          onClick={() => setNotificacionesTab('programadas')}
        >
          Programadas
        </button>
        {esAdministrador(user) && (
          <button
            className={notificacionesTab === 'reporte' ? 'active' : ''}
            onClick={() => {
              setNotificacionesTab('reporte');
              cargarReporteEmails(30);
            }}
          >
             Reporte
          </button>
        )}
      </div>

      <div className="notifications-content">
        {/* TAB: ENVIAR NOTIFICACIONES */}
        {notificacionesTab === 'enviar' && (
          <div className="notification-send-section">
            {/* ENVÍOS AUTOMÁTICOS A TODOS */}
            <div className="section-card">
              <h4> Envíos masivos automáticos</h4>
              <p>Enviar a todos los clientes con email o teléfono configurado</p>
              
              <div className="massive-send-buttons">
                <button 
                  onClick={() => enviarNotificacionesManuales('solicitud_inicial')}
                  className="btn-massive btn-primary"
                >
                   Solicitud inicial (día 1)
                  <small>Solicitud completa de documentos</small>
                </button>

                <button 
                  onClick={() => enviarNotificacionesManuales('recordatorio_faltantes')}
                  className="btn-massive btn-warning"
                >
                  Recordatorio de faltantes (día 15)
                  <small>Solo documentos pendientes</small>
                </button>
              </div>
            </div>

            {/* ENVÍOS SELECTIVOS */}
            <div className="section-card">
              <h4>Envío selectivo</h4>
              <p>Selecciona clientes específicos para enviar notificaciones</p>

              {/* TIPO DE NOTIFICACIÓN */}
              <div className="form-group">
                <label>Tipo de notificación</label>
                <select 
                  value={envioManual.tipo}
                  onChange={e => setEnvioManual({...envioManual, tipo: e.target.value})}
                  className="select-tipo-notificacion"
                >
                  <option value="solicitud_inicial">Solicitud inicial de documentos</option>
                  <option value="recordatorio_faltantes">Recordatorio de documentos faltantes</option>
                  <option value="personalizado">Mensaje personalizado</option>
                </select>
              </div>

              {/* MENSAJE PERSONALIZADO */}
              {envioManual.tipo === 'personalizado' && (
                <div className="form-group">
                  <label>Mensaje personalizado</label>
                  <textarea 
                    {...propsCampo('envio-mensaje')}
                    placeholder="Escribe tu mensaje aquí..."
                    value={envioManual.mensaje_personalizado}
                    onChange={e => setEnvioManual({...envioManual, mensaje_personalizado: e.target.value})}
                    rows="4"
                    className="textarea-mensaje"
                  />
                  {errorCampo('envio-mensaje')}
                </div>
              )}

              {/* SELECCIÓN DE CLIENTES */}
              <div className="clientes-seleccion">
                <div className="seleccion-header">
                  <h5>Seleccionar clientes ({envioManual.clientes_seleccionados.length}/{clientes.length})</h5>
                  <button onClick={seleccionarTodosClientes} className="btn-select-all">
                    {envioManual.clientes_seleccionados.length === clientes.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
                  </button>
                </div>

                <div className="clientes-list-select">
                  {clientes.map(cliente => {
                    const tieneContacto = cliente.email || cliente.telefono;
                    const seleccionado = envioManual.clientes_seleccionados.includes(cliente.id);

                    return (
                      <div 
                        key={cliente.id} 
                        className={`cliente-select-item ${seleccionado ? 'selected' : ''} ${!tieneContacto ? 'sin-contacto' : ''}`}
                        onClick={() => tieneContacto && toggleClienteSeleccionado(cliente.id)}
                      >
                        <input 
                          type="checkbox"
                          checked={seleccionado}
                          disabled={!tieneContacto}
                          readOnly
                        />
                        <div className="cliente-select-info">
                          <strong>{cliente.nombre}</strong>
                          <div className="cliente-contactos">
                            {cliente.email && <span> {cliente.email}</span>}
                            {cliente.telefono && <span> {cliente.telefono}</span>}
                            {!tieneContacto && <span className="sin-contacto-text"><TriangleAlert size={14} aria-hidden="true" /> Sin contacto</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

             
              {errorCampo('envio-clientes')}
              <button 
                onClick={enviarNotificacionesSeleccionadas}
                disabled={enviandoNotificaciones || envioManual.clientes_seleccionados.length === 0}
                className="btn-enviar-notificaciones"
              >
                {enviandoNotificaciones 
                  ? <><LoaderCircle size={16} aria-hidden="true" className="icono-girando" /> Enviando... ({envioManual.clientes_seleccionados.length})</>
                  : <><Send size={16} aria-hidden="true" /> Enviar a {envioManual.clientes_seleccionados.length} cliente(s)</>}
              </button>
            </div>
          </div>
        )}

        {/* TAB: CONTACTOS */}
        {notificacionesTab === 'contactos' && (
          
          <div className="contactos-section">
            <div className="section-card">
              <h4>Gestión de contactos</h4>
              <p>Configura email y teléfono de cada cliente</p>

              <div className="contactos-stats">
                <div className="stat-card">
                  <div className="stat-number">{clientes.filter(c => c.email).length}</div>
                  <div className="stat-label"><Mail size={14} aria-hidden="true" /> Con email</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{clientes.filter(c => c.telefono).length}</div>
                  <div className="stat-label"><Smartphone size={14} aria-hidden="true" /> Con teléfono</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{clientes.filter(c => c.email && c.telefono).length}</div>
                  <div className="stat-label"><CircleCheck size={14} aria-hidden="true" /> Completos</div>
                </div>
              </div>

              <div className="contactos-list">
                {clientes.map(cliente => (
                  <div key={cliente.id} className="contacto-item">
                    <div className="contacto-nombre">
                      <strong>{cliente.nombre}</strong>
                      <span className="tipo-badge">{cliente.tipo === 'moral' ? <><Building2 size={14} aria-hidden="true" /> P.M.</> : <><User size={14} aria-hidden="true" /> P.F.</>}</span>
                    </div>
                    {esAdministrador(user) && (
                    <button 
                      onClick={() => {
                        setSelectedClientContact(cliente);
                        setContactData({ email: cliente.email || '', telefono: cliente.telefono || '' });
                        setShowEditContact(true);
                      }}
                      className="btn-editar-contacto"
                    >
                      <Pencil size={16} aria-hidden="true" /> Editar
                    </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}


        {notificacionesTab === 'reporte' && esAdministrador(user) && (
          <div className="reporte-section">
            <div className="section-card">
              <h4>Reporte de entrega de correos</h4>
              <p>Consulta envíos exitosos y fallidos para validar que las notificaciones sí se estén entregando.</p>

              <div className="reporte-actions">
                <button onClick={() => cargarReporteEmails(7)} className="btn-select-all">Últimos 7 días</button>
                <button onClick={() => cargarReporteEmails(30)} className="btn-select-all">Últimos 30 días</button>
                <button onClick={() => cargarReporteEmails(90)} className="btn-select-all">Últimos 90 días</button>
              </div>

              <div className="contactos-stats">
                <div className="stat-card">
                  <div className="stat-number">{reporteEmails.resumen.total}</div>
                  <div className="stat-label">Total ({reporteEmails.dias} días)</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{reporteEmails.resumen.enviados}</div>
                  <div className="stat-label">Entregados (API aceptó)</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{reporteEmails.resumen.fallidos}</div>
                  <div className="stat-label">Fallidos</div>
                </div>
              </div>

              {cargandoReporteEmails ? (
                <p>Cargando reporte...</p>
              ) : (
                <div className="clientes-list-select">
                  {reporteEmails.logs.length === 0 ? (
                    <p>No hay movimientos en este periodo.</p>
                  ) : (
                    reporteEmails.logs.map((log) => (
                      <div key={log.id} className={`reporte-email-item ${log.estado}`}>
                        <div>
                          <strong>{log.destinatario}</strong>
                          <div className="cliente-contactos">
                            <span>{log.asunto}</span>
                            <span>{new Date(log.fecha_envio).toLocaleString('es-MX')}</span>
                            {log.resend_message_id && <span>ID: {log.resend_message_id}</span>}
                          </div>
                          {log.error && <small className="sin-contacto-text">Error: {log.error}</small>}
                        </div>
                        <span className={`programada-status ${log.estado === 'enviado' ? 'activa' : 'inactiva'}`}>
                          {log.estado === 'enviado' ? 'Enviado' : 'Fallido'}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: PROGRAMADAS */}
        {notificacionesTab === 'programadas' && (
          <div className="programadas-section">
            <div className="section-card">
              <h4>Notificaciones programadas</h4>
              <p>El sistema envía automáticamente notificaciones en fechas específicas</p>

              <div className="programadas-list">
                <div className="programada-card">
                  <div className="programada-icon"><Calendar size={20} aria-hidden="true" /></div>
                  <div className="programada-info">
                    <h5>Solicitud inicial de documentos</h5>
                    <p>Día 1 de cada mes a las 8:00 AM (hora de México)</p>
                    <div className="programada-detalles">
                      <span><Mail size={14} aria-hidden="true" /> Correo</span>
                      <span><Users size={14} aria-hidden="true" /> Todos los clientes activos con contacto</span>
                    </div>
                  </div>
                  <div className="programada-status activa">Activa</div>
                </div>

                <div className="programada-card">
                  <div className="programada-icon"><TriangleAlert size={20} aria-hidden="true" /></div>
                  <div className="programada-info">
                    <h5>Recordatorio de documentos faltantes</h5>
                    <p>Día 15 de cada mes a las 8:00 AM (hora de México)</p>
                    <div className="programada-detalles">
                      <span><Mail size={14} aria-hidden="true" /> Correo</span>
                      <span><Users size={14} aria-hidden="true" /> Solo clientes con documentos pendientes</span>
                    </div>
                  </div>
                  <div className="programada-status activa">Activa</div>
                </div>
              </div>

              <div className="info-box">
                <h5>ℹ️ Información importante</h5>
                <ul>
                  <li>Las notificaciones se envían automáticamente sin intervención</li>
                  <li>Se envía por <strong>correo</strong> a los clientes que lo tienen registrado. WhatsApp todavía no está conectado: a quien solo tiene teléfono no le llega nada</li>
                  <li>El recordatorio del día 15 solo se envía si el cliente tiene documentos faltantes</li>
                  <li>Los emails incluyen plantillas profesionales con diseño corporativo</li>
                  <li>Puedes enviar manualmente en cualquier momento desde la pestaña "Enviar"</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
)}


    {/* MODAL EDITAR CONTACTO */}
{showEditContact && (
  <div className="modal-overlay" onClick={() => setShowEditContact(false)}>
    <div className="modal" onClick={e => e.stopPropagation()}>
      <div className="modal-header">
        <h3><Pencil size={20} aria-hidden="true" /> Editar contacto - {selectedClientContact?.nombre}</h3>
        <BotonIcono onClick={() => setShowEditContact(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
      </div>

      <form onSubmit={editarContacto} className="contact-form">
        <div className="form-group">
          <label><Mail size={14} aria-hidden="true" /> Email</label>
          <input 
            type="email" 
            multiple
            placeholder="correo@ejemplo.com"
            value={contactData.email}
            onChange={e => setContactData({...contactData, email: e.target.value})}
          />
          <small>Formato: usuario@dominio.com. Separa múltiples correos con coma.</small>
        </div>

        <div className="form-group">
          <label>Teléfono</label>
          <input 
            type="tel" 
            placeholder="6441234567"
            value={contactData.telefono}
            onChange={e => setContactData({...contactData, telefono: e.target.value})}
          />
          <small>10 dígitos sin espacios ni guiones</small>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary"> Guardar cambios</button>
          <button 
            type="button" 
            onClick={() => setShowEditContact(false)}
            className="btn-secondary"
          >
             Cancelar
          </button>
        </div>
      </form>
    </div>
  </div>
)}

      {/* MODALES VIENTO */}
      {showNuevoDepto && (
        <div className="modal-overlay" onClick={() => setShowNuevoDepto(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Nuevo departamento</h3><BotonIcono onClick={() => setShowNuevoDepto(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono></div>
            <form onSubmit={crearDepto} className="client-form">
              <input type="text" placeholder="Código (ej. A-21)" value={newDepto.codigo} onChange={e => setNewDepto({ ...newDepto, codigo: e.target.value })} required />
              <input type="text" placeholder="Nombre del inquilino" value={newDepto.nombre} onChange={e => setNewDepto({ ...newDepto, nombre: e.target.value })} required />
              <input type="email" placeholder="Email (opcional)" value={newDepto.email} onChange={e => setNewDepto({ ...newDepto, email: e.target.value })} />
              <input type="tel" placeholder="Teléfono (opcional)" value={newDepto.telefono} onChange={e => setNewDepto({ ...newDepto, telefono: e.target.value })} />
              <input type="number" placeholder="Renta mensual" value={newDepto.renta_mensual} onChange={e => setNewDepto({ ...newDepto, renta_mensual: e.target.value })} min="0" step="0.01" />
              <textarea placeholder="Notas (opcional)" value={newDepto.notas} onChange={e => setNewDepto({ ...newDepto, notas: e.target.value })} rows={2} style={{ padding: 8, borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: 14 }} />
              <button type="submit">Crear departamento</button>
            </form>
          </div>
        </div>
      )}

      {showEditDepto && editingDepto && (
        <div className="modal-overlay" onClick={() => setShowEditDepto(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Editar departamento</h3><BotonIcono onClick={() => setShowEditDepto(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono></div>
            <form onSubmit={actualizarDepto} className="client-form">
              <input type="text" placeholder="Código" value={editingDepto.codigo} onChange={e => setEditingDepto({ ...editingDepto, codigo: e.target.value })} required />
              <input type="text" placeholder="Nombre del inquilino" value={editingDepto.nombre} onChange={e => setEditingDepto({ ...editingDepto, nombre: e.target.value })} required />
              <input type="email" placeholder="Email (opcional)" value={editingDepto.email || ''} onChange={e => setEditingDepto({ ...editingDepto, email: e.target.value })} />
              <input type="tel" placeholder="Teléfono (opcional)" value={editingDepto.telefono || ''} onChange={e => setEditingDepto({ ...editingDepto, telefono: e.target.value })} />
              <input type="number" placeholder="Renta mensual" value={editingDepto.renta_mensual || ''} onChange={e => setEditingDepto({ ...editingDepto, renta_mensual: e.target.value })} min="0" step="0.01" />
              <textarea placeholder="Notas (opcional)" value={editingDepto.notas || ''} onChange={e => setEditingDepto({ ...editingDepto, notas: e.target.value })} rows={2} style={{ padding: 8, borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: 14 }} />
              <div style={{ padding: '12px 16px', background: 'var(--yellow-100)', borderRadius: 8, border: '1px solid var(--yellow-300)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={editingDepto.activo !== false} onChange={e => setEditingDepto({ ...editingDepto, activo: e.target.checked })} style={{ width: 18, height: 18 }} />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>Departamento activo</span>
                </label>
              </div>
              <button type="submit">Guardar cambios</button>
            </form>
          </div>
        </div>
      )}

      {showGenerarCobroViento && deptoGenerarCobro && (
        <div className="modal-overlay" onClick={() => setShowGenerarCobroViento(false)}>
          <div className="modal modal-efectivo" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3>Registrar cobro</h3><BotonIcono onClick={() => setShowGenerarCobroViento(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono></div>
            <p style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 12 }}>Depto: <strong>{deptoGenerarCobro.codigo} — {deptoGenerarCobro.nombre}</strong></p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <select value={formCobroViento.mes} onChange={e => setFormCobroViento({ ...formCobroViento, mes: e.target.value })} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14 }}>
                {MESES_NOMBRE.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <input type="number" placeholder="Año" value={formCobroViento.anio} onChange={e => setFormCobroViento({ ...formCobroViento, anio: e.target.value })} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14 }} />
              <input type="number" placeholder="Monto" {...propsCampo('viento-monto')} value={formCobroViento.monto} onChange={e => setFormCobroViento({ ...formCobroViento, monto: e.target.value })} step="0.01" min="0" style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14 }} />
              {errorCampo('viento-monto')}
              <button className="btn-confirmar-efectivo" onClick={confirmarGenerarCobroViento}>Registrar cobro</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL GENERAR COBRO MENSUAL */}
      {showAdjuntosModal && cobroAdjuntos && (
        <div className="modal-overlay" onClick={() => { setShowAdjuntosModal(false); setArchivoPDF(null); setArchivoXML(null); }}>
          <div className="modal modal-efectivo" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enviar link con factura adjunta</h3>
              <BotonIcono onClick={() => { setShowAdjuntosModal(false); setArchivoPDF(null); setArchivoXML(null); }} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
            </div>
            <p style={{ fontSize: 13, color: 'var(--gray-700)', marginBottom: 16 }}>
              Se enviará el link de pago al email del cliente junto con los archivos que adjuntes.
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: 'var(--gray-500)', display: 'block', marginBottom: 6, fontWeight: 600 }}>Factura PDF</label>
              <input
                type="file"
                accept=".pdf"
                onChange={e => setArchivoPDF(e.target.files[0] || null)}
                style={{ fontSize: 13, width: '100%' }}
              />
              {archivoPDF && <p style={{ fontSize: 12, color: 'var(--green-600)', margin: '4px 0 0' }}>{archivoPDF.name}</p>}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, color: 'var(--gray-500)', display: 'block', marginBottom: 6, fontWeight: 600 }}>Archivo XML</label>
              <input
                type="file"
                accept=".xml"
                {...propsCampo('adjuntos')}
                onChange={e => setArchivoXML(e.target.files[0] || null)}
                style={{ fontSize: 13, width: '100%' }}
              />
              {archivoXML && <p style={{ fontSize: 12, color: 'var(--green-600)', margin: '4px 0 0' }}>{archivoXML.name}</p>}
              {errorCampo('adjuntos')}
            </div>
            <button
              className="btn-confirmar-efectivo"
              onClick={enviarConArchivos}
              disabled={!archivoPDF && !archivoXML}
              style={{ opacity: (!archivoPDF && !archivoXML) ? 0.5 : 1 }}
            >
              Enviar email con adjuntos
            </button>
          </div>
        </div>
      )}



      {/* TABLA PRINCIPAL */}
      <div className="main-content">
        {currentRoute === '/viento' ? (
          veViento(user) ? (() => {
            const fmt = (n) => Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            const deptosFiltrados = vientoDepartamentos
              .filter(d => mostrarInactivosViento ? true : d.activo !== false)
              .filter(d => d.nombre.toLowerCase().includes(busquedaViento.toLowerCase()) || d.codigo.toLowerCase().includes(busquedaViento.toLowerCase()))
              .sort((a, b) => a.codigo.localeCompare(b.codigo, 'es', { numeric: true }));
            const totalRenta = deptosFiltrados.reduce((s, d) => s + (Number(d.renta_mensual) || 0), 0);
            const totalSaldo = deptosFiltrados.reduce((s, d) => s + (Number(vientoSaldos[d.id]) || 0), 0);
            return (
              <div className="cobranza-wrapper">
                <div className="cobranza-header">
                  <div>
                    <h2>Viento — Departamentos</h2>
                    <p>Cobranza de rentas por departamento. Solo visible para administradores.</p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" onClick={() => navegarA('/cobranza')} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--gray-300)', background: 'var(--gray-100)', color: 'var(--gray-700)', fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}><ArrowLeft size={16} aria-hidden="true" /> Cobranza</button>
                    <input type="text" placeholder="Buscar depto o inquilino..." value={busquedaViento} onChange={e => setBusquedaViento(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--gray-200)', fontSize: 14, width: 220 }} />
                    <label style={{ fontSize: 13, color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={mostrarInactivosViento} onChange={e => setMostrarInactivosViento(e.target.checked)} /> Mostrar inactivos
                    </label>
                    <button className="btn-generar-link" onClick={() => setShowNuevoDepto(true)}>+ Departamento</button>
                  </div>
                </div>

                <div className="table-wrapper">
                  <table className="data-table cobranza-table">
                    <thead>
                      <tr>
                        <th>Depto</th>
                        <th>Inquilino</th>
                        <th>Renta mensual</th>
                        <th>Saldo pendiente</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deptosFiltrados.map(depto => {
                        const saldo = Number(vientoSaldos[depto.id]) || 0;
                        const expandido = expandedViento.has(depto.id);
                        const cargando = cargandoVientoHistorial.has(depto.id);
                        const historial = vientoHistoriales[depto.id] || [];
                        return (
                          <React.Fragment key={depto.id}>
                          <tr style={{ opacity: depto.activo === false ? 0.5 : 1 }}>
                            <td style={{ fontWeight: 700, color: 'var(--gray-700)' }}>{depto.codigo}</td>
                            <td>
                              <span>{depto.nombre}</span>
                              {depto.activo === false && <span style={{ color: 'var(--gray-400)', fontSize: 11, display: 'block' }}>Inactivo</span>}
                              {!depto.email && depto.activo !== false && <span style={{ color: 'var(--amber-500)', fontSize: 11, display: 'block' }}>Sin email</span>}
                            </td>
                            <td style={{ color: 'var(--gray-700)' }}>${fmt(depto.renta_mensual)}</td>
                            <td style={{ color: saldo > 0 ? 'var(--red-600)' : 'var(--green-600)', fontWeight: 600 }}>${fmt(saldo)}</td>
                            <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              <button type="button" className="btn-generar-link" onClick={() => abrirGenerarCobroViento(depto)} data-tooltip="Registrar cobro de un mes">+ Cobro</button>
                              <button type="button" className="btn-efectivo" style={{ background: 'var(--amber-500)' }} onClick={() => { setEditingDepto({ ...depto }); setShowEditDepto(true); }}>Editar</button>
                              <button type="button" className="btn-efectivo" style={{ background: expandido ? 'var(--gray-500)' : 'var(--cyan-600)' }} onClick={() => toggleVientoHistorial(depto.id)}>
                                {cargando ? '...' : <>{expandido ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />} Historial</>}
                              </button>
                            </td>
                          </tr>
                          {expandido && (
                            <tr style={{ background: 'var(--slate-50)' }}>
                              <td colSpan={5} style={{ padding: '12px 20px' }}>
                                {historial.length === 0 ? (
                                  <span style={{ color: 'var(--gray-400)', fontSize: 13 }}>Sin cobros. Usa "+ Cobro" para agregar.</span>
                                ) : (
                                  <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr style={{ color: 'var(--gray-500)', textAlign: 'left' }}>
                                        <th style={{ padding: '4px 12px 4px 0' }}>Mes</th>
                                        <th style={{ padding: '4px 12px 4px 0' }}>Monto</th>
                                        <th style={{ padding: '4px 12px 4px 0' }}>Estatus</th>
                                        <th style={{ padding: '4px 12px 4px 0' }}>Fecha pago</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {historial.map(c => (
                                        <tr key={c.id} style={{ borderTop: '1px solid var(--gray-200)' }}>
                                          <td style={{ padding: '6px 12px 6px 0', fontWeight: 500 }}>{MESES_NOMBRE[c.mes - 1]} {c.anio}</td>
                                          <td style={{ padding: '6px 12px 6px 0' }}>
                                            {editandoVientoCobro?.id === c.id
                                              ? <input type="number" value={editandoVientoCobro.monto} onChange={e => setEditandoVientoCobro({ id: c.id, monto: e.target.value })} style={{ width: 80, padding: '2px 6px', border: '1px solid var(--gray-300)', borderRadius: 4, fontSize: 13 }} autoFocus />
                                              : `$${fmt(c.monto)}`}
                                          </td>
                                          <td style={{ padding: '6px 12px 6px 0' }}>
                                            {c.estatus === 'pagado'
                                              ? <span style={{ color: 'var(--green-600)', fontWeight: 600 }}>Pagado</span>
                                              : <span style={{ color: 'var(--red-600)', fontWeight: 600 }}>Pendiente</span>}
                                          </td>
                                          <td style={{ padding: '6px 12px 6px 0', color: 'var(--gray-500)' }}>
                                            {editandoVientoFecha?.id === c.id
                                              ? <><input type="date" {...propsCampo('viento-fecha')} value={editandoVientoFecha.fecha} onChange={e => setEditandoVientoFecha(prev => ({ ...prev, fecha: e.target.value }))} style={{ fontSize: 12, padding: '2px 6px', border: '1px solid var(--gray-300)', borderRadius: 4 }} />{errorCampo('viento-fecha')}</>
                                              : <>{c.fecha_pago ? new Date(c.fecha_pago).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '—'}{c.registrado_por && <span style={{ marginLeft: 5, fontSize: 10, fontWeight: 700, color: 'var(--blanco)', background: 'var(--gray-500)', borderRadius: 3, padding: '1px 4px', letterSpacing: '0.03em' }} data-tooltip={c.registrado_por}>{siglas(c.registrado_por)}</span>}</>}
                                          </td>
                                          <td style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            {c.estatus === 'pagado' && (
                                              editandoVientoFecha?.id === c.id ? (
                                                <>
                                                  <button type="button" onClick={guardarFechaViento} style={{ fontSize: 12, padding: '3px 10px', background: 'var(--green-600)', color: 'var(--blanco)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Guardar</button>
                                                  <button type="button" onClick={() => setEditandoVientoFecha(null)} style={{ fontSize: 12, padding: '3px 10px', background: 'var(--gray-500)', color: 'var(--blanco)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Cancelar</button>
                                                </>
                                              ) : (
                                                <>
                                                  <button type="button" onClick={() => setEditandoVientoFecha({ id: c.id, fecha: c.fecha_pago ? new Date(c.fecha_pago).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10), cobro: c, deptoId: depto.id, deptoNombre: `${depto.codigo}` })} style={{ fontSize: 12, padding: '3px 10px', background: 'var(--amber-500)', color: 'var(--blanco)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Editar fecha</button>
                                                  <button type="button" onClick={() => revertirVientoPagado(c, depto.id)} style={{ fontSize: 12, padding: '3px 10px', background: 'var(--red-600)', color: 'var(--blanco)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Revertir</button>
                                                </>
                                              )
                                            )}
                                            {c.estatus === 'pendiente' && (
                                              <button type="button" onClick={() => marcarVientoPagado(c, depto.id)} style={{ fontSize: 12, padding: '3px 10px', background: 'var(--green-600)', color: 'var(--blanco)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Pagado</button>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700, borderTop: '2px solid var(--gray-200)' }}>
                        <td colSpan={2} style={{ textAlign: 'right', paddingRight: 8 }}>Totales</td>
                        <td>${fmt(totalRenta)}</td>
                        <td style={{ color: totalSaldo > 0 ? 'var(--red-600)' : 'var(--green-600)' }}>${fmt(totalSaldo)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Resumen mensual Viento */}
                {(() => {
                  const totalSaldoGlobal = Object.values(vientoSaldos).reduce((s, v) => s + Number(v), 0);
                  const totalPendienteAnio = vientoResumenMensual.reduce((s, r) => s + Number(r.pendiente), 0);
                  const pendienteAnteriores = Math.max(0, totalSaldoGlobal - totalPendienteAnio);
                  return (
                    <div style={{ marginTop: 32 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--slate-900)' }}>Resumen por mes</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 0, border: '1px solid var(--gray-300)', borderRadius: 6, overflow: 'hidden' }}>
                          <BotonIcono type="button" onClick={() => setSelectedAnioViento(a => a - 1)} style={{ border: 'none', background: 'var(--gray-100)', cursor: 'pointer', padding: '3px 10px', fontSize: 14, color: 'var(--gray-700)' }} etiqueta="Año anterior"><ChevronLeft size={14} aria-hidden="true" /></BotonIcono>
                          <span style={{ fontWeight: 700, fontSize: 14, padding: '3px 10px', background: 'var(--blanco)', color: 'var(--slate-900)', userSelect: 'none' }}>{selectedAnioViento}</span>
                          <BotonIcono type="button" onClick={() => setSelectedAnioViento(a => a + 1)} style={{ border: 'none', background: 'var(--gray-100)', cursor: 'pointer', padding: '3px 10px', fontSize: 14, color: 'var(--gray-700)' }} etiqueta="Año siguiente"><ChevronRight size={14} aria-hidden="true" /></BotonIcono>
                        </div>
                      </div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ fontSize: 13, width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ background: 'var(--slate-50)' }}>
                            <tr>
                              {['Mes', 'Cobrado', 'Pendiente', 'Total'].map(h => (
                                <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Mes' ? 'left' : 'right', fontSize: 11, fontWeight: 700, color: 'var(--slate-600)', textTransform: 'uppercase', borderBottom: '2px solid var(--slate-200)' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {vientoResumenMensual.length === 0
                              ? <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '16px 12px' }}>Sin datos para {selectedAnioViento}</td></tr>
                              : vientoResumenMensual.map(r => {
                                  const total = Number(r.total);
                                  const pctC = total > 0 ? Math.round(Number(r.cobrado) / total * 100) : 0;
                                  const pctP = total > 0 ? Math.round(Number(r.pendiente) / total * 100) : 0;
                                  return (
                                    <tr key={r.mes} style={{ borderBottom: '1px solid var(--slate-100)' }}>
                                      <td style={{ padding: '8px 12px' }}>{MESES_NOMBRE[r.mes - 1]}</td>
                                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                        <span style={{ color: 'var(--green-600)', fontWeight: 600 }}>${fmt(r.cobrado)}</span>
                                        {pctC > 0 && <span style={{ color: 'var(--gray-400)', fontSize: 11, marginLeft: 5 }}>{pctC}%</span>}
                                      </td>
                                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                        <span style={{ color: Number(r.pendiente) > 0 ? 'var(--red-600)' : 'var(--gray-500)', fontWeight: Number(r.pendiente) > 0 ? 600 : 400 }}>${fmt(r.pendiente)}</span>
                                        {pctP > 0 && <span style={{ color: 'var(--gray-400)', fontSize: 11, marginLeft: 5 }}>{pctP}%</span>}
                                      </td>
                                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>${fmt(r.total)}</td>
                                    </tr>
                                  );
                                })
                            }
                            {pendienteAnteriores > 0 && (
                              <tr style={{ color: 'var(--amber-800)', background: 'var(--amber-100)', fontStyle: 'italic' }}>
                                <td style={{ padding: '8px 12px' }}>Años anteriores</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>—</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>${fmt(pendienteAnteriores)}</td>
                                <td style={{ padding: '8px 12px', textAlign: 'right' }}>—</td>
                              </tr>
                            )}
                          </tbody>
                          <tfoot>
                            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--gray-200)', background: 'var(--slate-50)' }}>
                              <td style={{ padding: '10px 12px' }}>Total {selectedAnioViento}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--green-600)' }}>${fmt(vientoResumenMensual.reduce((s, r) => s + Number(r.cobrado), 0))}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--red-600)' }}>${fmt(totalSaldoGlobal)}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}>${fmt(vientoResumenMensual.reduce((s, r) => s + Number(r.total), 0))}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()
          : <div className="cobranza-wrapper"><p>Esta sección está disponible solo para administradores.</p></div>
        ) : currentRoute === '/cobranza' ? (
          veCobranza(user) ? (
            <Cobranza
              fetchAPI={fetchAPI}
              puedeAdministrar={esAdministrador(user)}
              mes={periodoCobranza.mes + 1}
              anio={periodoCobranza.anio}
              elegirMes={(mes, anio) => setPeriodoCobranza({ mes: mes - 1, anio })}
              enviarLink={(cobroId) => enviarLinkCobro({ id: cobroId })}
              copiarLink={copiarLinkCobro}
              adjuntarFactura={(cobroId) => { setCobroAdjuntos({ cobro: { id: cobroId } }); setShowAdjuntosModal(true); }}
              editarCliente={(clienteId) => {
                const cliente = cobranzaTodosClientes.find(c => c.id === clienteId) || clientes.find(c => c.id === clienteId);
                if (cliente) abrirEditarCliente(cliente);
              }}
              nuevoCliente={() => { setNewClient(p => ({ ...p, otros_ingresos: false, solo_cobranza: false })); setShowNewClient(true); cargarUsuarios(); }}
              abrirCliente={esAdministrador(user) ? abrirProcesosDetalle : null}
              exportarExcel={generarExcelCobranza}
              enviarATodos={enviarCobranzaMasiva}
              generandoExcel={generandoExcel}
              enviandoATodos={enviandoMasivo}
            />
          ) : (
            <div className="cobranza-wrapper">
              <p>Esta sección está disponible solo para usuarios administrador.</p>
            </div>
          )
        ) : verInactivos && esAdministrador(user) ? (
          // C-027: lista de clientes inactivos en lugar de la tabla de pasos
          <section className="lista-inactivos" aria-labelledby="titulo-inactivos">
            <div className="lista-inactivos-cab">
              <h2 id="titulo-inactivos">Clientes inactivos</h2>
              <span>
                Mostrando {inactivosQueCoinciden.length} de {clientesInactivos.length} {clientesInactivos.length === 1 ? 'cliente inactivo' : 'clientes inactivos'} ·{' '}
                <button type="button" className="btn-link" onClick={() => setVerInactivos(false)}>← Volver a clientes activos</button>
              </span>
            </div>
            {inactivosQueCoinciden.length === 0 ? (
              <p className="lista-inactivos-vacia">
                {clientesInactivos.length === 0 ? 'No hay clientes inactivos.' : 'Ningún cliente inactivo coincide con la búsqueda.'}
              </p>
            ) : (
              <ul>
                {inactivosQueCoinciden.map(c => (
                  <li key={c.id} className="inactivo">
                    <div className="inactivo-datos">
                      <button type="button" className="inactivo-nombre" onClick={() => abrirProcesosDetalle(c.id)} title={`Ver el detalle de ${c.nombre}`}>
                        {c.nombre}
                      </button>
                      <span>
                        {c.contador_nombre && <><User size={13} aria-hidden="true" /> {c.contador_nombre} · </>}
                        {c.desactivado_el
                          ? <>Desactivado el {fechaCorta(c.desactivado_el)}{c.desactivado_por ? ` por ${c.desactivado_por}` : ''}</>
                          : 'Desactivado (sin fecha)'}
                        {' · '}
                        {c.ultimo_mes ? `Último mes trabajado: ${nombreMes(c.ultimo_mes.mes - 1).toLowerCase()} ${c.ultimo_mes.anio}` : 'Sin meses trabajados'}
                      </span>
                      {c.debe > 0 && <span className="inactivo-debe">Debe ${Number(c.debe).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} en Cobranza</span>}
                    </div>
                    <button type="button" className="btn-secondary" onClick={() => reactivarCliente(c)}>Reactivar…</button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : viewMode === 'mensual' ? (
          <div className="table-wrapper tabla-mensual-wrap">
            {/* C-024: anchos fijos para que quepa en 1024 px (los anchos están en App.css) */}
            <table className="data-table tabla-mensual">
            <colgroup>
              <col className="ancho-cliente" />
              {procesos.map(p => <col key={p.id} className={esOpinionCumplimiento(p) ? 'ancho-opinion' : 'ancho-paso'} />)}
              <col className="ancho-docs" />
            </colgroup>
            <thead>
              <tr className="fila-etapas">
                <th aria-hidden="true" />
                {ETAPAS.map(e => <th key={e.nombre} colSpan={e.pasos} scope="colgroup"><span>{e.nombre}</span></th>)}
                {procesos.length > ETAPAS.reduce((n, e) => n + e.pasos, 0) + 1 && (
                  <th colSpan={procesos.length - ETAPAS.reduce((n, e) => n + e.pasos, 0) - 1} />
                )}
                {procesoOpinion && <th className="etapa-resultado" scope="colgroup"><span>Resultado</span></th>}
                <th aria-hidden="true" />
              </tr>
              <tr className="fila-pasos">
                <th className="col-client" scope="col">Cliente</th>
                {procesos.map(p => {
                  const esOpinion = esOpinionCumplimiento(p);
                  const paso = pasosDelEmbudo.find(x => x.proceso.id === p.id);
                  const numero = paso ? paso.numero : pasosDelEmbudo.length + 1;
                  const info = INFO_PASOS[esOpinion ? 'opinion' : paso?.slug] || {};
                  const limite = fechasLimite[p.id] ? fechaLimiteDe(fechasLimite[p.id], periodoSeleccionado) : null;
                  const ayuda = `Paso ${numero} · ${nombreVisibleProceso(p)}. ${info.que || ''} ${info.quien || ''}${limite ? ` A más tardar el ${limite.getDate()} de ${nombreMes(limite.getMonth()).toLowerCase()}.` : ''}`.replace(/\s+/g, ' ').trim();
                  return (
                    <th key={p.id} scope="col" className={`col-proceso${p.id === procesoResaltadoId ? ' col-resaltada' : ''}${esOpinion ? ' col-opinion' : ''}`}>
                      <span className="paso-encabezado" tabIndex={0} data-tooltip={ayuda}>
                        <span className="paso-numero">{numero}</span>
                        <span className="paso-nombre">{conGuionesSuaves(nombreVisibleProceso(p))}</span>
                        <span className="paso-limite">
                          {esOpinion ? <>Solo<br />informa</>
                            : limite ? <>A más tardar<br />{fechaCorta(limite)}</>
                            : <>Sin fecha<br />límite</>}
                        </span>
                      </span>
                    </th>
                  );
                })}
                <th className="col-documentos" scope="col">Docs.</th>
              </tr>
            </thead>
            <tbody>
              {clientesFiltrados.length === 0 ? renderFilaSinResultados()
                : (filtroAtrasados || filtroPorVencer) ? clientesFiltrados.map(c => renderFilaCliente(c, { mostrarTipo: true }))
                : (() => {
                  const morales = clientesFiltrados.filter(c => c.tipo === 'moral');
                  const fisicas = clientesFiltrados.filter(c => c.tipo === 'fisica');
                  return (
                    <>
                      <tr className="section-header"><td colSpan={procesos.length+2}>PERSONAS MORALES · {morales.length}</td></tr>
                      {morales.map(c => renderFilaCliente(c))}
                      <tr className="section-header"><td colSpan={procesos.length+2}>PERSONAS FÍSICAS · {fisicas.length}</td></tr>
                      {fisicas.map(c => renderFilaCliente(c))}
                    </>
                  );
                })()}
            </tbody>
          </table>
        </div>
        ) : viewMode === 'especiales' ? (
          /* OBLIGACIONES ADICIONALES (C-021) */
          <ObligacionesAdicionales
            periodo={periodoSeleccionado}
            api={apiObligaciones}
            esContador={user?.rol === 'contador'}
            soloAtrasadas={obligAtrasadas}
            onCambio={setObligacionesMes}
            Icono={IconoEstado}
            onAbrirCliente={abrirProcesosDetalle}
            onEditarCliente={esAdministrador(user) ? (id) => { const c = clientes.find(x => x.id === id); if (c) abrirEditarCliente(c); } : null}
            busquedaInicial={obligBuscar}
          />
        ) : (
          /* DECLARACIÓN ANUAL (C-020) */
          <DeclaracionAnual
            ejercicio={ejercicio}
            api={apiAnual}
            esContador={user?.rol === 'contador'}
            marcaRevisado={marcaRevisado(user)}
            muestraPorRevisar={esAdministrador(user)}
            Icono={IconoEstado}
            Sello={SelloRevision}
            onAbrirCliente={abrirProcesosDetalle}
            onEditarCliente={esAdministrador(user) ? (id) => { const c = clientes.find(x => x.id === id); if (c) abrirEditarCliente(c); } : null}
            clienteInicial={anualCliente}
          />
        )}
      </div>

      {/* MODAL DE DETALLE ANUAL */}

      {/* MODAL SOLICITAR PERMISO */}
      {/* C-028: pedir acceso a un mes pasado */}
      {showSolicitarPermiso && (
        <div className="modal-overlay" onClick={() => setShowSolicitarPermiso(false)}>
          <div className="modal permiso-dialogo" role="dialog" aria-modal="true" aria-labelledby="titulo-pedir-acceso" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="titulo-pedir-acceso">Pedir acceso a {nombreMesPermiso(mesParaPermiso, anioParaPermiso)}</h3>
              <BotonIcono onClick={() => setShowSolicitarPermiso(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
            </div>
            <form onSubmit={solicitarPermisoMes} className="permiso-form">
              <p>
                Se lo enviamos a Pedro y a Agustín. Cuando alguno lo apruebe, podrás editar {nombreMesPermiso(mesParaPermiso, anioParaPermiso).split(' ')[0]} durante 24 horas, y te avisamos aquí en Activo.
              </p>
              <label htmlFor="motivo-permiso" className="permiso-label">¿Qué necesitas corregir?</label>
              <textarea
                id="motivo-permiso"
                placeholder="Ejemplo: corregir el cálculo de IVA de Macchina"
                value={motivoPermiso}
                onChange={e => { setMotivoPermiso(e.target.value); if (errorMotivoPermiso) setErrorMotivoPermiso(''); }}
                rows="3"
                className="permiso-textarea"
                aria-invalid={!!errorMotivoPermiso}
                aria-describedby={errorMotivoPermiso ? 'motivo-permiso-error' : undefined}
                autoFocus
              />
              {errorMotivoPermiso && <p id="motivo-permiso-error" className="error-campo" role="alert">{errorMotivoPermiso}</p>}
              <div className="modal-actions">
                <button type="button" onClick={() => setShowSolicitarPermiso(false)} className="btn-secondary">Cancelar</button>
                <button type="submit" className="btn-primary">Enviar solicitud</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* C-028: Solicitudes de permiso (Administración → Solicitudes de permiso) */}
      {showPermisosPanel && esAdministrador(user) && (
        <div className="modal-overlay" onClick={() => setShowPermisosPanel(false)}>
          <div className="modal solicitudes-panel" role="dialog" aria-modal="true" aria-labelledby="titulo-solicitudes" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 id="titulo-solicitudes"><Clock size={20} aria-hidden="true" /> Solicitudes de permiso</h3>
              <BotonIcono onClick={() => setShowPermisosPanel(false)} etiqueta="Cerrar"><X size={20} aria-hidden="true" /></BotonIcono>
            </div>
            <div className="solicitudes-cuerpo">
              <h4>Pendientes</h4>
              {solicitudesPendientes.length === 0 && <p className="solicitudes-vacio">No hay solicitudes pendientes.</p>}
              {solicitudesPendientes.map(sol => (
                <div key={sol.id} className="solicitud">
                  <div className="solicitud-quien">
                    <b>{sol.contador_nombre} · {nombreMesPermiso(sol.mes, sol.anio).replace(/^./, l => l.toUpperCase())}</b>
                    <span>{fechaHora(sol.fecha_solicitud)}</span>
                  </div>
                  <p className="solicitud-motivo">{sol.motivo || 'Sin motivo'}</p>
                  {rechazandoSolicitud?.id === sol.id ? (
                    <div className="solicitud-acciones">
                      <label className="sr-only" htmlFor={`nota-${sol.id}`}>Motivo del rechazo</label>
                      <input
                        id={`nota-${sol.id}`}
                        className="solicitud-nota"
                        placeholder={`Motivo (opcional), lo verá ${sol.contador_nombre}`}
                        value={rechazandoSolicitud.nota}
                        onChange={e => setRechazandoSolicitud({ id: sol.id, nota: e.target.value })}
                        autoFocus
                      />
                      <button type="button" className="btn-secondary" onClick={() => setRechazandoSolicitud(null)}>Cancelar</button>
                      <button type="button" className="btn-secondary peligro" onClick={() => responderSolicitud(sol, 'rechazado', rechazandoSolicitud.nota)}>Rechazar</button>
                    </div>
                  ) : (
                    <div className="solicitud-acciones">
                      <button type="button" className="btn-secondary" onClick={() => setRechazandoSolicitud({ id: sol.id, nota: '' })}>Rechazar…</button>
                      <button type="button" className="btn-primary" onClick={() => responderSolicitud(sol, 'aprobado')}>Aprobar 24 horas</button>
                    </div>
                  )}
                </div>
              ))}

              <h4>Respondidas</h4>
              {solicitudesRespondidas.length === 0 && <p className="solicitudes-vacio">Todavía no hay solicitudes respondidas.</p>}
              {solicitudesRespondidas.map(sol => {
                const vigente = sol.estado === 'aprobado';
                const texto = sol.estado === 'rechazado'
                  ? `Rechazada por ${sol.respondio}${sol.nota ? `: "${sol.nota}"` : ''}`
                  : sol.estado === 'quitado'
                    ? `Aprobada por ${sol.respondio} · ${sol.quitado_por || 'alguien'} quitó el acceso ${fechaHora(sol.quitado_en)}`
                    : vigente
                      ? `Aprobada por ${sol.respondio} · vence ${fechaHora(sol.fecha_expiracion)}`
                      : `Aprobada por ${sol.respondio} · venció ${fechaHora(sol.fecha_expiracion)}`;
                return (
                  <div key={sol.id} className="solicitud respondida">
                    <div className="solicitud-quien">
                      <b>{sol.contador_nombre} · {nombreMesPermiso(sol.mes, sol.anio).replace(/^./, l => l.toUpperCase())}</b>
                      <span>{fechaHora(sol.fecha_solicitud)}</span>
                    </div>
                    <p className="solicitud-motivo">{sol.motivo}</p>
                    <div className="solicitud-acciones">
                      <span className="solicitud-estado">
                        {texto}
                        {sol.estado !== 'rechazado' && sol.cambios > 0 && (
                          <> · <button type="button" className="btn-link" onClick={() => verCambiosConAcceso(sol)}>{sol.cambios} {sol.cambios === 1 ? 'cambio' : 'cambios'}</button></>
                        )}
                      </span>
                      {vigente && <button type="button" className="btn-secondary" onClick={() => quitarAccesoPermiso(sol)}>Quitar acceso</button>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <DialogoHost />
      <AvisosHost />
    </div>
    );
  }

export default App;
